import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
import ReactDOM from "react-dom";
import { createClient } from "@supabase/supabase-js";
const supabase = createClient(
  "https://fmtwsnrvutfvcfqkthjh.supabase.co",
  process.env.REACT_APP_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZtdHdzbnJ2dXRmdmNmcWt0aGpoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU3OTAzNDIsImV4cCI6MjA5MTM2NjM0Mn0.5R4DE3nLfo_asqeKnRgnpdHE4mU3ERGapHET9icGwBo",
  { auth: { flowType: "pkce" } }
);

const logError = async ({ agencyId, userId, action, error, context = {} }) => {
  try {
    await supabase.from("error_logs").insert({
      agency_id: agencyId || null,
      user_id: userId || null,
      action: action || "unknown",
      error_message: error?.message || String(error),
      error_code: error?.code || null,
      context,
      created_at: new Date().toISOString(),
    });
  } catch (_) {
    console.error("[logError failed]", _);
  }
};
/* ═══════════════════════════════════════════════════════════════
   AUDITNEST — Complete Home Health Compliance Platform
   ENV VARS NEEDED:
   REACT_APP_SUPABASE_URL · REACT_APP_SUPABASE_ANON_KEY
   SUPABASE_SERVICE_ROLE_KEY (server) · STRIPE_SECRET_KEY (server)
   REACT_APP_STRIPE_PUBLISHABLE_KEY · TWILIO_ACCOUNT_SID (server)
   TWILIO_AUTH_TOKEN (server) · TWILIO_FROM_NUMBER (server)
   SENDGRID_API_KEY (server) · DISABLE_ESLINT_PLUGIN=true
═══════════════════════════════════════════════════════════════ */

const _style = document.createElement("style");
_style.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'DM Sans',sans-serif;background:#E8F8F5;color:#1F2937}
  ::-webkit-scrollbar{width:5px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:#E5E7EB;border-radius:10px}
  input,select,textarea,button{font-family:'DM Sans',sans-serif}
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .fade-in{animation:fadeIn 0.3s ease forwards}
`;
document.head.appendChild(_style);

// ─── COLORS ──────────────────────────────────────────────────────
const C = {
  navy:"#12304A",blue:"#2F80ED",teal:"#12B8A6",
  mint:"#E8F8F5",white:"#F8FAFC",slate:"#1F2937",
  gray:"#6B7280",light:"#E5E7EB",red:"#EF4444",
  amber:"#F59E0B",green:"#10B981",purple:"#8B5CF6",
};

// ─── UTILITIES ───────────────────────────────────────────────────
const fmt = d => d ? new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "N/A";
const today = () => new Date().toISOString().split("T")[0];
const daysUntil = d => Math.ceil((new Date(d)-new Date())/86400000);
const daysFromNow = n => { const d=new Date(); d.setDate(d.getDate()+n); return d.toISOString().split("T")[0]; };
const initials = name => name.split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2);
const uid = () => `id_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
const getCredStatus = expiry => {
  const days = daysUntil(expiry);
  if(days < 0)   return {label:"Expired",      color:C.red,  bg:"#FEE2E2"};
  if(days <= 30) return {label:`${days}d`,      color:C.red,  bg:"#FEE2E2"};
  if(days <= 60) return {label:`${days}d left`, color:C.amber,bg:"#FEF3C7"};
  return             {label:"Current",       color:C.teal, bg:"#CCFBF1"};
};

// ─── SEED DATA ───────────────────────────────────────────────────
const SEED_AGENCY = {
  id:"agency-1",name:"Sunrise Home Health Agency",
  address:"123 Care Lane, Atlanta, GA 30301",phone:"(404) 555-0100",
  email:"admin@sunrisehomehealth.com",plan:"growth",
  npi:"1234567890",medicare_number:"11A1234",
  administrator:"Sarah Williams RN",clinical_manager:"James Carter RN",
  alternate_admin:"Maria Lopez LPN",state_license:"HHA-GA-2024-00142",
  state_license_expiry: daysFromNow(180),
  federal_cert_expiry: daysFromNow(365),
  accreditation:"CHAP",accreditation_expiry: daysFromNow(540),
};

const SEED_STAFF = [
  {id:"s1",agency_id:"agency-1",name:"Maria Rodriguez",role:"RN",email:"maria@agency.com",phone:"(404)555-0101",hire_date:"2022-03-15",status:"active",years_licensed:6},
  {id:"s2",agency_id:"agency-1",name:"James Thompson",role:"LPN",email:"james@agency.com",phone:"(404)555-0102",hire_date:"2021-08-20",status:"active",years_licensed:4},
  {id:"s3",agency_id:"agency-1",name:"Priya Patel",role:"PT",email:"priya@agency.com",phone:"(404)555-0103",hire_date:"2023-01-10",status:"active",years_licensed:3},
  {id:"s4",agency_id:"agency-1",name:"David Kim",role:"OT",email:"david@agency.com",phone:"(404)555-0104",hire_date:"2022-11-05",status:"active",years_licensed:5},
  {id:"s5",agency_id:"agency-1",name:"Sarah Johnson",role:"HHA",email:"sarah@agency.com",phone:"(404)555-0105",hire_date:"2023-06-01",status:"active",years_licensed:2},
  {id:"s6",agency_id:"agency-1",name:"Robert Green",role:"HHA",email:"robert@agency.com",phone:"(404)555-0106",hire_date:"2023-09-01",status:"active",years_licensed:1},
];

const SEED_CREDENTIALS = [
  {id:"c1",staff_id:"s1",type:"RN License",expiry_date:daysFromNow(45),file_url:null,number:"RN-GA-123456"},
  {id:"c2",staff_id:"s1",type:"CPR Certification",expiry_date:daysFromNow(200),file_url:null,number:"CPR-2024-001"},
  {id:"c3",staff_id:"s1",type:"TB Test",expiry_date:daysFromNow(-10),file_url:null,number:null},
  {id:"c4",staff_id:"s1",type:"Physical Exam",expiry_date:daysFromNow(300),file_url:null,number:null},
  {id:"c5",staff_id:"s1",type:"Hepatitis B",expiry_date:daysFromNow(180),file_url:null,number:null},
  {id:"c6",staff_id:"s2",type:"LPN License",expiry_date:daysFromNow(180),file_url:null,number:"LPN-GA-789012"},
  {id:"c7",staff_id:"s2",type:"CPR Certification",expiry_date:daysFromNow(25),file_url:null,number:"CPR-2024-002"},
  {id:"c8",staff_id:"s2",type:"TB Test",expiry_date:daysFromNow(300),file_url:null,number:null},
  {id:"c9",staff_id:"s3",type:"PT License",expiry_date:daysFromNow(300),file_url:null,number:"PT-GA-345678"},
  {id:"c10",staff_id:"s3",type:"Background Check",expiry_date:daysFromNow(365),file_url:null,number:"BGC-2024-003"},
  {id:"c11",staff_id:"s4",type:"OT License",expiry_date:daysFromNow(-5),file_url:null,number:"OT-GA-901234"},
  {id:"c12",staff_id:"s5",type:"HHA Certification",expiry_date:daysFromNow(90),file_url:null,number:"HHA-GA-567890"},
  {id:"c13",staff_id:"s5",type:"TB Test",expiry_date:daysFromNow(55),file_url:null,number:null},
  {id:"c14",staff_id:"s6",type:"HHA Certification",expiry_date:daysFromNow(120),file_url:null,number:"HHA-GA-678901"},
  {id:"c15",staff_id:"s6",type:"Background Check",expiry_date:daysFromNow(300),file_url:null,number:"BGC-2024-006"},
];

const SEED_HR_DOCS = [
  {id:"h1",staff_id:"s1",doc_type:"Physical Exam",status:"complete",date_completed:"2024-01-10",notes:""},
  {id:"h2",staff_id:"s1",doc_type:"Flu Vaccine",status:"complete",date_completed:"2024-10-01",notes:""},
  {id:"h3",staff_id:"s1",doc_type:"Hep B Vaccine",status:"complete",date_completed:"2023-06-15",notes:""},
  {id:"h4",staff_id:"s1",doc_type:"I-9 Form",status:"complete",date_completed:"2022-03-15",notes:""},
  {id:"h5",staff_id:"s1",doc_type:"OIG Exclusion Check",status:"complete",date_completed:today(),notes:"Clear"},
  {id:"h6",staff_id:"s1",doc_type:"HIPAA Training",status:"complete",date_completed:"2024-01-15",notes:""},
  {id:"h7",staff_id:"s1",doc_type:"Orientation",status:"complete",date_completed:"2022-03-16",notes:""},
  {id:"h8",staff_id:"s1",doc_type:"Competency Evaluation",status:"complete",date_completed:"2022-03-20",notes:""},
  {id:"h9",staff_id:"s2",doc_type:"Physical Exam",status:"complete",date_completed:"2023-08-20",notes:""},
  {id:"h10",staff_id:"s2",doc_type:"Flu Vaccine",status:"missing",date_completed:null,notes:""},
  {id:"h11",staff_id:"s2",doc_type:"Hep B Declination",status:"complete",date_completed:"2021-08-20",notes:"Declined"},
  {id:"h12",staff_id:"s2",doc_type:"I-9 Form",status:"complete",date_completed:"2021-08-20",notes:""},
  {id:"h13",staff_id:"s5",doc_type:"OIG Exclusion Check",status:"missing",date_completed:null,notes:""},
  {id:"h14",staff_id:"s6",doc_type:"HIPAA Training",status:"missing",date_completed:null,notes:""},
];

const SEED_AIDE_SUPERVISION = [
  {id:"sup1",staff_id:"s5",supervised_by:"s1",visit_date:daysFromNow(-30),next_due:daysFromNow(30),skills_adequate:true,notes:"All skills verified. Patient satisfied.",documented:true},
  {id:"sup2",staff_id:"s6",supervised_by:"s1",visit_date:daysFromNow(-70),next_due:daysFromNow(-10),skills_adequate:true,notes:"Good performance.",documented:true},
];

const SEED_COPS = [
  {id:"cop1",number:"484.40",title:"Patient Rights",level:"Level 1",status:"met",evidence:"Patient rights brochure updated Jan 2024. Signed by all current patients.",last_reviewed:daysFromNow(-30)},
  {id:"cop2",number:"484.45",title:"OASIS Data Submission",level:"Level 1",status:"met",evidence:"OASIS submitted monthly via iQIES. Validation reports on file.",last_reviewed:daysFromNow(-15)},
  {id:"cop3",number:"484.50",title:"Comprehensive Assessment",level:"Level 1",status:"needs_review",evidence:"",last_reviewed:null},
  {id:"cop4",number:"484.55",title:"Care Planning",level:"Level 1",status:"met",evidence:"Care plans reviewed and updated every 60 days per schedule.",last_reviewed:daysFromNow(-20)},
  {id:"cop5",number:"484.60",title:"Skilled Nursing Services",level:"Level 1",status:"met",evidence:"All skilled nursing provided by licensed RN/LPN.",last_reviewed:daysFromNow(-25)},
  {id:"cop6",number:"484.65",title:"Therapy Services",level:"Standard",status:"needs_review",evidence:"Supervision logs need update for Q1 2025.",last_reviewed:null},
  {id:"cop7",number:"484.70",title:"Medical Social Services",level:"Standard",status:"met",evidence:"MSW services documented and coordinated.",last_reviewed:daysFromNow(-40)},
  {id:"cop8",number:"484.75",title:"Home Health Aide Services",level:"Level 1",status:"not_met",evidence:"2 HHAs have overdue 60-day supervision visits.",last_reviewed:daysFromNow(-60)},
  {id:"cop9",number:"484.80",title:"Infection Control",level:"Level 1",status:"met",evidence:"Written IC program in place. Staff trained annually.",last_reviewed:daysFromNow(-10)},
  {id:"cop10",number:"484.85",title:"Compliance with Federal/State Laws",level:"Standard",status:"met",evidence:"Agency complies with all applicable laws. Legal review completed.",last_reviewed:daysFromNow(-90)},
  {id:"cop11",number:"484.90",title:"Skilled Professional Services",level:"Standard",status:"needs_review",evidence:"Integrated care planning documentation needs strengthening.",last_reviewed:null},
  {id:"cop12",number:"484.95",title:"Release of Patient OASIS Data",level:"Standard",status:"met",evidence:"Patient consent for OASIS release obtained at admission.",last_reviewed:daysFromNow(-45)},
  {id:"cop13",number:"484.100",title:"Reporting OASIS Data (iQIES)",level:"Standard",status:"met",evidence:"OASIS transmittal records maintained. iQIES submissions current.",last_reviewed:daysFromNow(-15)},
  {id:"cop14",number:"484.102",title:"Emergency Preparedness (Appendix Z)",level:"Standard",status:"needs_review",evidence:"Annual drill scheduled Q2 2025. Staff training due.",last_reviewed:null},
  {id:"cop15",number:"484.105",title:"Governing Body + Acceptance-to-Service Policy",level:"Standard",status:"met",evidence:"Governing body active. Acceptance-to-service policy adopted Nov 2024.",last_reviewed:daysFromNow(-60)},
];

const SEED_PATIENTS = [
  {id:"p1",name:"Eleanor Davis",dob:"1945-03-15",diagnosis:"CHF, HTN",soc_date:"2024-10-01",physician:"Dr. James Hart",oasis_submitted:true,oasis_date:"2024-10-01",poc_due:daysFromNow(12),status:"active",risk_level:"high"},
  {id:"p2",name:"Robert Chen",dob:"1952-07-22",diagnosis:"COPD, Diabetes",soc_date:"2024-11-15",physician:"Dr. Lisa Park",oasis_submitted:true,oasis_date:"2024-11-15",poc_due:daysFromNow(35),status:"active",risk_level:"medium"},
  {id:"p3",name:"Betty Williams",dob:"1938-11-08",diagnosis:"CVA, Hypertension",soc_date:"2024-09-10",physician:"Dr. Marcus Williams",oasis_submitted:true,oasis_date:"2024-09-10",poc_due:daysFromNow(-5),status:"active",risk_level:"high"},
];

const SEED_QAPI = [
  {id:"q1",meeting_date:daysFromNow(-90),quarter:"Q3 2024",attendees:"Admin, Clinical Manager, RN Staff",topics:"Fall rates, readmissions, infection rates",action_items:"Implement fall prevention protocol",minutes_documented:true},
  {id:"q2",meeting_date:daysFromNow(-1),quarter:"Q4 2024",attendees:"Admin, Clinical Manager, QA Coordinator",topics:"EVV compliance, credential gaps, aide supervision",action_items:"Monthly OIG checks, aide supervision schedule",minutes_documented:true},
];

const SEED_QAPI_METRICS = [
  {id:"m1",metric:"30-day Readmission Rate",value:"12%",target:"<15%",trend:"improving",period:"Q4 2024"},
  {id:"m2",metric:"Patient Fall Rate",value:"2.1 per 100 visits",target:"<2.0",trend:"stable",period:"Q4 2024"},
  {id:"m3",metric:"Infection Rate",value:"0.8%",target:"<1%",trend:"improving",period:"Q4 2024"},
  {id:"m4",metric:"Patient Satisfaction",value:"94%",target:">90%",trend:"improving",period:"Q4 2024"},
  {id:"m5",metric:"EVV Compliance Rate",value:"96%",target:">95%",trend:"stable",period:"Q4 2024"},
];

const SEED_INFECTION = [
  {id:"inf1",date:daysFromNow(-20),type:"UTI",staff_involved:"s1",patient:"Eleanor Davis",reported:true,investigated:true,outcome:"Treated. IC protocol reinforced."},
  {id:"inf2",date:daysFromNow(-5),type:"Respiratory — possible COVID",staff_involved:"s3",patient:"Robert Chen",reported:true,investigated:false,outcome:"Under investigation"},
];

const SEED_EP_DRILLS = [
  {id:"ep1",drill_type:"Tabletop Exercise",date:daysFromNow(-180),facilitator:"Admin",participants:"All staff",scenario:"Hurricane evacuation",documented:true,next_due:daysFromNow(185)},
];

const SEED_HIPAA = [
  {id:"hp1",staff_id:"s1",training_date:"2024-01-15",expiry:daysFromNow(285),completed:true},
  {id:"hp2",staff_id:"s2",training_date:"2024-01-16",expiry:daysFromNow(286),completed:true},
  {id:"hp3",staff_id:"s3",training_date:"2024-02-01",expiry:daysFromNow(300),completed:true},
  {id:"hp4",staff_id:"s4",training_date:"2024-02-01",expiry:daysFromNow(300),completed:true},
  {id:"hp5",staff_id:"s5",training_date:null,expiry:null,completed:false},
  {id:"hp6",staff_id:"s6",training_date:null,expiry:null,completed:false},
];

const SEED_BAA = [
  {id:"b1",vendor:"Supabase (Database)",signed_date:"2024-01-01",expiry:daysFromNow(300),status:"active"},
  {id:"b2",vendor:"SendGrid (Email)",signed_date:"2024-01-01",expiry:daysFromNow(300),status:"active"},
  {id:"b3",vendor:"Twilio (SMS)",signed_date:"2024-01-01",expiry:daysFromNow(300),status:"active"},
  {id:"b4",vendor:"Billing Software Vendor",signed_date:"2023-06-01",expiry:daysFromNow(-30),status:"expired"},
];

const SEED_OIG = [
  {id:"o1",staff_id:"s1",check_date:today(),result:"Clear",checked_by:"Admin",next_due:daysFromNow(30)},
  {id:"o2",staff_id:"s2",check_date:today(),result:"Clear",checked_by:"Admin",next_due:daysFromNow(30)},
  {id:"o3",staff_id:"s3",check_date:daysFromNow(-35),result:"Clear",checked_by:"Admin",next_due:daysFromNow(-5)},
  {id:"o4",staff_id:"s4",check_date:today(),result:"Clear",checked_by:"Admin",next_due:daysFromNow(30)},
  {id:"o5",staff_id:"s5",check_date:null,result:null,checked_by:null,next_due:null},
  {id:"o6",staff_id:"s6",check_date:null,result:null,checked_by:null,next_due:null},
];

const SEED_INSURANCE = [
  {id:"i1",type:"General Liability",carrier:"Hartford",policy_number:"GL-2024-001",limit:"$1M/$3M",expiry:daysFromNow(180),status:"active"},
  {id:"i2",type:"Professional Liability",carrier:"ProAssurance",policy_number:"PL-2024-002",limit:"$1M/$3M",expiry:daysFromNow(180),status:"active"},
  {id:"i3",type:"Workers Compensation",carrier:"State Farm",policy_number:"WC-2024-003",limit:"Statutory",expiry:daysFromNow(180),status:"active"},
  {id:"i4",type:"Cyber Liability",carrier:"Chubb",policy_number:"CY-2024-004",limit:"$1M",expiry:daysFromNow(25),status:"active"},
  {id:"i5",type:"Commercial Auto",carrier:"Nationwide",policy_number:"CA-2024-005",limit:"$500K",expiry:daysFromNow(180),status:"active"},
];

const SEED_COMPLAINTS = [
  {id:"com1",date:daysFromNow(-45),patient:"Eleanor Davis",nature:"Aide arrived 2 hours late",received_by:"Admin",status:"resolved",resolution:"Staff counseled. Schedule adjusted.",resolution_date:daysFromNow(-40),days_to_resolve:5},
  {id:"com2",date:daysFromNow(-10),patient:"Robert Chen",nature:"Medication left at wrong time",received_by:"Clinical Manager",status:"open",resolution:"",resolution_date:null,days_to_resolve:null},
];

const SEED_INCIDENTS = [
  {id:"inc1",date:daysFromNow(-30),type:"Patient Fall",patient:"Betty Williams",staff_involved:"s5",description:"Patient fell attempting to get to bathroom. No injury.",reported_to_state:true,report_date:daysFromNow(-30),outcome:"Fall prevention plan updated."},
  {id:"inc2",date:daysFromNow(-7),type:"Medication Error",patient:"Eleanor Davis",staff_involved:"s2",description:"Medication given 2 hours late.",reported_to_state:false,report_date:null,outcome:"Under review"},
];

const SEED_POC = [
  {id:"poc1",survey_date:daysFromNow(-365),deficiency:"G0157 — HHA supervision not completed within 60 days",tag:"G0157",cop:"484.75",submitted_date:daysFromNow(-355),completion_date:daysFromNow(-340),status:"resolved",corrective_action:"Implemented 60-day supervision calendar. Assigned RN supervisor to each HHA."},
];

const SEED_EVV = [
  {id:"e1",staff_id:"s1",client:"Eleanor Davis",visit_date:daysFromNow(-1),check_in:"09:00",check_out:"11:30",gps:true,status:"verified",service_type:"Skilled Nursing"},
  {id:"e2",staff_id:"s2",client:"Robert Chen",visit_date:daysFromNow(-1),check_in:"13:00",check_out:"15:00",gps:true,status:"verified",service_type:"LPN Visit"},
  {id:"e3",staff_id:"s5",client:"Betty Williams",visit_date:daysFromNow(-2),check_in:"10:00",check_out:"12:00",gps:false,status:"pending",service_type:"HHA Services"},
  {id:"e4",staff_id:"s6",client:"Eleanor Davis",visit_date:daysFromNow(-3),check_in:"14:00",check_out:"16:00",gps:false,status:"flagged",service_type:"HHA Services"},
];

// ─── SHARED COMPONENTS ────────────────────────────────────────────

const Btn = ({children,onClick,variant="primary",style:s={},disabled=false,size="md"}) => {
  const sizes={sm:"7px 14px",md:"10px 20px",lg:"13px 28px"};
  const base={padding:sizes[size],borderRadius:8,border:"none",cursor:disabled?"not-allowed":"pointer",fontWeight:600,fontSize:14,transition:"all 0.18s",opacity:disabled?0.55:1,display:"inline-flex",alignItems:"center",gap:6,...s};
  const vars={
    primary:{background:C.blue,color:"#fff"},
    teal:{background:C.teal,color:"#fff"},
    navy:{background:C.navy,color:"#fff"},
    ghost:{background:"transparent",color:C.navy,border:`1.5px solid ${C.navy}`},
    danger:{background:C.red,color:"#fff"},
    light:{background:C.light,color:C.slate},
    amber:{background:C.amber,color:"#fff"},
    purple:{background:C.purple,color:"#fff"},
  };
  return <button onClick={disabled?undefined:onClick} style={{...base,...vars[variant]}}>{children}</button>;
};

const Card = ({children,style:s={}}) => (
  <div style={{background:"#fff",borderRadius:14,padding:24,boxShadow:"0 1px 6px rgba(18,48,74,0.07)",...s}}>{children}</div>
);

const Badge = ({label,color,bg}) => (
  <span style={{background:bg,color,padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:700,whiteSpace:"nowrap",display:"inline-block"}}>{label}</span>
);

const StatusBadge = ({status}) => {
  const map={
    met:{label:"Met",color:C.teal,bg:"#CCFBF1"},
    not_met:{label:"Not Met",color:C.red,bg:"#FEE2E2"},
    needs_review:{label:"Needs Review",color:C.amber,bg:"#FEF3C7"},
    verified:{label:"Verified",color:C.teal,bg:"#CCFBF1"},
    pending:{label:"Pending",color:C.amber,bg:"#FEF3C7"},
    flagged:{label:"Flagged",color:C.red,bg:"#FEE2E2"},
    open:{label:"Open",color:C.red,bg:"#FEE2E2"},
    in_progress:{label:"In Progress",color:C.amber,bg:"#FEF3C7"},
    resolved:{label:"Resolved",color:C.teal,bg:"#CCFBF1"},
    active:{label:"Active",color:C.teal,bg:"#CCFBF1"},
    complete:{label:"Complete",color:C.teal,bg:"#CCFBF1"},
    missing:{label:"Missing",color:C.red,bg:"#FEE2E2"},
    expired:{label:"Expired",color:C.red,bg:"#FEE2E2"},
    high:{label:"High Risk",color:C.red,bg:"#FEE2E2"},
    medium:{label:"Medium Risk",color:C.amber,bg:"#FEF3C7"},
    low:{label:"Low Risk",color:C.teal,bg:"#CCFBF1"},
  };
  const st=map[status]||{label:status,color:C.gray,bg:C.light};
  return <Badge label={st.label} color={st.color} bg={st.bg}/>;
};

const Field = ({label,value,onChange,type="text",placeholder="",required=false,onKeyDown}) => (
  <div style={{marginBottom:16}}>
    {label&&<label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>{label}{required&&<span style={{color:C.red}}> *</span>}</label>}
    <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} onKeyDown={onKeyDown}
      style={{width:"100%",padding:"10px 14px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:14,color:C.slate,outline:"none",background:C.white}}
      onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.light}/>
  </div>
);

const Dropdown = ({label,value,onChange,options}) => (
  <div style={{marginBottom:16}}>
    {label&&<label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>{label}</label>}
    <select value={value||""} onChange={e=>onChange(e.target.value)}
      style={{width:"100%",padding:"10px 14px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:14,color:C.slate,outline:"none",background:C.white}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Textarea = ({label,value,onChange,placeholder="",rows=3,required=false,onKeyDown}) => (
  <div style={{marginBottom:16}}>
    {label&&<label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>{label}{required&&<span style={{color:C.red}}> *</span>}</label>}
    <textarea value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
      onKeyDown={onKeyDown}
      style={{width:"100%",padding:"10px 14px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:14,color:C.slate,outline:"none",background:C.white,resize:"vertical"}}
      onFocus={e=>e.target.style.borderColor=C.blue} onBlur={e=>e.target.style.borderColor=C.light}/>
  </div>
);

const Modal = ({title,children,onClose,width=540}) => {
  const content = (
    <div style={{position:"fixed",inset:0,background:"rgba(18,48,74,0.45)",zIndex:99999,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"40px 16px"}}>
      <div className="fade-in" style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:width,padding:32,position:"relative",maxHeight:"90vh",overflowY:"auto",flexShrink:0}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:C.navy}}>{title}</h2>
          <button onClick={onClose} style={{background:C.light,border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",fontSize:18,color:C.gray}}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
  return ReactDOM.createPortal(content, document.body);
};

const Avatar = ({name,size=40}) => (
  <div style={{width:size,height:size,borderRadius:size/2,background:`linear-gradient(135deg,${C.navy},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontFamily:"'Outfit',sans-serif",fontWeight:700,fontSize:size*0.36,flexShrink:0}}>
    {initials(name)}
  </div>
);

const PageHeader = ({title,subtitle,action}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:28}}>
    <div>
      <h1 style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:800,color:C.navy,lineHeight:1.2}}>{title}</h1>
      {subtitle&&<div style={{color:C.gray,fontSize:14,marginTop:4}}>{subtitle}</div>}
    </div>
    {action}
  </div>
);

const StatCard = ({label,value,color=C.blue,sub,onClick}) => (
  <Card style={{textAlign:"center",padding:20,cursor:onClick?"pointer":"default"}} onClick={onClick}>
    <div style={{fontFamily:"'Outfit',sans-serif",fontSize:34,fontWeight:900,color,lineHeight:1}}>{value}</div>
    <div style={{fontSize:12,color:C.gray,marginTop:6,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5}}>{label}</div>
    {sub&&<div style={{fontSize:11,color:C.gray,marginTop:2}}>{sub}</div>}
  </Card>
);

const TipBox = ({children}) => (
  <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:14,marginBottom:16,fontSize:13,color:"#1E40AF"}}>{children}</div>
);

const ScoreGauge = ({score}) => {
  const r=54,circ=2*Math.PI*r,offset=circ-(score/100)*circ;
  const color=score>=80?C.teal:score>=60?C.amber:C.red;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center"}}>
      <svg width={140} height={140} viewBox="0 0 140 140">
        <circle cx={70} cy={70} r={r} fill="none" stroke={C.light} strokeWidth={12}/>
        <circle cx={70} cy={70} r={r} fill="none" stroke={color} strokeWidth={12}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 70 70)" style={{transition:"stroke-dashoffset 1.2s ease"}}/>
        <text x={70} y={67} textAnchor="middle" style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:900,fill:C.navy}}>{score}%</text>
        <text x={70} y={85} textAnchor="middle" style={{fontFamily:"'DM Sans',sans-serif",fontSize:11,fill:C.gray}}>Compliance</text>
      </svg>
      <div style={{fontSize:13,color,fontWeight:700,marginTop:4}}>
        {score>=80?"🟢 Survey Ready":score>=60?"🟡 Needs Attention":"🔴 Action Required"}
      </div>
    </div>
  );
};

// ─── TRIAL BANNER ─────────────────────────────────────────────────
const TrialBanner = ({agency, setScreen}) => {
  const [dismissed, setDismissed] = useState(false);
  if(dismissed) return null;
  if(agency?.subscribed) return null;
  if(!agency?.trial_ends_at) return null;

  const daysLeft = Math.ceil((new Date(agency.trial_ends_at) - new Date()) / 86400000);
  const expired = daysLeft <= 0;

  if(expired) return (
    <div style={{background:C.red,padding:"12px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:16}}>🔒</span>
        <span style={{color:"#fff",fontWeight:700,fontSize:14}}>Your trial has ended. Subscribe to continue adding and editing data.</span>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={()=>setScreen("billing")}
          style={{background:"#fff",color:C.red,border:"none",borderRadius:7,padding:"7px 16px",fontWeight:700,fontSize:13,cursor:"pointer"}}>
          Subscribe Now — $249/mo
        </button>
      </div>
    </div>
  );

  const color = daysLeft <= 3 ? C.red : daysLeft <= 7 ? C.amber : C.navy;
  const bg = daysLeft <= 3 ? "#FEE2E2" : daysLeft <= 7 ? "#FEF3C7" : "#EEF5FF";

  return (
    <div style={{background:bg,padding:"10px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,borderBottom:`1px solid ${C.light}`}}>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:14}}>⏱</span>
        <span style={{color,fontWeight:600,fontSize:13}}>
          {daysLeft <= 3 ? `⚠️ Trial expires in ${daysLeft} day${daysLeft===1?"":"s"}!` : `Free trial — ${daysLeft} days remaining`}
        </span>
      </div>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <button onClick={()=>setScreen("billing")}
          style={{background:C.blue,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
          Subscribe — $249/mo
        </button>
        <button onClick={()=>setDismissed(true)}
          style={{background:"transparent",border:"none",cursor:"pointer",color:C.gray,fontSize:16,lineHeight:1}}>×</button>
      </div>
    </div>
  );
};

// ─── RESET PASSWORD SCREEN ────────────────────────────────────────
const ResetPasswordScreen = ({onDone}) => {
  const [password,setPassword]=useState("");
  const [confirm,setConfirm]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [success,setSuccess]=useState(false);

  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    const token_hash = params.get("token_hash");
    const type = params.get("type");
    if(token_hash && type==="recovery"){
      supabase.auth.verifyOtp({token_hash, type}).then(({error})=>{
        if(error) setError("Reset link is invalid or expired. Please request a new one.");
      });
    }
  },[]);

  const handle = async () => {
    if(!password||!confirm) return setError("Both fields are required.");
    if(password!==confirm) return setError("Passwords do not match.");
    if(password.length<8) return setError("Password must be at least 8 characters.");
    setLoading(true);setError("");
    const {error} = await supabase.auth.updateUser({password});
    if(error) setError(error.message);
    else setSuccess(true);
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",background:"#E8F8F5",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:"100%",maxWidth:420,padding:24}}>
        <div style={{marginBottom:32,textAlign:"center"}}>
          <div style={{width:48,height:48,borderRadius:12,background:"#12304A",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>
            <span style={{color:"#12B8A6",fontWeight:900,fontSize:24}}>A</span>
          </div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:24,fontWeight:900,color:"#12304A"}}>AuditNest</div>
        </div>
        <div style={{background:"#fff",borderRadius:16,padding:32,boxShadow:"0 4px 24px rgba(18,48,74,0.08)"}}>
          {success?(
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:12}}>✓</div>
              <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:"#12304A",marginBottom:8}}>Password updated!</h2>
              <p style={{color:"#6B7280",fontSize:14,marginBottom:20}}>You can now sign in with your new password.</p>
              <button onClick={onDone}
                style={{background:"#2F80ED",color:"#fff",border:"none",borderRadius:8,padding:"12px 28px",fontWeight:700,fontSize:14,cursor:"pointer",width:"100%"}}>
                Go to Sign In
              </button>
            </div>
          ):(
            <>
              <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:"#12304A",marginBottom:6}}>Set new password</h2>
              <p style={{color:"#6B7280",fontSize:14,marginBottom:24}}>Enter your new password below.</p>
              <div style={{marginBottom:16}}>
                <label style={{display:"block",fontSize:13,fontWeight:600,color:"#1F2937",marginBottom:6}}>New Password</label>
                <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={{width:"100%",padding:"10px 14px",borderRadius:8,border:"1.5px solid #E5E7EB",fontSize:14,color:"#1F2937",outline:"none"}}/>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{display:"block",fontSize:13,fontWeight:600,color:"#1F2937",marginBottom:6}}>Confirm Password</label>
                <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
                  placeholder="Repeat your new password"
                  style={{width:"100%",padding:"10px 14px",borderRadius:8,border:"1.5px solid #E5E7EB",fontSize:14,color:"#1F2937",outline:"none"}}/>
              </div>
              {error&&<div style={{color:"#EF4444",fontSize:13,marginBottom:12,padding:"8px 12px",background:"#FEE2E2",borderRadius:8}}>{error}</div>}
              <button onClick={handle} disabled={loading}
                style={{width:"100%",padding:"13px 20px",background:loading?"#9CA3AF":"#2F80ED",color:"#fff",border:"none",borderRadius:8,cursor:loading?"not-allowed":"pointer",fontWeight:700,fontSize:15}}>
                {loading?"Updating...":"Update Password"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── AUTH SCREEN ──────────────────────────────────────────────────
const AuthScreen = ({onAuth}) => {
  const [mode,setMode]=useState("login");
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [agency,setAgency]=useState("");
  const [state,setState]=useState("");
  const [npi,setNpi]=useState("");
  const [medicare,setMedicare]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [resetSent,setResetSent]=useState(false);

  const handleReset = async () => {
    setLoading(true);setError("");
    try {
      if(!email) throw new Error("Enter your email address first.");
      localStorage.setItem("auditNestReset", Date.now().toString());
      const {error} = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo:"https://app.audit-nest.com/"
      });
      if(error) throw error;
      setResetSent(true);
    } catch(e){setError(e.message);}
    setLoading(false);
  };

  const handle = async () => {
    setLoading(true);setError("");
    try {
      if(!email||!password) throw new Error("Email and password required.");
      if(mode==="register"&&!agency) throw new Error("Agency name required.");
      if(mode==="register") {
        const res = await fetch('/api/auth/register', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({email, password, agencyName: agency, state, npi, medicare})
        });
        const data = await res.json();
        if(data.error) throw new Error(data.error);
        onAuth({email, agency: data.agency || {...SEED_AGENCY, name: agency}});
      } else {
        const {data,error} = await supabase.auth.signInWithPassword({email,password});
        if(error) throw error;
        const {data:agencyData} = await supabase.from('agencies').select('*').eq('owner_id',data.user.id).single();
        if(agencyData) await supabase.from('agencies').update({last_login:new Date().toISOString()}).eq('id',agencyData.id);
        onAuth({email, agency: agencyData || SEED_AGENCY, session: data.session});
      }
    } catch(e){setError(e.message);}
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",background:C.mint}}>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
        <div style={{width:"100%",maxWidth:420}} className="fade-in">
          <div style={{marginBottom:36}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
              <div style={{width:36,height:36,borderRadius:10,background:C.navy,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <span style={{color:C.teal,fontWeight:900,fontSize:18}}>A</span>
              </div>
              <span style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:900,color:C.navy}}>AuditNest</span>
            </div>
            <div style={{color:C.gray,fontSize:14}}>Home Health Compliance Platform</div>
          </div>
          <Card>
            <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:C.navy,marginBottom:22}}>
              {mode==="login"?"Welcome back":"Create your agency"}
            </h2>
            <div onKeyDown={e=>e.key==="Enter"&&!loading&&handle()}>
            {mode==="register"&&<Field label="Agency Name" value={agency} onChange={setAgency} placeholder="Sunrise Home Health Agency" required/>}
            {mode==="register"&&<Field label="State / Location" value={state} onChange={setState} placeholder="Georgia" required/>}
            {mode==="register"&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <Field label="NPI Number" value={npi} onChange={setNpi} placeholder="1234567890" required/>
                <Field label="Medicare Provider #" value={medicare} onChange={setMedicare} placeholder="11A1234" required/>
              </div>
            )}
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="admin@agency.com" required/>
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" required onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();handle();}}}/>
            {mode==="login"&&(
              <div style={{textAlign:"right",marginTop:-8,marginBottom:12}}>
                <span onClick={handleReset} style={{fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>
                  {resetSent?"✓ Reset email sent!":"Forgot password?"}
                </span>
              </div>
            )}
            {error&&<div style={{color:C.red,fontSize:13,marginBottom:12,padding:"8px 12px",background:"#FEE2E2",borderRadius:8}}>{error}</div>}
            <Btn onClick={handle} disabled={loading} style={{width:"100%",justifyContent:"center",padding:"13px 20px",fontSize:15,marginBottom:4}}>
              {loading?"Please wait...":mode==="login"?"Sign In":"Create Account"}
            </Btn>
            </div>
            <div style={{textAlign:"center",marginTop:14,fontSize:14,color:C.gray}}>
              {mode==="login"?"No account? ":"Have an account? "}
              <span onClick={()=>{setMode(mode==="login"?"register":"login");setError("");}} style={{color:C.blue,cursor:"pointer",fontWeight:700}}>
                {mode==="login"?"Sign up free":"Sign in"}
              </span>
            </div>
          </Card>
          
        </div>
      </div>
      <div style={{flex:1,background:C.navy,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 56px"}}>
        <div style={{maxWidth:380}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:32,fontWeight:900,color:"#fff",lineHeight:1.2,marginBottom:16}}>
            Audit-ready in<br/><span style={{color:C.teal}}>30 minutes.</span>
          </div>
          <div style={{color:"rgba(255,255,255,0.65)",fontSize:15,lineHeight:1.75,marginBottom:36}}>
            Full pre-survey compliance tracking across every domain CMS and state surveyors actually inspect.
          </div>
          {["All 15 CMS Conditions of Participation","Complete HR File per staff member","HHA 60-day supervision tracking","QAPI quarterly meeting management","Infection control & incident log","Emergency preparedness drills","HIPAA training & BAA tracking","OIG exclusion checks monthly","Patient records & OASIS status","Plan of Correction history","EVV compliance monitoring","Complaint & grievance log"].map(f=>(
            <div key={f} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <div style={{width:22,height:22,borderRadius:11,background:C.teal,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{color:"#fff",fontSize:12,fontWeight:700}}>✓</span>
              </div>
              <span style={{color:"rgba(255,255,255,0.8)",fontSize:14}}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── SIDEBAR ──────────────────────────────────────────────────────
const NAV = [
  {id:"dashboard",icon:"◈",label:"Dashboard"},
  {id:"staff",icon:"👤",label:"Staff & HR Files"},
  {id:"supervision",icon:"👁",label:"Aide Supervision"},
  {id:"cops",icon:"📋",label:"CMS CoPs (All 15)"},
  {id:"patients",icon:"🏥",label:"Patient Records"},
  {id:"qapi",icon:"📊",label:"QAPI"},
  {id:"infection",icon:"🛡",label:"Infection Control"},
  {id:"ep",icon:"🚨",label:"Emergency Prep"},
  {id:"hipaa",icon:"🔒",label:"HIPAA Compliance"},
  {id:"governing",icon:"🏛",label:"Governing Body"},
  {id:"insurance",icon:"📄",label:"Insurance & Legal"},
  {id:"oig",icon:"⚖",label:"OIG & Billing"},
  {id:"complaints",icon:"💬",label:"Complaints & Incidents"},
  {id:"poc",icon:"✏",label:"Plan of Correction"},
  {id:"evv",icon:"📍",label:"EVV Compliance"},
  {id:"documents",icon:"📁",label:"Documents"},
  {id:"billing",icon:"💳",label:"Billing"},
  {id:"support",icon:"🎫",label:"Support"},
  {id:"settings",icon:"⚙",label:"Settings"},
  {id:"admin",icon:"🔑",label:"Admin"},
];

const Sidebar = ({screen,setScreen,agency,onLogout,complianceStatus={}}) => {
  const [collapsed,setCollapsed]=useState(false);
  return (
    <div style={{width:collapsed?64:248,minHeight:"100vh",background:C.navy,display:"flex",flexDirection:"column",flexShrink:0,position:"sticky",top:0,transition:"width 0.2s",overflow:"hidden"}}>
      <div onClick={()=>window.location.href="https://audit-nest.com"}
        style={{padding:"22px 14px 16px",display:"flex",alignItems:"center",gap:10,justifyContent:collapsed?"center":"flex-start",cursor:"pointer"}}>
        <div style={{width:32,height:32,borderRadius:8,background:C.teal,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <span style={{color:"#fff",fontSize:16,fontWeight:900}}>A</span>
        </div>
        {!collapsed&&<span style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:900,color:"#fff"}}>AuditNest</span>}
      </div>
      {!collapsed&&<div style={{fontSize:10,color:"rgba(255,255,255,0.35)",paddingLeft:16,marginBottom:12,fontWeight:500}}>{agency?.name}</div>}
      <div style={{flex:1,padding:"4px 8px",overflowY:"auto"}}>
        {NAV.map(n=>{
          const active=screen===n.id;
          const status=complianceStatus[n.id];
          const dotColor=status==="red"?C.red:status==="amber"?C.amber:status==="green"?C.teal:null;
          return (
            <div key={n.id} onClick={()=>setScreen(n.id)}
              title={collapsed?n.label:""}
              style={{display:"flex",alignItems:"center",gap:10,padding:collapsed?"10px":"9px 10px",borderRadius:10,cursor:"pointer",marginBottom:2,
                background:active?"rgba(18,184,166,0.15)":"transparent",
                borderLeft:active?`3px solid ${C.teal}`:"3px solid transparent",
                transition:"all 0.15s",justifyContent:collapsed?"center":"flex-start"}}>
              <span style={{fontSize:14,color:active?C.teal:"rgba(255,255,255,0.45)",width:18,textAlign:"center",flexShrink:0}}>{n.icon}</span>
              {!collapsed&&<span style={{fontSize:12.5,fontWeight:active?700:400,color:active?"#fff":"rgba(255,255,255,0.6)",whiteSpace:"nowrap",flex:1}}>{n.label}</span>}
              {dotColor&&<div style={{width:8,height:8,borderRadius:4,background:dotColor,flexShrink:0}}/>}
            </div>
          );
        })}
      </div>
      <div style={{padding:"10px 12px"}}>
        {!collapsed&&(
          <div style={{background:"rgba(18,184,166,0.12)",border:"1px solid rgba(18,184,166,0.25)",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:10,color:C.teal,fontWeight:800,letterSpacing:1,marginBottom:2}}>GROWTH PLAN</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>Up to 30 clinicians</div>
          </div>
        )}
        <div onClick={()=>setCollapsed(!collapsed)} style={{fontSize:11,color:"rgba(255,255,255,0.35)",cursor:"pointer",textAlign:"center",padding:"4px 0",marginBottom:6}}>
          {collapsed?"→":"← collapse"}
        </div>
        <div onClick={onLogout} style={{fontSize:11,color:"rgba(255,255,255,0.3)",cursor:"pointer",textAlign:"center",padding:"4px 0"}}>
          Sign Out
        </div>
      </div>
    </div>
  );
};

// ─── DASHBOARD ────────────────────────────────────────────────────
const DashboardScreen = ({staff,credentials,hrDocs,aideSupervision,cops,patients,qapiMeetings,infectionIncidents,hipaaTraining,oigChecks,insurance,complaints,incidents,evv,documents,agency,setScreen}) => {

  const exportPDF = () => {
    const doc = new jsPDF();
    const teal = [18,184,166];
    const navy = [18,48,74];
    const gray = [107,114,128];
    const red = [239,68,68];
    const amber = [245,158,11];

    // Header
    doc.setFillColor(...navy);
    doc.rect(0,0,210,28,"F");
    doc.setTextColor(255,255,255);
    doc.setFontSize(18);
    doc.setFont("helvetica","bold");
    doc.text("AuditNest Compliance Report",14,12);
    doc.setFontSize(10);
    doc.setFont("helvetica","normal");
    doc.text(agency?.name||"Agency",14,20);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`,14,26);

    // Compliance Score
    const expired = credentials.filter(c=>Math.ceil((new Date(c.expiry_date)-new Date())/86400000)<0).length;
    const expiring = credentials.filter(c=>{const d=Math.ceil((new Date(c.expiry_date)-new Date())/86400000);return d>=0&&d<=60;}).length;
    const copsMet = cops.filter(c=>c.status==="met").length;
    const copsNotMet = cops.filter(c=>c.status==="not_met").length;
    const evvRate = evv.length>0?Math.round(evv.filter(e=>e.status==="verified").length/evv.length*100):100;
    const credScore = credentials.length>0?Math.round(credentials.filter(c=>Math.ceil((new Date(c.expiry_date)-new Date())/86400000)>60).length/credentials.length*100):100;
    const pocDue = Math.round((copsMet/Math.max(cops.length,1))*100);
    const hipaaScore = Math.round(hipaaTraining.filter(h=>h.completed).length/Math.max(hipaaTraining.length,1)*100);
    const overallScore = Math.round((pocDue*0.4)+(credScore*0.3)+(evvRate*0.15)+(hipaaScore*0.15));
    const scoreColor = overallScore>=80?teal:overallScore>=60?amber:red;

    doc.setFontSize(36);
    doc.setTextColor(...scoreColor);
    doc.setFont("helvetica","bold");
    doc.text(`${overallScore}%`,14,50);
    doc.setFontSize(12);
    doc.setTextColor(...navy);
    doc.text("Overall Compliance Score",40,47);
    doc.setFontSize(10);
    doc.setTextColor(...gray);
    doc.text(overallScore>=80?"Survey Ready — Good standing":overallScore>=60?"Attention Needed — Address gaps":"Action Required — Critical issues",40,54);

    // Summary stats
    autoTable(doc,{
      startY:62,
      head:[["Metric","Value","Status"]],
      body:[
        ["CMS CoPs Met",`${copsMet}/15`,copsNotMet>0?"! Not Met":"OK"],
        ["Expired Credentials",expired,expired>0?"! Action Required":"OK"],
        ["Expiring (60 days)",expiring,expiring>0?"! Review":"OK"],
        ["EVV Compliance Rate",`${evvRate}%`,evvRate<95?"! Below Target":"OK"],
        ["HIPAA Training",`${hipaaTraining.filter(h=>h.completed).length}/${Math.max(hipaaTraining.length,staff.length)} staff`,hipaaTraining.filter(h=>!h.completed).length>0?"! Gaps":"OK"],
        ["OIG Checks",`${oigChecks.filter(o=>o.check_date).length}/${staff.length} staff`,oigChecks.filter(o=>!o.check_date||(o.next_due&&Math.ceil((new Date(o.next_due)-new Date())/86400000)<0)).length>0?"! Overdue":"OK"],
        ["Active Patients",patients.filter(p=>p.status==="active").length,"—"],
        ["Active Staff",staff.filter(s=>s.status==="active").length,"—"],
      ],
      headStyles:{fillColor:navy,textColor:[255,255,255],fontSize:10},
      bodyStyles:{fontSize:9},
      alternateRowStyles:{fillColor:[248,250,252]},
      margin:{left:14,right:14},
    });

    // CMS CoPs Detail
    const y1 = doc.lastAutoTable.finalY+10;
    doc.setFontSize(13);
    doc.setTextColor(...navy);
    doc.setFont("helvetica","bold");
    doc.text("CMS Conditions of Participation",14,y1);

    autoTable(doc,{
      startY:y1+4,
      head:[["CoP","Title","Status","Evidence"]],
      body:cops.map(c=>[
        `§${c.number}`,
        c.title,
        c.status==="met"?"Met":c.status==="not_met"?"Not Met":"Needs Review",
        c.evidence?c.evidence.slice(0,60)+(c.evidence.length>60?"...":""):"No evidence noted"
      ]),
      headStyles:{fillColor:navy,textColor:[255,255,255],fontSize:9},
      bodyStyles:{fontSize:8},
      alternateRowStyles:{fillColor:[248,250,252]},
      columnStyles:{0:{cellWidth:18},1:{cellWidth:45},2:{cellWidth:25},3:{cellWidth:90}},
      margin:{left:14,right:14},
      didParseCell:(data)=>{
        if(data.column.index===2&&data.section==="body"){
          if(data.cell.text[0].includes("Not Met")) data.cell.styles.textColor=red;
          else if(data.cell.text[0].includes("Needs")) data.cell.styles.textColor=amber;
          else if(data.cell.text[0]==="Met") data.cell.styles.textColor=teal;
        }
      }
    });

    // Staff Credentials
    const y2 = doc.lastAutoTable.finalY+10;
    if(y2>240){doc.addPage();}
    doc.setFontSize(13);
    doc.setTextColor(...navy);
    doc.setFont("helvetica","bold");
    doc.text("Staff Credentials",14,y2>240?14:y2);

    const credRows = [];
    staff.filter(s=>s.status==="active").forEach(s=>{
      const staffCreds = credentials.filter(c=>c.staff_id===s.id);
      staffCreds.forEach(c=>{
        const days = Math.ceil((new Date(c.expiry_date)-new Date())/86400000);
        credRows.push([
          s.name,
          s.role,
          c.type,
          new Date(c.expiry_date).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
          days<0?"EXPIRED":days<=30?`${days}d left`:days<=60?`${days}d left`:"Current"
        ]);
      });
    });

    autoTable(doc,{
      startY:(y2>240?14:y2)+4,
      head:[["Staff Name","Role","Credential","Expiry","Status"]],
      body:credRows.length>0?credRows:[["No credentials on file","","","",""]],
      headStyles:{fillColor:navy,textColor:[255,255,255],fontSize:9},
      bodyStyles:{fontSize:8},
      alternateRowStyles:{fillColor:[248,250,252]},
      margin:{left:14,right:14},
      didParseCell:(data)=>{
        if(data.column.index===4&&data.section==="body"){
          const txt = data.cell.text[0];
          if(txt==="EXPIRED") data.cell.styles.textColor=red;
          else if(txt.includes("d left")&&parseInt(txt)<31) data.cell.styles.textColor=red;
          else if(txt.includes("d left")) data.cell.styles.textColor=amber;
          else if(txt==="OK") data.cell.styles.textColor=teal;
        }
      }
    });

    // Footer on all pages
    const pageCount = doc.getNumberOfPages();
    for(let i=1;i<=pageCount;i++){
      doc.setPage(i);
      doc.setFillColor(...navy);
      doc.rect(0,287,210,10,"F");
      doc.setFontSize(8);
      doc.setTextColor(255,255,255);
      doc.text("AuditNest — Home Health Compliance Platform — audit-nest.com",14,293);
      doc.text(`Page ${i} of ${pageCount}`,186,293);
    }

    doc.save(`AuditNest_Compliance_Report_${agency?.name?.replace(/\s+/g,"_")||"Agency"}_${new Date().toISOString().split("T")[0]}.pdf`);
  };
  const expired    = credentials.filter(c=>daysUntil(c.expiry_date)<0).length;
  const expiring   = credentials.filter(c=>{const d=daysUntil(c.expiry_date);return d>=0&&d<=60;}).length;
  const missingHR  = hrDocs.filter(h=>h.status==="missing").length;
  const supOverdue = aideSupervision.filter(s=>s.next_due&&daysUntil(s.next_due)<0).length;
  const copsMet    = cops.filter(c=>c.status==="met").length;
  const copsNotMet = cops.filter(c=>c.status==="not_met").length;
  const openComp   = complaints.filter(c=>c.status==="open").length;
  const hipaaGap   = hipaaTraining.filter(h=>!h.completed).length;
  const oigOverdue = oigChecks.filter(o=>!o.check_date||daysUntil(o.next_due)<0).length;
  const insExpiring= insurance.filter(i=>daysUntil(i.expiry)<30).length;
  const evvRate    = evv.length>0?Math.round(evv.filter(e=>e.status==="verified").length/evv.length*100):100;
  const pocDue     = Math.round((copsMet/cops.length)*100);
  const credScore  = credentials.length>0?Math.round(credentials.filter(c=>daysUntil(c.expiry_date)>60).length/credentials.length*100):100;
  const overallScore = Math.round((pocDue*0.4)+(credScore*0.3)+(evvRate*0.15)+((hipaaTraining.filter(h=>h.completed).length/Math.max(hipaaTraining.length,1)*100)*0.15));

  const alerts = [
    ...credentials.filter(c=>daysUntil(c.expiry_date)<0).map(c=>({level:"red",msg:`EXPIRED: ${c.type} — ${staff.find(s=>s.id===c.staff_id)?.name}`})),
    ...credentials.filter(c=>{const d=daysUntil(c.expiry_date);return d>=0&&d<=30;}).map(c=>({level:"amber",msg:`Expiring in ${daysUntil(c.expiry_date)}d: ${c.type} — ${staff.find(s=>s.id===c.staff_id)?.name}`})),
    ...(supOverdue>0?[{level:"red",msg:`${supOverdue} HHA aide supervision visits overdue (60-day requirement)`}]:[]),
    ...(copsNotMet>0?[{level:"red",msg:`${copsNotMet} CMS CoP${copsNotMet>1?"s":""} not met — survey risk`}]:[]),
    ...(oigOverdue>0?[{level:"red",msg:`${oigOverdue} staff OIG exclusion checks overdue`}]:[]),
    ...(hipaaGap>0?[{level:"amber",msg:`${hipaaGap} staff missing HIPAA training`}]:[]),
    ...(insExpiring>0?[{level:"amber",msg:`${insExpiring} insurance polic${insExpiring>1?"ies":"y"} expiring within 30 days`}]:[]),
    ...(openComp>0?[{level:"amber",msg:`${openComp} open patient complaint${openComp>1?"s":""} pending resolution`}]:[]),
    ...documents.filter(d=>d.expiry_date&&daysUntil(d.expiry_date)<0).map(d=>({level:"red",msg:`EXPIRED Document: ${d.name}`})),
    ...documents.filter(d=>d.expiry_date&&daysUntil(d.expiry_date)>=0&&daysUntil(d.expiry_date)<=30).map(d=>({level:"amber",msg:`Document expiring in ${daysUntil(d.expiry_date)}d: ${d.name}`})),
  ].slice(0,10);

  return (
    <div className="fade-in">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:28}}>
        <div>
          <h1 style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:800,color:C.navy,lineHeight:1.2}}>Compliance Dashboard</h1>
          <div style={{color:C.gray,fontSize:14,marginTop:4}}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
        </div>
        <Btn onClick={exportPDF} variant="ghost">📄 Export PDF Report</Btn>
      </div>

      {/* ── HERO SCORE BANNER ── */}
      <Card style={{marginBottom:20,padding:0,overflow:"hidden",background:overallScore>=80?`linear-gradient(135deg,${C.navy} 0%,#1a4a6e 100%)`:overallScore>=60?`linear-gradient(135deg,#78350F 0%,#92400E 100%)`:`linear-gradient(135deg,#7F1D1D 0%,#991B1B 100%)`}}>
        <div style={{display:"grid",gridTemplateColumns:"auto 1fr auto",gap:32,alignItems:"center",padding:"28px 36px"}}>
          <div style={{textAlign:"center"}}>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:72,fontWeight:900,color:"#fff",lineHeight:1}}>{overallScore}%</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginTop:4}}>Overall Compliance</div>
          </div>
          <div>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:"#fff",marginBottom:8}}>
              {overallScore>=80?"🟢 Survey Ready":overallScore>=60?"🟡 Attention Needed":"🔴 Action Required"}
            </div>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.7)",marginBottom:16,maxWidth:500}}>
              {overallScore>=80?"Your agency is in good compliance standing. Keep credentials current and maintain your documentation."
              :overallScore>=60?"Several areas need attention before your next survey. Review the alerts below and take action."
              :"Critical compliance gaps detected. Address the red alerts immediately to avoid survey deficiencies."}
            </div>
            <div style={{width:"100%",maxWidth:400,height:8,background:"rgba(255,255,255,0.15)",borderRadius:4}}>
              <div style={{width:`${overallScore}%`,height:8,background:overallScore>=80?"#12B8A6":overallScore>=60?C.amber:C.red,borderRadius:4,transition:"width 1s ease"}}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,minWidth:240}}>
            {[
              {label:"CoPs Met",value:`${copsMet}/15`,good:copsNotMet===0},
              {label:"Cred Issues",value:expired+expiring,good:expired+expiring===0},
              {label:"EVV Rate",value:`${evvRate}%`,good:evvRate>=95},
              {label:"OIG Overdue",value:oigOverdue,good:oigOverdue===0},
            ].map(s=>(
              <div key={s.label} style={{background:"rgba(255,255,255,0.08)",borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:s.good?"#12B8A6":"#FCA5A5"}}>{s.value}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
        <StatCard label="Expired Creds" value={expired} color={expired>0?C.red:C.teal} onClick={()=>setScreen("staff")}/>
        <StatCard label="Expiring (60d)" value={expiring} color={expiring>0?C.amber:C.teal} onClick={()=>setScreen("staff")}/>
        <StatCard label="Missing HR Docs" value={missingHR} color={missingHR>0?C.red:C.teal} onClick={()=>setScreen("staff")}/>
        <StatCard label="Supervision Due" value={supOverdue} color={supOverdue>0?C.red:C.teal} sub="HHA 60-day req" onClick={()=>setScreen("supervision")}/>
        <StatCard label="CoPs Not Met" value={copsNotMet} color={copsNotMet>0?C.red:C.teal} sub={`${copsMet}/15 met`} onClick={()=>setScreen("cops")}/>
        <StatCard label="EVV Rate" value={`${evvRate}%`} color={evvRate>=95?C.teal:C.amber} onClick={()=>setScreen("evv")}/>
        <StatCard label="HIPAA Gaps" value={hipaaGap} color={hipaaGap>0?C.amber:C.teal} onClick={()=>setScreen("hipaa")}/>
        <StatCard label="OIG Overdue" value={oigOverdue} color={oigOverdue>0?C.red:C.teal} sub="Monthly required" onClick={()=>setScreen("oig")}/>
      </div>

      {alerts.length>0&&(
        <Card style={{marginBottom:20,padding:20}}>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>🚨 Active Alerts ({alerts.length})</h3>
          {alerts.map((a,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:a.level==="red"?"#FEE2E2":"#FEF3C7",borderRadius:8,marginBottom:6,fontSize:13,color:a.level==="red"?C.red:C.amber,fontWeight:600}}>
              <span>{a.level==="red"?"🔴":"🟡"}</span>{a.msg}
            </div>
          ))}
        </Card>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 CMS CoPs Status</h3>
          {[{label:"Met",key:"met",color:C.teal},{label:"Needs Review",key:"needs_review",color:C.amber},{label:"Not Met",key:"not_met",color:C.red}].map(s=>(
            <div key={s.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.light}`}}>
              <span style={{fontSize:14,color:C.slate}}>{s.label}</span>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:80,height:6,background:C.light,borderRadius:3}}>
                  <div style={{width:`${(cops.filter(c=>c.status===s.key).length/cops.length)*100}%`,height:6,background:s.color,borderRadius:3}}/>
                </div>
                <span style={{fontSize:14,fontWeight:700,color:s.color,minWidth:24,textAlign:"right"}}>{cops.filter(c=>c.status===s.key).length}</span>
              </div>
            </div>
          ))}
          <div onClick={()=>setScreen("cops")} style={{marginTop:12,fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>View full checklist →</div>
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📊 QAPI Metrics</h3>
          {[{label:"30d Readmission",value:"12%",ok:true},{label:"Fall Rate",value:"2.1/100",ok:false},{label:"Infection Rate",value:"0.8%",ok:true},{label:"Patient Satisfaction",value:"94%",ok:true},{label:"EVV Compliance",value:`${evvRate}%`,ok:evvRate>=95}].map(m=>(
            <div key={m.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${C.light}`}}>
              <span style={{fontSize:13,color:C.slate}}>{m.label}</span>
              <Badge label={m.value} color={m.ok?C.teal:C.amber} bg={m.ok?"#CCFBF1":"#FEF3C7"}/>
            </div>
          ))}
          <div onClick={()=>setScreen("qapi")} style={{marginTop:12,fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>View QAPI program →</div>
        </Card>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:20}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:800,color:C.navy,marginBottom:12}}>🏥 Active Patients</h3>
          {patients.map(p=>(
            <div key={p.id} style={{padding:"8px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:600,color:C.slate}}>{p.name}</span>
                <StatusBadge status={p.risk_level}/>
              </div>
              <div style={{fontSize:11,color:p.poc_due&&daysUntil(p.poc_due)<0?C.red:C.gray,marginTop:2}}>
                PoC due: {fmt(p.poc_due)} {p.poc_due&&daysUntil(p.poc_due)<0?"⚠ OVERDUE":""}
              </div>
            </div>
          ))}
          <div onClick={()=>setScreen("patients")} style={{marginTop:10,fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>View all patients →</div>
        </Card>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:800,color:C.navy,marginBottom:12}}>💬 Recent Incidents</h3>
          {incidents.slice(0,3).map(i=>(
            <div key={i.id} style={{padding:"8px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{i.type}</div>
              <div style={{fontSize:11,color:C.gray}}>{fmt(i.date)} · {i.patient}</div>
            </div>
          ))}
          <div onClick={()=>setScreen("complaints")} style={{marginTop:10,fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>View all incidents →</div>
        </Card>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:14,fontWeight:800,color:C.navy,marginBottom:12}}>📄 Insurance Status</h3>
          {insurance.map(i=>{
            const days=daysUntil(i.expiry);
            const color=days<30?C.red:days<60?C.amber:C.teal;
            return(
              <div key={i.id} style={{padding:"7px 0",borderBottom:`1px solid ${C.light}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,color:C.slate}}>{i.type}</span>
                  <span style={{fontSize:11,color,fontWeight:700}}>{days<0?"EXPIRED":`${days}d`}</span>
                </div>
              </div>
            );
          })}
          <div onClick={()=>setScreen("insurance")} style={{marginTop:10,fontSize:13,color:C.blue,cursor:"pointer",fontWeight:600}}>Manage policies →</div>
        </Card>
      </div>
    </div>
  );
};

// ─── STAFF & HR FILES ─────────────────────────────────────────────
const HR_DOC_TYPES = [
  "Physical Exam","TB Test","TB Self-Assessment","Hepatitis B Vaccine","Hep B Declination",
  "Flu Vaccine","Flu Declination","CPR Certification","Background Check","OIG Exclusion Check",
  "State Abuse Registry Check","I-9 Form","Reference Checks","Orientation","Competency Evaluation",
  "HIPAA Training","Annual CEU (12hrs)","Driver License","Auto Insurance",
];
const CRED_TYPES = [
  "RN License","LPN License","PT License","OT License","SLP License","MSW License",
  "HHA Certification","CPR Certification","TB Test","Background Check",
  "Physical Exam","Hepatitis B","Flu Vaccine","Driver License","Auto Insurance",
];
const ROLES = ["RN","LPN","PT","OT","SLP","MSW","HHA","RN Supervisor","Administrator","Clinical Manager","Aide","Other"];

const StaffScreen = ({staff,setStaff,credentials,setCredentials,hrDocs,setHrDocs,agency,saveToDocumentLibrary}) => {
  const [showStaffModal,setShowStaffModal]=useState(false);
  const [showArchived,setShowArchived]=useState(false);
  const [showCredModal,setShowCredModal]=useState(false);
  const [showHRModal,setShowHRModal]=useState(false);
  const [expandedId,setExpandedId]=useState(null);
  const [selectedStaff,setSelectedStaff]=useState(null);
  const [tab,setTab]=useState("credentials");
  const [ns,setNs]=useState({name:"",role:"",email:"",phone:""});
  const [nc,setNc]=useState({type:"",expiry_date:"",number:""});
  const [nh,setNh]=useState({doc_type:"",status:"complete",date_completed:today(),notes:""});
  const [credFile,setCredFile]=useState(null);
  const [hrFile,setHrFile]=useState(null);

  const addStaff = async () => {
    if(!ns.name||!ns.role) return;
    const tempId = uid();
    const staffData = {agency_id:agency?.id,name:ns.name,role:ns.role,email:ns.email||null,phone:ns.phone||null,hire_date:today(),status:"active"};
    setStaff(prev=>[...prev,{id:tempId,...staffData}]);
    setNs({name:"",role:"",email:"",phone:""});setShowStaffModal(false);
    const {data,error} = await supabase.from('staff_members').insert(staffData).select().single();
    if(error) { await logError({agencyId:currentAgency?.id,action:"add_staff",error}); }
    if(error) console.error('Staff insert:',error);
    if(data) setStaff(prev=>prev.map(s=>s.id===tempId?data:s));
  };
  const addCred = async () => {
    if(!nc.type||!nc.expiry_date||!selectedStaff) return;
    if(!credFile){ alert("Please attach the credential document before saving."); return; }
    const docName = `${nc.type} — ${selectedStaff.name}`;
    const file_url = await saveToDocumentLibrary(credFile,"Certification",docName,agency?.id,nc.expiry_date,null);
    const tempId = uid();
    const credData = {staff_id:selectedStaff.id,agency_id:agency?.id,type:nc.type,number:nc.number||null,expiry_date:nc.expiry_date,file_url};
    setCredentials(prev=>[...prev,{id:tempId,...credData}]);
    setNc({type:"",expiry_date:"",number:""});setCredFile(null);setShowCredModal(false);
    const {data,error} = await supabase.from('credentials').insert(credData).select().single();
    if(error) { await logError({agencyId:currentAgency?.id,action:"add_credential",error}); }
    if(error) console.error('Cred insert:',error);
    if(data) setCredentials(prev=>prev.map(c=>c.id===tempId?data:c));
  };
  const addHRDoc = async () => {
    if(!nh.doc_type||!selectedStaff) return;
    if(!hrFile){ alert("Please attach the document before saving."); return; }
    const docName = `${nh.doc_type} — ${selectedStaff.name}`;
    await saveToDocumentLibrary(hrFile,"Certification",docName,agency?.id,null,null);
    const tempId = uid();
    const hrData = {staff_id:selectedStaff.id,agency_id:agency?.id,doc_type:nh.doc_type,status:nh.status,date_completed:nh.date_completed||null,notes:nh.notes||null};
    setHrDocs(prev=>[...prev,{id:tempId,...hrData}]);
    setNh({doc_type:"",status:"complete",date_completed:today(),notes:""});setHrFile(null);setShowHRModal(false);
    const {data,error} = await supabase.from('hr_documents').insert(hrData).select().single();
    if(error) console.error('HR insert:',error);
    if(data) setHrDocs(prev=>prev.map(h=>h.id===tempId?data:h));
  };
  const archiveStaff = async id => {
    if(!window.confirm("Archive this staff member? Their records will be preserved but they will be removed from active lists.")) return;
    setStaff(prev=>prev.map(s=>s.id===id?{...s,status:"archived"}:s));
    await supabase.from("staff_members").update({status:"archived"}).eq("id",id);
  };
  const restoreStaff = async id => {
    setStaff(prev=>prev.map(s=>s.id===id?{...s,status:"active"}:s));
    await supabase.from("staff_members").update({status:"active"}).eq("id",id);
  };
  const deleteHR = async id => {
    setHrDocs(prev=>prev.filter(h=>h.id!==id));
    await supabase.from('hr_documents').delete().eq('id',id);
  };
  const deleteCred = async id => {
    setCredentials(prev=>prev.filter(c=>c.id!==id));
    await supabase.from('credentials').delete().eq('id',id);
  };

  return (
    <div className="fade-in">
      <PageHeader title="Staff & HR Files" subtitle={`${staff.length} staff · Complete HR file per clinician`}
        action={<div style={{display:'flex',gap:8}}><Btn variant='ghost' size='sm' onClick={()=>setShowArchived(!showArchived)}>{showArchived?'Hide Archived':'Show Archived'}</Btn><Btn onClick={()=>setShowStaffModal(true)}>+ Add Staff</Btn></div>}/>

      {staff.filter(s=>s.status!=="archived").map(s=>{
        const staffCreds=credentials.filter(c=>c.staff_id===s.id);
        const staffHR=hrDocs.filter(h=>h.staff_id===s.id);
        const expiredCreds=staffCreds.filter(c=>daysUntil(c.expiry_date)<0).length;
        const missingHR=staffHR.filter(h=>h.status==="missing").length;
        const open=expandedId===s.id;
        return (
          <Card key={s.id} style={{marginBottom:12,padding:"18px 22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <Avatar name={s.name}/>
                <div>
                  <div style={{fontWeight:700,color:C.slate,fontSize:15}}>{s.name}</div>
                  <div style={{fontSize:13,color:C.gray}}>{s.role} · {s.email} · Hired {fmt(s.hire_date)}</div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                {expiredCreds>0&&<Badge label={`${expiredCreds} Expired`} color={C.red} bg="#FEE2E2"/>}
                {missingHR>0&&<Badge label={`${missingHR} HR Missing`} color={C.amber} bg="#FEF3C7"/>}
                {expiredCreds===0&&missingHR===0&&<Badge label="File Complete" color={C.teal} bg="#CCFBF1"/>}
                <Btn variant="ghost" size="sm" onClick={()=>{setSelectedStaff(s);setShowCredModal(true);}}>+ Credential</Btn>
                <Btn variant="light" size="sm" onClick={()=>{setSelectedStaff(s);setShowHRModal(true);}}>+ HR Doc</Btn>
                {s.status!=="archived"?<Btn variant="danger" size="sm" onClick={()=>archiveStaff(s.id)}>Archive</Btn>:<Btn variant="teal" size="sm" onClick={()=>restoreStaff(s.id)}>Restore</Btn>}
                <Btn variant="light" size="sm" onClick={()=>setExpandedId(open?null:s.id)}>{open?"▲":"▼"}</Btn>
              </div>
            </div>

            {open&&(
              <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.light}`}}>
                <div style={{display:"flex",gap:8,marginBottom:16}}>
                  {["credentials","hr_docs"].map(t=>(
                    <Btn key={t} variant={tab===t?"navy":"light"} size="sm" onClick={()=>setTab(t)}>
                      {t==="credentials"?"License & Certs":"HR File Documents"}
                    </Btn>
                  ))}
                </div>

                {tab==="credentials"&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:800,color:C.gray,letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>Credentials ({staffCreds.length})</div>
                    {staffCreds.length===0?<div style={{color:C.gray,fontSize:13}}>No credentials added yet.</div>:
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                        {staffCreds.map(c=>{
                          const st=getCredStatus(c.expiry_date);
                          return (
                            <div key={c.id} style={{background:C.mint,borderRadius:10,padding:"12px 14px"}}>
                              <div style={{fontSize:13,fontWeight:700,color:C.slate,marginBottom:2}}>{c.type}</div>
                              {c.number&&<div style={{fontSize:11,color:C.gray,marginBottom:4}}>#{c.number}</div>}
                              <div style={{fontSize:12,color:C.gray,marginBottom:8}}>Exp {fmt(c.expiry_date)}</div>
                              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <Badge label={st.label} color={st.color} bg={st.bg}/>
                                <div style={{display:"flex",gap:6}}>
                                  {c.file_url&&<span onClick={()=>window.open(c.file_url,'_blank')} style={{fontSize:11,color:C.blue,cursor:"pointer",fontWeight:600}}>View</span>}
                                  <span onClick={()=>deleteCred(c.id)} style={{fontSize:11,color:C.red,cursor:"pointer",fontWeight:600}}>✕</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    }
                  </div>
                )}

                {tab==="hr_docs"&&(
                  <div>
                    <div style={{fontSize:11,fontWeight:800,color:C.gray,letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>HR File Documents ({staffHR.length})</div>
                    {staffHR.length===0?<div style={{color:C.gray,fontSize:13}}>No HR documents added yet.</div>:
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                        {staffHR.map(h=>(
                          <div key={h.id} style={{background:h.status==="missing"?"#FEE2E2":"#F0FDF4",borderRadius:10,padding:"12px 14px",border:`1px solid ${h.status==="missing"?"#FECACA":"#BBF7D0"}`}}>
                            <div style={{fontSize:13,fontWeight:700,color:C.slate,marginBottom:2}}>{h.doc_type}</div>
                            <div style={{fontSize:11,color:C.gray,marginBottom:6}}>{h.date_completed?fmt(h.date_completed):"Not completed"}</div>
                            {h.notes&&<div style={{fontSize:11,color:C.gray,marginBottom:6,fontStyle:"italic"}}>{h.notes}</div>}
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                              <StatusBadge status={h.status}/>
                              <div style={{display:"flex",gap:6}}>
                                {h.file_url&&<span onClick={()=>window.open(h.file_url,'_blank')} style={{fontSize:11,color:C.blue,cursor:"pointer",fontWeight:600}}>View</span>}
                                <span onClick={()=>deleteHR(h.id)} style={{fontSize:11,color:C.red,cursor:"pointer",fontWeight:600}}>✕</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    }
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {showStaffModal&&(
        <Modal title="Add Staff Member" onClose={()=>setShowStaffModal(false)}>
          <Field label="Full Name" value={ns.name} onChange={v=>setNs({...ns,name:v})} required/>
          <Dropdown label="Role" value={ns.role} onChange={v=>setNs({...ns,role:v})} options={[{value:"",label:"Select role..."},...ROLES.map(r=>({value:r,label:r}))]}/>
          <Field label="Email" type="email" value={ns.email} onChange={v=>setNs({...ns,email:v})}/>
          <Field label="Phone" value={ns.phone} onChange={v=>setNs({...ns,phone:v})}/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowStaffModal(false)}>Cancel</Btn>
            <Btn onClick={addStaff}>Add Staff Member</Btn>
          </div>
        </Modal>
      )}

      {showCredModal&&selectedStaff&&(
        <Modal title={`Add Credential — ${selectedStaff.name}`} onClose={()=>setShowCredModal(false)}>
          <Dropdown label="Type" value={nc.type} onChange={v=>setNc({...nc,type:v})} options={[{value:"",label:"Select type..."},...CRED_TYPES.map(t=>({value:t,label:t}))]}/>
          <Field label="License / Cert Number" value={nc.number} onChange={v=>setNc({...nc,number:v})} required/>
          <Field label="Expiry Date" type="date" value={nc.expiry_date} onChange={v=>setNc({...nc,expiry_date:v})} required/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Document (optional)</label>
            <input type="file" onChange={e=>setCredFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {credFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {credFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowCredModal(false)}>Cancel</Btn>
            <Btn onClick={addCred}>Add Credential</Btn>
          </div>
        </Modal>
      )}

      {showHRModal&&selectedStaff&&(
        <Modal title={`Add HR Document — ${selectedStaff.name}`} onClose={()=>setShowHRModal(false)}>
          <Dropdown label="Document Type" value={nh.doc_type} onChange={v=>setNh({...nh,doc_type:v})} options={[{value:"",label:"Select type..."},...HR_DOC_TYPES.map(t=>({value:t,label:t}))]}/>
          <Dropdown label="Status" value={nh.status} onChange={v=>setNh({...nh,status:v})} options={[{value:"complete",label:"Complete"},{value:"missing",label:"Missing"},{value:"pending",label:"Pending"}]}/>
          <Field label="Date Completed" type="date" value={nh.date_completed} onChange={v=>setNh({...nh,date_completed:v})}/>
          <Textarea label="Notes" value={nh.notes} onChange={v=>setNh({...nh,notes:v})} placeholder="e.g. Hep B declined, flu exemption..."/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Document <span style={{color:C.red}}>*</span></label>
            <input type="file" onChange={e=>setHrFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {hrFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {hrFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowHRModal(false)}>Cancel</Btn>
            <Btn onClick={addHRDoc}>Add HR Document</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── AIDE SUPERVISION ─────────────────────────────────────────────
const SupervisionScreen = ({staff,aideSupervision,setAideSupervision,agency}) => {
  const [showModal,setShowModal]=useState(false);
  const [ns,setNs]=useState({staff_id:"",supervised_by:"",visit_date:today(),next_due:"",skills_adequate:true,notes:""});
  const aides=staff.filter(s=>s.role==="HHA"||s.role==="Aide");
  const rns=staff.filter(s=>s.role==="RN"||s.role==="RN Supervisor");

  const add = async () => {
    if(!ns.staff_id||!ns.visit_date) return;
    const tempId = uid();
    const supData = {staff_id:ns.staff_id,supervised_by:ns.supervised_by||null,visit_date:ns.visit_date,next_due:ns.next_due||null,skills_adequate:ns.skills_adequate,notes:ns.notes||null,documented:true,agency_id:agency?.id};
    setAideSupervision(prev=>[...prev,{id:tempId,...supData}]);
    setNs({staff_id:"",supervised_by:"",visit_date:today(),next_due:"",skills_adequate:true,notes:""});
    setShowModal(false);
    const {data,error} = await supabase.from('aide_supervision').insert(supData).select().single();
    if(error) console.error('Supervision insert:',error);
    if(data) setAideSupervision(prev=>prev.map(s=>s.id===tempId?data:s));
  };

  return (
    <div className="fade-in">
      <PageHeader title="HHA Aide Supervision" subtitle="CMS §484.75 — Every HHA must be supervised by RN in-person every 60 days"
        action={<Btn onClick={()=>setShowModal(true)}>+ Log Supervision Visit</Btn>}/>

      <TipBox>
        <strong>⚠ CMS Requirement:</strong> Every Home Health Aide must receive an in-person supervisory visit by an RN at least every 60 days. The visit must occur in the patient's home during care. If aide skills are suspected insufficient, additional supervision is required immediately. This is one of the most frequently cited deficiencies nationally.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total HHAs" value={aides.length} color={C.blue}/>
        <StatCard label="Supervision Overdue" value={aideSupervision.filter(s=>daysUntil(s.next_due)<0).length} color={aideSupervision.filter(s=>daysUntil(s.next_due)<0).length>0?C.red:C.teal}/>
        <StatCard label="Due Within 30 Days" value={aideSupervision.filter(s=>{const d=daysUntil(s.next_due);return d>=0&&d<=30;}).length} color={C.amber}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        {aides.map(aide=>{
          const visits=aideSupervision.filter(s=>s.staff_id===aide.id).sort((a,b)=>new Date(b.visit_date)-new Date(a.visit_date));
          const latest=visits[0];
          const overdue=latest?daysUntil(latest.next_due)<0:true;
          const daysLeft=latest?daysUntil(latest.next_due):null;
          return (
            <Card key={aide.id} style={{borderLeft:`4px solid ${overdue?C.red:daysLeft&&daysLeft<=30?C.amber:C.teal}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <Avatar name={aide.name} size={36}/>
                  <div>
                    <div style={{fontWeight:700,color:C.slate}}>{aide.name}</div>
                    <div style={{fontSize:12,color:C.gray}}>{aide.role}</div>
                  </div>
                </div>
                <Badge label={overdue?"OVERDUE":daysLeft&&daysLeft<=30?`Due in ${daysLeft}d`:"On Track"} color={overdue?C.red:daysLeft&&daysLeft<=30?C.amber:C.teal} bg={overdue?"#FEE2E2":daysLeft&&daysLeft<=30?"#FEF3C7":"#CCFBF1"}/>
              </div>
              {latest?(
                <>
                  <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Last visit:</strong> {fmt(latest.visit_date)}</div>
                  <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Next due:</strong> {fmt(latest.next_due)}</div>
                  <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Supervised by:</strong> {staff.find(s=>s.id===latest.supervised_by)?.name||"Unknown"}</div>
                  <div style={{fontSize:13,color:C.slate,marginBottom:8}}><strong>Skills adequate:</strong> {latest.skills_adequate?"✓ Yes":"⚠ No — additional supervision required"}</div>
                  {latest.notes&&<div style={{fontSize:12,color:C.gray,fontStyle:"italic"}}>{latest.notes}</div>}
                </>
              ):(
                <div style={{color:C.red,fontSize:13,fontWeight:600}}>⚠ No supervision visits on record — schedule immediately</div>
              )}
              <Btn variant="ghost" size="sm" style={{marginTop:12}} onClick={()=>{setNs(p=>({...p,staff_id:aide.id}));setShowModal(true);}}>+ Log Visit</Btn>
            </Card>
          );
        })}
      </div>

      <Card>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>All Supervision Visit History</h3>
        {aideSupervision.length===0?<div style={{color:C.gray,fontSize:14}}>No supervision visits recorded yet.</div>:
          aideSupervision.sort((a,b)=>new Date(b.visit_date)-new Date(a.visit_date)).map(v=>{
            const aide=staff.find(s=>s.id===v.staff_id);
            const supervisor=staff.find(s=>s.id===v.supervised_by);
            return (
              <div key={v.id} style={{display:"grid",gridTemplateColumns:"1.5fr 1.5fr 1fr 1fr 1fr 80px",gap:12,padding:"10px 0",borderBottom:`1px solid ${C.light}`,alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{aide?.name||"Unknown"}</div>
                <div style={{fontSize:13,color:C.gray}}>By: {supervisor?.name||"Unknown"}</div>
                <div style={{fontSize:13,color:C.gray}}>{fmt(v.visit_date)}</div>
                <Badge label={v.skills_adequate?"Skills OK":"Skills Issue"} color={v.skills_adequate?C.teal:C.red} bg={v.skills_adequate?"#CCFBF1":"#FEE2E2"}/>
                <div style={{fontSize:12,color:daysUntil(v.next_due)<0?C.red:C.gray}}>Next: {fmt(v.next_due)}</div>
                <Btn variant="danger" size="sm" onClick={async()=>{if(!window.confirm("Delete this visit?"))return;setAideSupervision(prev=>prev.filter(x=>x.id!==v.id));await supabase.from("aide_supervision").delete().eq("id",v.id);}}>Delete</Btn>
              </div>
            );
          })
        }
      </Card>

      {showModal&&(
        <Modal title="Log Aide Supervision Visit" onClose={()=>setShowModal(false)}>
          <TipBox>Visit must occur in the patient's home while aide is providing care. Document specifically what was observed.</TipBox>
          <Dropdown label="Home Health Aide" value={ns.staff_id} onChange={v=>setNs({...ns,staff_id:v})} options={[{value:"",label:"Select aide..."},...aides.map(s=>({value:s.id,label:`${s.name} (${s.role})`}))]}/>
          <Dropdown label="Supervising RN" value={ns.supervised_by} onChange={v=>setNs({...ns,supervised_by:v})} options={[{value:"",label:"Select RN..."},...rns.map(s=>({value:s.id,label:s.name}))]}/>
          <Field label="Visit Date" type="date" value={ns.visit_date} onChange={v=>setNs({...ns,visit_date:v})} required/>
          <Field label="Next Supervision Due (60 days from visit)" type="date" value={ns.next_due} onChange={v=>setNs({...ns,next_due:v})}/>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,cursor:"pointer"}} onClick={()=>setNs({...ns,skills_adequate:!ns.skills_adequate})}>
            <div style={{width:22,height:22,borderRadius:5,border:`2px solid ${ns.skills_adequate?C.teal:C.light}`,background:ns.skills_adequate?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {ns.skills_adequate&&<span style={{color:"#fff",fontSize:13,fontWeight:900}}>✓</span>}
            </div>
            <span style={{fontSize:14,color:C.slate}}>Aide skills adequate — care consistent with plan</span>
          </div>
          <Textarea label="Observations & Notes" value={ns.notes} required onChange={v=>setNs({...ns,notes:v})} placeholder="Document specific observations during the visit..."/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Log Supervision Visit</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── CMS CoPs — ALL 15 ────────────────────────────────────────────
const CopsScreen = ({cops,setCops,agency,saveToDocumentLibrary}) => {
  const [uploadingId,setUploadingId]=useState(null);
  const uploadEvidence = async (id,file,title) => {
    if(!file) return;
    setUploadingId(id);
    const file_url = await saveToDocumentLibrary(file,"Audit Evidence",`CoP — ${title}`,agency?.id,null,null);
    if(file_url) await update(id,"file_url",file_url);
    setUploadingId(null);
  };
  const met=cops.filter(c=>c.status==="met").length;
  const score=Math.round((met/cops.length)*100);
  const update = async (id,field,value) => {
    setCops(prev=>prev.map(c=>c.id===id?{...c,[field]:value}:c));
    const cop = cops.find(c=>c.id===id);
    if(!cop) return;
    const existing = cops.find(c=>c.id===id);
    const payload = {status:field==="status"?value:existing.status, evidence:field==="evidence"?value:existing.evidence, last_reviewed:field==="last_reviewed"?value:existing.last_reviewed, file_url:field==="file_url"?value:existing.file_url, agency_id:agency?.id, number:existing.number};
    await supabase.from("cms_checklist").upsert(payload, {onConflict:"agency_id,number"});
  };
  const borderColors={met:C.teal,not_met:C.red,needs_review:C.amber};
  const levelColors={"Level 1":{color:C.red,bg:"#FEE2E2"},"Standard":{color:C.blue,bg:"#EEF5FF"}};

  return (
    <div className="fade-in">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:28}}>
        <div>
          <h1 style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:800,color:C.navy}}>CMS Conditions of Participation</h1>
          <div style={{color:C.gray,fontSize:14}}>All 15 CoPs — 42 CFR §484 · Revised Appendix B April 2024</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:40,fontWeight:900,color:score>=80?C.teal:score>=60?C.amber:C.red,lineHeight:1}}>{score}%</div>
          <div style={{fontSize:12,color:C.gray}}>Met ({met}/15)</div>
        </div>
      </div>

      <TipBox>
        <strong>Level 1 standards</strong> are most critical — a single deficiency can trigger an extended survey covering all CoPs. <strong>Standard level</strong> findings are documented but don't automatically trigger extended surveys. Surveyors arrive unannounced with OASIS reports already pulled.
      </TipBox>

      <div style={{display:"flex",gap:14,marginBottom:24}}>
        {[{label:"Met",key:"met",color:C.teal},{label:"Needs Review",key:"needs_review",color:C.amber},{label:"Not Met",key:"not_met",color:C.red}].map(s=>(
          <Card key={s.key} style={{flex:1,textAlign:"center",padding:16}}>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:28,fontWeight:900,color:s.color}}>{cops.filter(c=>c.status===s.key).length}</div>
            <div style={{fontSize:12,color:C.gray,fontWeight:600}}>{s.label}</div>
          </Card>
        ))}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {cops.map(cop=>{
          const lc=levelColors[cop.level]||levelColors["Standard"];
          return (
            <Card key={cop.id} style={{padding:"16px 20px",borderLeft:`4px solid ${borderColors[cop.status]||C.light}`}}>
              <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                    <span style={{fontSize:11,fontWeight:800,color:C.blue,background:"#EEF5FF",padding:"2px 8px",borderRadius:6}}>§{cop.number}</span>
                    <span style={{fontSize:14,fontWeight:700,color:C.navy}}>{cop.title}</span>
                    <Badge label={cop.level} color={lc.color} bg={lc.bg}/>
                  </div>
                  {cop.evidence&&<div style={{fontSize:12,color:C.gray,marginBottom:8,fontStyle:"italic"}}>Evidence: {cop.evidence}</div>}
                  {cop.last_reviewed&&<div style={{fontSize:11,color:C.gray,marginBottom:8}}>Last reviewed: {fmt(cop.last_reviewed)}</div>}
                  <textarea value={cop.evidence||""} onChange={e=>update(cop.id,"evidence",e.target.value)}
                    placeholder="Add evidence notes — what documentation exists to prove compliance..."
                    rows={2} style={{width:"100%",padding:"7px 10px",borderRadius:7,border:`1px solid ${C.light}`,fontSize:13,color:C.slate,background:C.white,outline:"none",resize:"vertical",marginBottom:8}}/>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 12px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:600,color:C.navy}}>
                      📎 {uploadingId===cop.id?"Uploading...":"Attach Evidence"}
                      <input type="file" style={{display:"none"}} onChange={e=>uploadEvidence(cop.id,e.target.files[0],cop.title)} disabled={uploadingId===cop.id}/>
                    </label>
                    {cop.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(cop.file_url,'_blank')}>View Evidence</Btn>}
                    {cop.file_url&&<Badge label="Evidence on file ✓" color={C.teal} bg="#CCFBF1"/>}
                  </div>
                </div>
                <div style={{minWidth:160}}>
                  <select value={cop.status} onChange={e=>update(cop.id,"status",e.target.value)}
                    style={{padding:"8px 12px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,color:C.slate,background:C.white,width:"100%",outline:"none",marginBottom:8}}>
                    <option value="met">✓ Met</option>
                    <option value="needs_review">⚠ Needs Review</option>
                    <option value="not_met">✗ Not Met</option>
                  </select>
                  <Btn variant="ghost" size="sm" style={{width:"100%",justifyContent:"center"}} onClick={()=>update(cop.id,"last_reviewed",today())}>Mark Reviewed</Btn>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ─── PATIENT RECORDS ──────────────────────────────────────────────
const PatientScreen = ({patients,setPatients,staff,agency,patientDocs,setPatientDocs,saveToDocumentLibrary}) => {
  const [showArchived,setShowArchived]=useState(false);
  const [showModal,setShowModal]=useState(false);
  const [expandedId,setExpandedId]=useState(null);
  const [np,setNp]=useState({name:"",dob:"",diagnosis:"",soc_date:today(),physician:"",risk_level:"medium"});

  const add = async () => {
    if(!np.name) return;
    const tempId = uid();
    const patData = {
      oasis_submitted:false,oasis_date:null,
      poc_due:daysFromNow(60),status:"active",
      agency_id:agency?.id,
      name:np.name,
      dob:np.dob||null,
      diagnosis:np.diagnosis||null,
      soc_date:np.soc_date||null,
      physician:np.physician||null,
      risk_level:np.risk_level||"medium"
    };
    setPatients(prev=>[...prev,{id:tempId,...patData}]);
    setNp({name:"",dob:"",diagnosis:"",soc_date:today(),physician:"",risk_level:"medium"});
    setShowModal(false);
    const {data,error} = await supabase.from("patient_records").insert(patData).select().single();
    if(error) { await logError({agencyId:currentAgency?.id,action:"add_patient",error}); }
    if(error) console.error("Patient insert error:",error);
    if(data) setPatients(prev=>prev.map(p=>p.id===tempId?data:p));
  };
  const toggleOASIS = id => setPatients(prev=>prev.map(p=>p.id===id?{...p,oasis_submitted:!p.oasis_submitted,oasis_date:p.oasis_submitted?null:today()}:p));
  const archivePatient = async id => {
    if(!window.confirm("Archive this patient? Records will be preserved.")) return;
    setPatients(prev=>prev.map(p=>p.id===id?{...p,status:"archived"}:p));
    await supabase.from("patient_records").update({status:"archived"}).eq("id",id);
  };
  const restorePatient = async id => {
    setPatients(prev=>prev.map(p=>p.id===id?{...p,status:"active"}:p));
    await supabase.from("patient_records").update({status:"active"}).eq("id",id);
  };
  const updatePOC = (id,date) => setPatients(prev=>prev.map(p=>p.id===id?{...p,poc_due:date}:p));

  return (
    <div className="fade-in">
      <PageHeader title="Patient Records" subtitle={`${patients.length} active patients · OASIS, Plan of Care, risk tracking`}
        action={<div style={{display:'flex',gap:8}}><Btn variant='ghost' size='sm' onClick={()=>setShowArchived(!showArchived)}>{showArchived?'Hide Archived':'Show Archived'}</Btn><Btn onClick={()=>setShowModal(true)}>+ Add Patient</Btn></div>}/>

      <TipBox>
        <strong>CMS Requirements:</strong> Comprehensive assessment (OASIS) must be completed at start of care and submitted to iQIES. Plan of Care must be updated every 60 days. Patient rights must be acknowledged at admission. Risk for hospitalization/ED must be assessed and documented.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Active Patients" value={patients.filter(p=>p.status==="active").length} color={C.blue}/>
        <StatCard label="OASIS Submitted" value={patients.filter(p=>p.oasis_submitted).length} color={C.teal}/>
        <StatCard label="PoC Overdue" value={patients.filter(p=>p.poc_due&&daysUntil(p.poc_due)<0).length} color={C.red}/>
        <StatCard label="High Risk" value={patients.filter(p=>p.risk_level==="high").length} color={C.amber}/>
      </div>

      {patients.filter(p=>showArchived?p.status==="archived":p.status!=="archived").map(p=>{
        const pocOverdue=p.poc_due&&daysUntil(p.poc_due)<0;
        const open=expandedId===p.id;
        return (
          <Card key={p.id} style={{marginBottom:12,borderLeft:`4px solid ${pocOverdue?C.red:p.risk_level==="high"?C.amber:C.teal}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:700,color:C.slate,fontSize:15,marginBottom:2}}>{p.name}</div>
                <div style={{fontSize:13,color:C.gray}}>DOB: {fmt(p.dob)} · {p.diagnosis} · Dr. {p.physician}</div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                {pocOverdue&&<Badge label="PoC OVERDUE" color={C.red} bg="#FEE2E2"/>}
                <StatusBadge status={p.risk_level}/>
                <Badge label={p.oasis_submitted?"OASIS ✓":"OASIS Pending"} color={p.oasis_submitted?C.teal:C.amber} bg={p.oasis_submitted?"#CCFBF1":"#FEF3C7"}/>
                {p.status!=="archived"?<Btn variant="danger" size="sm" onClick={()=>archivePatient(p.id)}>Archive</Btn>:<Btn variant="teal" size="sm" onClick={()=>restorePatient(p.id)}>Restore</Btn>}
                <Btn variant="light" size="sm" onClick={()=>setExpandedId(open?null:p.id)}>{open?"▲":"▼"}</Btn>
              </div>
            </div>
            {open&&(
              <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.light}`}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                  <div style={{background:C.mint,borderRadius:10,padding:14}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.navy,marginBottom:8}}>📋 OASIS Status</div>
                    <div style={{fontSize:13,color:C.slate,marginBottom:4}}>SOC Date: {fmt(p.soc_date)}</div>
                    <div style={{fontSize:13,color:C.slate,marginBottom:8}}>Submitted: {p.oasis_date?fmt(p.oasis_date):"Not yet"}</div>
                    <Btn variant={p.oasis_submitted?"light":"teal"} size="sm" onClick={()=>toggleOASIS(p.id)}>
                      {p.oasis_submitted?"✓ Submitted":"Mark Submitted"}
                    </Btn>
                  </div>
                  <div style={{background:C.mint,borderRadius:10,padding:14}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.navy,marginBottom:8}}>📅 Plan of Care</div>
                    <div style={{fontSize:13,color:pocOverdue?C.red:C.slate,marginBottom:4,fontWeight:pocOverdue?700:400}}>
                      Due: {fmt(p.poc_due)} {pocOverdue?"⚠ OVERDUE":""}
                    </div>
                    <div style={{fontSize:12,color:C.gray,marginBottom:8}}>Must update every 60 days</div>
                    <Field label="" value={p.poc_due||""} onChange={v=>updatePOC(p.id,v)} type="date" placeholder=""/>
                  </div>
                  <div style={{background:C.mint,borderRadius:10,padding:14}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.navy,marginBottom:8}}>⚠ Risk Assessment</div>
                    <div style={{fontSize:13,color:C.slate,marginBottom:4}}>Risk Level: {p.risk_level}</div>
                    <div style={{fontSize:12,color:C.gray}}>Hospitalization/ED risk documented in plan of care per §484.55</div>
                  </div>
                </div>
                <div style={{marginTop:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.navy,marginBottom:8}}>Required Documentation Checklist</div>
                  {["Patient rights acknowledged at admission","Notice of Privacy Practices (HIPAA NPP) signed","Comprehensive assessment completed","Plan of care signed by physician","Medication list documented","Risk assessment for hospitalization","Emergency contacts documented","Consent for services signed"].map(item=>{
                    const doc = patientDocs.find(d=>d.patient_id===p.id&&d.item===item);
                    const checked = doc?.checked||false;
                    const fileUrl = doc?.file_url||null;
                    const toggle = async () => {
                      const newChecked = !checked;
                      if(doc){
                        setPatientDocs(prev=>prev.map(d=>d.id===doc.id?{...d,checked:newChecked}:d));
                        await supabase.from('patient_documents').update({checked:newChecked}).eq('id',doc.id);
                      } else {
                        const newDoc = {patient_id:p.id,agency_id:agency?.id,item,checked:newChecked,file_url:null};
                        const {data} = await supabase.from('patient_documents').insert(newDoc).select().single();
                        if(data) setPatientDocs(prev=>[...prev,data]);
                      }
                    };
                    const uploadDoc = async (file) => {
                      if(!file) return;
                      const url = await saveToDocumentLibrary(file,"Certification",`${p.name} — ${item}`,agency?.id,null,null);
                      if(doc){
                        setPatientDocs(prev=>prev.map(d=>d.id===doc.id?{...d,file_url:url,checked:true}:d));
                        await supabase.from('patient_documents').update({file_url:url,checked:true}).eq('id',doc.id);
                      } else {
                        const newDoc = {patient_id:p.id,agency_id:agency?.id,item,checked:true,file_url:url};
                        const {data} = await supabase.from('patient_documents').insert(newDoc).select().single();
                        if(data) setPatientDocs(prev=>[...prev,data]);
                      }
                    };
                    return (
                      <div key={item} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.light}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={toggle}>
                          <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${checked?C.teal:C.light}`,background:checked?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {checked&&<span style={{color:"#fff",fontSize:11,fontWeight:900}}>✓</span>}
                          </div>
                          <span style={{fontSize:13,color:checked?C.slate:C.gray}}>{item}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {fileUrl&&<span onClick={()=>window.open(fileUrl,"_blank")} style={{fontSize:11,color:C.blue,cursor:"pointer",fontWeight:600}}>View</span>}
                          <label style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:5,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                            📎<input type="file" style={{display:"none"}} onChange={e=>uploadDoc(e.target.files[0])}/>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {showModal&&(
        <Modal title="Add Patient" onClose={()=>setShowModal(false)}>
          <Field label="Patient Name" value={np.name} onChange={v=>setNp({...np,name:v})} required/>
          <Field label="Date of Birth" type="date" value={np.dob} onChange={v=>setNp({...np,dob:v})}/>
          <Field label="Primary Diagnosis" value={np.diagnosis} required onChange={v=>setNp({...np,diagnosis:v})}/>
          <Field label="Start of Care Date" type="date" value={np.soc_date} onChange={v=>setNp({...np,soc_date:v})}/>
          <Field label="Physician Name" value={np.physician} required onChange={v=>setNp({...np,physician:v})}/>
          <Dropdown label="Risk Level" value={np.risk_level} onChange={v=>setNp({...np,risk_level:v})} options={[{value:"low",label:"Low Risk"},{value:"medium",label:"Medium Risk"},{value:"high",label:"High Risk"}]}/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Add Patient</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── QAPI ─────────────────────────────────────────────────────────
const QAPIScreen = ({qapiMeetings,setQapiMeetings,qapiMetrics,setQapiMetrics,agency}) => {
  const [showMeetingModal,setShowMeetingModal]=useState(false);
  const [showMetricModal,setShowMetricModal]=useState(false);
  const [nm,setNm]=useState({meeting_date:today(),quarter:"",attendees:"",topics:"",action_items:"",minutes_documented:true});
  const [nmt,setNmt]=useState({metric:"",value:"",target:"",trend:"stable",period:""});

  const addMeeting = async () => {
    if(!nm.meeting_date||!nm.quarter) return;
    const tempId = uid();
    const mData = {meeting_date:nm.meeting_date,quarter:nm.quarter,attendees:nm.attendees||null,topics:nm.topics||null,action_items:nm.action_items||null,minutes_documented:nm.minutes_documented,agency_id:agency?.id};
    setQapiMeetings(prev=>[...prev,{id:tempId,...mData}]);
    setNm({meeting_date:today(),quarter:"",attendees:"",topics:"",action_items:"",minutes_documented:true});setShowMeetingModal(false);
    const {data,error} = await supabase.from('qapi_meetings').insert(mData).select().single();
    if(error) console.error('QAPI meeting:',error);
    if(data) setQapiMeetings(prev=>prev.map(m=>m.id===tempId?data:m));
  };
  const addMetric = async () => {
    if(!nmt.metric) return;
    const tempId = uid();
    const mtData = {metric:nmt.metric,value:nmt.value||null,target:nmt.target||null,trend:nmt.trend,period:nmt.period||null,agency_id:agency?.id};
    setQapiMetrics(prev=>[...prev,{id:tempId,...mtData}]);
    setNmt({metric:"",value:"",target:"",trend:"stable",period:""});setShowMetricModal(false);
    const {data,error} = await supabase.from('qapi_metrics').insert(mtData).select().single();
    if(error) console.error('Metric insert:',error);
    if(data) setQapiMetrics(prev=>prev.map(m=>m.id===tempId?data:m));
  };

  const lastMeeting=qapiMeetings.sort((a,b)=>new Date(b.meeting_date)-new Date(a.meeting_date))[0];
  const daysSinceMeeting=lastMeeting?Math.abs(daysUntil(lastMeeting.meeting_date)):999;
  const meetingOverdue=daysSinceMeeting>90;

  return (
    <div className="fade-in">
      <PageHeader title="QAPI — Quality Assurance & Performance Improvement"
        subtitle="§484.65 — Quarterly meetings required · Data-driven quality program"
        action={<div style={{display:"flex",gap:8}}><Btn variant="ghost" onClick={()=>setShowMetricModal(true)}>+ Metric</Btn><Btn onClick={()=>setShowMeetingModal(true)}>+ Meeting</Btn></div>}/>

      <TipBox>
        <strong>CMS Requirement:</strong> QAPI meetings must be held at minimum quarterly. Minutes must be documented. Program must be data-driven and include patient/family feedback mechanisms, performance indicators, and continuous improvement action items. Surveyors request QAPI records during every survey.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Meetings" value={qapiMeetings.length} color={C.blue}/>
        <StatCard label="Last Meeting" value={lastMeeting?`${daysSinceMeeting}d ago`:"Never"} color={meetingOverdue?C.red:C.teal} sub={meetingOverdue?"OVERDUE":""} />
        <StatCard label="Meetings This Year" value={qapiMeetings.filter(m=>new Date(m.meeting_date).getFullYear()===new Date().getFullYear()).length} color={C.navy}/>
        <StatCard label="Metrics Tracked" value={qapiMetrics.length} color={C.purple}/>
      </div>

      {meetingOverdue&&(
        <div style={{background:"#FEE2E2",border:`1px solid #FECACA`,borderRadius:10,padding:14,marginBottom:20,color:C.red,fontWeight:600,fontSize:14}}>
          ⚠ QAPI meeting is overdue. Last meeting was {daysSinceMeeting} days ago. Quarterly meetings are required by CMS.
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📊 Performance Metrics</h3>
          {qapiMetrics.map(m=>{
            const trendIcon=m.trend==="improving"?"↑":m.trend==="declining"?"↓":"→";
            const trendColor=m.trend==="improving"?C.teal:m.trend==="declining"?C.red:C.amber;
            return (
              <div key={m.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                  <span style={{fontSize:14,fontWeight:600,color:C.slate}}>{m.metric}</span>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:13,fontWeight:700,color:C.navy}}>{m.value}</span>
                    <span style={{color:trendColor,fontWeight:700}}>{trendIcon}</span>
                  </div>
                </div>
                <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Target: {m.target} · {m.period}</div>
              <Btn variant="danger" size="sm" onClick={async()=>{if(!window.confirm("Delete this metric?"))return;setQapiMetrics(prev=>prev.filter(x=>x.id!==m.id));await supabase.from("qapi_metrics").delete().eq("id",m.id);}}>Delete</Btn>
              </div>
            );
          })}
          {qapiMetrics.length===0&&<div style={{color:C.gray,fontSize:13}}>No metrics added yet.</div>}
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📅 Meeting Log</h3>
          {qapiMeetings.sort((a,b)=>new Date(b.meeting_date)-new Date(a.meeting_date)).map(m=>(
            <div key={m.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:14,fontWeight:600,color:C.slate}}>{m.quarter}</span>
                <Badge label={m.minutes_documented?"Minutes Documented":"No Minutes"} color={m.minutes_documented?C.teal:C.red} bg={m.minutes_documented?"#CCFBF1":"#FEE2E2"}/>
              </div>
              <div style={{fontSize:12,color:C.gray,marginBottom:2}}>{fmt(m.meeting_date)} · {m.attendees}</div>
              {m.topics&&<div style={{fontSize:12,color:C.slate,marginBottom:2}}><strong>Topics:</strong> {m.topics}</div>}
              {m.action_items&&<div style={{fontSize:12,color:C.slate,marginBottom:4}}><strong>Actions:</strong> {m.action_items}</div>}
              <Btn variant="danger" size="sm" onClick={async()=>{if(!window.confirm("Delete this meeting?"))return;setQapiMeetings(prev=>prev.filter(x=>x.id!==m.id));await supabase.from("qapi_meetings").delete().eq("id",m.id);}}>Delete</Btn>
            </div>
          ))}
          {qapiMeetings.length===0&&<div style={{color:C.gray,fontSize:13}}>No meetings logged yet.</div>}
        </Card>
      </div>

      {showMeetingModal&&(
        <Modal title="Log QAPI Meeting" onClose={()=>setShowMeetingModal(false)}>
          <Field label="Meeting Date" type="date" value={nm.meeting_date} onChange={v=>setNm({...nm,meeting_date:v})} required/>
          <Field label="Quarter (e.g. Q1 2025)" value={nm.quarter} onChange={v=>setNm({...nm,quarter:v})} required/>
          <Field label="Attendees" value={nm.attendees} required onChange={v=>setNm({...nm,attendees:v})} placeholder="Admin, Clinical Manager, QA Coordinator..."/>
          <Textarea label="Topics Discussed" value={nm.topics} required onChange={v=>setNm({...nm,topics:v})} placeholder="Fall rates, readmissions, infection incidents..."/>
          <Textarea label="Action Items" value={nm.action_items} onChange={v=>setNm({...nm,action_items:v})} placeholder="Corrective actions and improvement initiatives..."/>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,cursor:"pointer"}} onClick={()=>setNm({...nm,minutes_documented:!nm.minutes_documented})}>
            <div style={{width:22,height:22,borderRadius:5,border:`2px solid ${nm.minutes_documented?C.teal:C.light}`,background:nm.minutes_documented?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {nm.minutes_documented&&<span style={{color:"#fff",fontSize:13}}>✓</span>}
            </div>
            <span style={{fontSize:14,color:C.slate}}>Meeting minutes documented</span>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowMeetingModal(false)}>Cancel</Btn>
            <Btn onClick={addMeeting}>Log Meeting</Btn>
          </div>
        </Modal>
      )}

      {showMetricModal&&(
        <Modal title="Add QAPI Metric" onClose={()=>setShowMetricModal(false)}>
          <Field label="Metric Name" value={nmt.metric} onChange={v=>setNmt({...nmt,metric:v})} placeholder="30-day Readmission Rate" required/>
          <Field label="Current Value" value={nmt.value} onChange={v=>setNmt({...nmt,value:v})} placeholder="12%"/>
          <Field label="Target" value={nmt.target} onChange={v=>setNmt({...nmt,target:v})} placeholder="<15%"/>
          <Dropdown label="Trend" value={nmt.trend} onChange={v=>setNmt({...nmt,trend:v})} options={[{value:"improving",label:"↑ Improving"},{value:"stable",label:"→ Stable"},{value:"declining",label:"↓ Declining"}]}/>
          <Field label="Period" value={nmt.period} onChange={v=>setNmt({...nmt,period:v})} placeholder="Q4 2024"/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowMetricModal(false)}>Cancel</Btn>
            <Btn onClick={addMetric}>Add Metric</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── INFECTION CONTROL ────────────────────────────────────────────
const InfectionScreen = ({infectionIncidents,setInfectionIncidents,staff,agency,saveToDocumentLibrary,checklistItems,setChecklistItems}) => {
  const [showModal,setShowModal]=useState(false);
  const [ni,setNi]=useState({date:today(),type:"",staff_involved:"",patient:"",reported:false,investigated:false,outcome:""});
  const [infFile,setInfFile]=useState(null);

  const add = async () => {
    if(!ni.type) return;
    const tempId = uid();
    let file_url = null;
    if(infFile) file_url = await saveToDocumentLibrary(infFile,"Audit Evidence",`Infection Incident — ${ni.type} ${ni.date}`,agency?.id,null,null);
    const infData = {date:ni.date,type:ni.type,staff_id:ni.staff_involved||null,patient:ni.patient||null,reported:ni.reported,investigated:ni.investigated,outcome:ni.outcome||null,file_url,agency_id:agency?.id};
    setInfectionIncidents(prev=>[...prev,{id:tempId,...infData}]);
    setNi({date:today(),type:"",staff_involved:"",patient:"",reported:false,investigated:false,outcome:""});setShowModal(false);
    const {data,error} = await supabase.from('infection_incidents').insert(infData).select().single();
    if(error) console.error('Infection insert:',error);
    if(data) setInfectionIncidents(prev=>prev.map(i=>i.id===tempId?data:i));
  };

  const IC_DEFAULTS = [
    {id:"ic1",item:"Written Infection Control Program on file",required:"§484.80",status:"met",notes:"",file_url:null},
    {id:"ic2",item:"Hand hygiene policy documented and staff trained",required:"CDC/APIC standards",status:"met",notes:"",file_url:null},
    {id:"ic3",item:"PPE policy — gloves, masks, gowns available",required:"OSHA Bloodborne Pathogens",status:"met",notes:"",file_url:null},
    {id:"ic4",item:"Bag technique policy for field staff",required:"Agency policy",status:"met",notes:"",file_url:null},
    {id:"ic5",item:"Exposure control plan documented",required:"OSHA 29 CFR 1910.1030",status:"needs_review",notes:"",file_url:null},
    {id:"ic6",item:"Annual infection control training per staff member",required:"§484.80",status:"met",notes:"",file_url:null},
    {id:"ic7",item:"Respiratory illness / COVID protocols in place",required:"CDC guidance",status:"met",notes:"",file_url:null},
    {id:"ic8",item:"Sharps disposal policy documented",required:"OSHA",status:"met",notes:"",file_url:null},
    {id:"ic9",item:"Isolation procedure policy on file",required:"§484.80",status:"met",notes:"",file_url:null},
    {id:"ic10",item:"Infection incident reporting procedure documented",required:"State law",status:"met",notes:"",file_url:null},
  ];
  const [icPolicies,setIcPolicies]=useState(()=>IC_DEFAULTS.map(d=>{
    const saved=checklistItems.find(c=>c.checklist==="ic"&&c.item_id===d.id);
    return saved?{...d,status:saved.status,notes:saved.notes||"",file_url:saved.file_url||null}:d;
  }));
  const updateICPolicy = async (id,field,value) => {
    setIcPolicies(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const existing = checklistItems.find(c=>c.checklist==="ic"&&c.item_id===id);
    const current = icPolicies.find(r=>r.id===id)||{};
    const payload = {agency_id:agency?.id,checklist:"ic",item_id:id,status:field==="status"?value:current.status,notes:field==="notes"?value:current.notes,file_url:field==="file_url"?value:current.file_url};
    if(existing){
      await supabase.from("checklist_items").update(payload).eq("id",existing.id);
      setChecklistItems(prev=>prev.map(c=>c.id===existing.id?{...c,...payload}:c));
    } else {
      const {data} = await supabase.from("checklist_items").insert(payload).select().single();
      if(data) setChecklistItems(prev=>[...prev,data]);
    }
  };
  const [uploadingIC,setUploadingIC]=useState(null);
  const uploadICEvidence = async (id,file,item) => {
    if(!file) return;
    setUploadingIC(id);
    const file_url = await saveToDocumentLibrary(file,"Audit Evidence",`IC — ${item}`,agency?.id,null,null);
    if(file_url) updateICPolicy(id,"file_url",file_url);
    setUploadingIC(null);
  };

  return (
    <div className="fade-in">
      <PageHeader title="Infection Control" subtitle="§484.80 — Level 1 CoP · Most frequently cited deficiency nationally"
        action={<Btn onClick={()=>setShowModal(true)}>+ Log Incident</Btn>}/>

      <TipBox>
        <strong>⚠ Most Cited:</strong> Infection control is the #1 most cited deficiency for home health agencies. Surveyors conduct in-home observations to assess hand hygiene, bag technique, and PPE use. A written IC program, annual staff training, and an incident log are mandatory.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Incidents" value={infectionIncidents.length} color={C.navy}/>
        <StatCard label="Under Investigation" value={infectionIncidents.filter(i=>!i.investigated).length} color={infectionIncidents.filter(i=>!i.investigated).length>0?C.amber:C.teal}/>
        <StatCard label="This Month" value={infectionIncidents.filter(i=>new Date(i.date).getMonth()===new Date().getMonth()).length} color={C.blue}/>
        <StatCard label="IC Policies Active" value={icPolicies.filter(p=>p.status==="met").length} color={C.teal} sub={`of ${icPolicies.length} required`}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 IC Policy Checklist</h3>
          {icPolicies.map(r=>(
            <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1,marginRight:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{r.item}</div>
                  <div style={{fontSize:11,color:C.gray,marginBottom:4}}>{r.required}</div>
                  <input value={r.notes||""} onChange={e=>updateICPolicy(r.id,"notes",e.target.value)}
                    placeholder="Add evidence notes..."
                    style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",marginBottom:6}}/>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                      📎 {uploadingIC===r.id?"Uploading...":"Attach Evidence"}
                      <input type="file" style={{display:"none"}} onChange={e=>uploadICEvidence(r.id,e.target.files[0],r.item)} disabled={uploadingIC===r.id}/>
                    </label>
                    {r.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(r.file_url,"_blank")}>View Evidence</Btn>}
                    {r.file_url&&<Badge label="Evidence ✓" color={C.teal} bg="#CCFBF1"/>}
                  </div>
                </div>
                <select value={r.status} onChange={e=>updateICPolicy(r.id,"status",e.target.value)}
                  style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",minWidth:130}}>
                  <option value="met">✓ Met</option>
                  <option value="needs_review">⚠ Needs Review</option>
                  <option value="not_met">✗ Not Met</option>
                </select>
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>🚨 Incident Log</h3>
          {infectionIncidents.length===0?(
            <div style={{color:C.teal,fontSize:14,fontWeight:600}}>✓ No infection incidents recorded</div>
          ):infectionIncidents.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(inc=>(
            <div key={inc.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:14,fontWeight:600,color:C.slate}}>{inc.type}</span>
                <Badge label={inc.investigated?"Investigated":"Pending"} color={inc.investigated?C.teal:C.amber} bg={inc.investigated?"#CCFBF1":"#FEF3C7"}/>
              </div>
              <div style={{fontSize:12,color:C.gray,marginBottom:2}}>{fmt(inc.date)} · Patient: {inc.patient}</div>
              {inc.outcome&&<div style={{fontSize:12,color:C.slate,fontStyle:"italic"}}>{inc.outcome}</div>}
            </div>
          ))}
        </Card>
      </div>

      {showModal&&(
        <Modal title="Log Infection Incident" onClose={()=>setShowModal(false)}>
          <Field label="Date" type="date" value={ni.date} onChange={v=>setNi({...ni,date:v})} required/>
          <Field label="Infection Type" value={ni.type} onChange={v=>setNi({...ni,type:v})} placeholder="UTI, Wound infection, Respiratory..." required/>
          <Dropdown label="Staff Involved" value={ni.staff_involved} onChange={v=>setNi({...ni,staff_involved:v})} options={[{value:"",label:"Select staff..."},...staff.map(s=>({value:s.id,label:s.name}))]}/>
          <Field label="Patient Name" value={ni.patient} required onChange={v=>setNi({...ni,patient:v})}/>
          <Textarea label="Outcome / Resolution" value={ni.outcome} onChange={v=>setNi({...ni,outcome:v})} placeholder="Actions taken, treatment, IC protocol changes..."/>
          <div style={{display:"flex",gap:16,marginBottom:16}}>
            {[{key:"reported",label:"Reported to state/county"},{key:"investigated",label:"Investigation complete"}].map(f=>(
              <div key={f.key} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>setNi({...ni,[f.key]:!ni[f.key]})}>
                <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${ni[f.key]?C.teal:C.light}`,background:ni[f.key]?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {ni[f.key]&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
                </div>
                <span style={{fontSize:13,color:C.slate}}>{f.label}</span>
              </div>
            ))}
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Document (optional)</label>
            <input type="file" onChange={e=>setInfFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {infFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {infFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Log Incident</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── EMERGENCY PREPAREDNESS ───────────────────────────────────────
const EPScreen = ({epDrills,setEpDrills,staff,agency,saveToDocumentLibrary,checklistItems,setChecklistItems}) => {
  const [showModal,setShowModal]=useState(false);
  const [nd,setNd]=useState({drill_type:"Tabletop Exercise",date:today(),facilitator:"",participants:"",scenario:"",documented:true,next_due:daysFromNow(365)});
  const [epFile,setEpFile]=useState(null);

  const add = async () => {
    if(!nd.date) return;
    const tempId = uid();
    let file_url = null;
    if(epFile) file_url = await saveToDocumentLibrary(epFile,"Emergency Preparedness",`EP Drill — ${nd.drill_type} ${nd.date}`,agency?.id,null,null);
    const epData = {drill_type:nd.drill_type,date:nd.date,facilitator:nd.facilitator||null,participants:nd.participants||null,scenario:nd.scenario||null,documented:nd.documented,next_due:nd.next_due||null,file_url,agency_id:agency?.id};
    setEpDrills(prev=>[...prev,{id:tempId,...epData}]);
    setNd({drill_type:"Tabletop Exercise",date:today(),facilitator:"",participants:"",scenario:"",documented:true,next_due:daysFromNow(365)});setShowModal(false);
    const {data,error} = await supabase.from('ep_drills').insert(epData).select().single();
    if(error) console.error('EP insert:',error);
    if(data) setEpDrills(prev=>prev.map(d=>d.id===tempId?data:d));
  };

  const lastDrill=epDrills.sort((a,b)=>new Date(b.date)-new Date(a.date))[0];
  const drillOverdue=lastDrill?daysUntil(lastDrill.next_due)<0:true;

  const EP_DEFAULTS = [
    {id:"ep1",item:"Written Emergency Preparedness Plan on file",ref:"42 CFR §484.102 / Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep2",item:"EP plan reviewed and updated annually",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep3",item:"Annual drill or full-scale exercise documented",ref:"Appendix Z",status:"needs_review",notes:"",file_url:null},
    {id:"ep4",item:"Staff trained on EP plan (all staff)",ref:"Appendix Z",status:"needs_review",notes:"",file_url:null},
    {id:"ep5",item:"Patient tracking during emergency plan",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep6",item:"Communication plan — backup systems documented",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep7",item:"Coordination with local emergency management",ref:"Appendix Z",status:"needs_review",notes:"",file_url:null},
    {id:"ep8",item:"Policies for sheltering in place documented",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep9",item:"Evacuation routes and procedures documented",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep10",item:"Business continuity / disaster recovery plan",ref:"Appendix Z",status:"met",notes:"",file_url:null},
    {id:"ep11",item:"Volunteer staff protocols documented",ref:"Appendix Z",status:"needs_review",notes:"",file_url:null},
    {id:"ep12",item:"After-action report completed after each drill",ref:"Appendix Z",status:"needs_review",notes:"",file_url:null},
  ];
  const [epReqs,setEpReqs]=useState(()=>EP_DEFAULTS.map(d=>{
    const saved=checklistItems.find(c=>c.checklist==="ep"&&c.item_id===d.id);
    return saved?{...d,status:saved.status,notes:saved.notes||"",file_url:saved.file_url||null}:d;
  }));
  const updateEPReq = async (id,field,value) => {
    setEpReqs(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const existing = checklistItems.find(c=>c.checklist==="ep"&&c.item_id===id);
    const current = epReqs.find(r=>r.id===id)||{};
    const payload = {agency_id:agency?.id,checklist:"ep",item_id:id,status:field==="status"?value:current.status,notes:field==="notes"?value:current.notes,file_url:field==="file_url"?value:current.file_url};
    if(existing){
      await supabase.from("checklist_items").update(payload).eq("id",existing.id);
      setChecklistItems(prev=>prev.map(c=>c.id===existing.id?{...c,...payload}:c));
    } else {
      const {data} = await supabase.from("checklist_items").insert(payload).select().single();
      if(data) setChecklistItems(prev=>[...prev,data]);
    }
  };
  const [uploadingEP,setUploadingEP]=useState(null);
  const uploadEPEvidence = async (id,file,item) => {
    if(!file) return;
    setUploadingEP(id);
    const file_url = await saveToDocumentLibrary(file,"Emergency Preparedness",`EP — ${item}`,agency?.id,null,null);
    if(file_url) updateEPReq(id,"file_url",file_url);
    setUploadingEP(null);
  };

  return (
    <div className="fade-in">
      <PageHeader title="Emergency Preparedness" subtitle="42 CFR §484.102 / Appendix Z — Annual drill required"
        action={<Btn onClick={()=>setShowModal(true)}>+ Log Drill</Btn>}/>

      <TipBox>
        <strong>CMS Requirement (Appendix Z):</strong> All HHAs must have a written EP plan tested annually via a full-scale exercise or tabletop drill. All staff must be trained on the EP plan. Compliance with EP requirements is assessed separately from the main survey using Appendix Z protocols.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Drills" value={epDrills.length} color={C.blue}/>
        <StatCard label="Last Drill" value={lastDrill?`${Math.abs(daysUntil(lastDrill.date))}d ago`:"Never"} color={drillOverdue?C.red:C.teal} sub={drillOverdue?"ANNUAL DRILL OVERDUE":""}/>
        <StatCard label="Requirements Met" value={epReqs.filter(r=>r.status==="met").length} color={C.teal} sub={`of ${epReqs.length}`}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 EP Requirements Checklist</h3>
          {epReqs.map(r=>(
            <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1,marginRight:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{r.item}</div>
                  <div style={{fontSize:11,color:C.gray,marginBottom:4}}>{r.ref}</div>
                  <input value={r.notes||""} onChange={e=>updateEPReq(r.id,"notes",e.target.value)}
                    placeholder="Add evidence notes..."
                    style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",marginBottom:6}}/>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                      📎 {uploadingEP===r.id?"Uploading...":"Attach Evidence"}
                      <input type="file" style={{display:"none"}} onChange={e=>uploadEPEvidence(r.id,e.target.files[0],r.item)} disabled={uploadingEP===r.id}/>
                    </label>
                    {r.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(r.file_url,"_blank")}>View Evidence</Btn>}
                    {r.file_url&&<Badge label="Evidence ✓" color={C.teal} bg="#CCFBF1"/>}
                  </div>
                </div>
                <select value={r.status} onChange={e=>updateEPReq(r.id,"status",e.target.value)}
                  style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",minWidth:130}}>
                  <option value="met">✓ Met</option>
                  <option value="needs_review">⚠ Needs Review</option>
                  <option value="not_met">✗ Not Met</option>
                </select>
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>🚨 Drill History</h3>
          {epDrills.length===0?(
            <div style={{color:C.red,fontSize:14,fontWeight:600}}>⚠ No drills on record — annual drill required</div>
          ):epDrills.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(d=>(
            <div key={d.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:14,fontWeight:600,color:C.slate}}>{d.drill_type}</span>
                <Badge label={d.documented?"Documented":"Undocumented"} color={d.documented?C.teal:C.red} bg={d.documented?"#CCFBF1":"#FEE2E2"}/>
              </div>
              <div style={{fontSize:12,color:C.gray,marginBottom:2}}>{fmt(d.date)} · Facilitator: {d.facilitator}</div>
              <div style={{fontSize:12,color:C.slate,marginBottom:2}}>Scenario: {d.scenario}</div>
              <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Participants: {d.participants}</div>
              {d.file_url&&<span onClick={()=>window.open(d.file_url,'_blank')} style={{fontSize:11,color:C.blue,cursor:"pointer",fontWeight:600}}>View Report</span>}
              <div style={{fontSize:12,color:daysUntil(d.next_due)<0?C.red:C.gray,fontWeight:daysUntil(d.next_due)<0?700:400,marginTop:4}}>
                Next drill due: {fmt(d.next_due)} {daysUntil(d.next_due)<0?"— OVERDUE":""}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {showModal&&(
        <Modal title="Log Emergency Preparedness Drill" onClose={()=>setShowModal(false)}>
          <Dropdown label="Drill Type" value={nd.drill_type} onChange={v=>setNd({...nd,drill_type:v})} options={[{value:"Tabletop Exercise",label:"Tabletop Exercise"},{value:"Full-Scale Exercise",label:"Full-Scale Exercise"},{value:"Functional Exercise",label:"Functional Exercise"},{value:"Drill",label:"Drill"}]}/>
          <Field label="Date" type="date" value={nd.date} onChange={v=>setNd({...nd,date:v})} required/>
          <Field label="Facilitator" value={nd.facilitator} required onChange={v=>setNd({...nd,facilitator:v})} placeholder="Admin / Clinical Manager"/>
          <Field label="Participants" value={nd.participants} onChange={v=>setNd({...nd,participants:v})} placeholder="All clinical staff, admin..."/>
          <Field label="Scenario" value={nd.scenario} required onChange={v=>setNd({...nd,scenario:v})} placeholder="Hurricane evacuation, power outage, pandemic..."/>
          <Field label="Next Drill Due" type="date" value={nd.next_due} onChange={v=>setNd({...nd,next_due:v})}/>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,cursor:"pointer"}} onClick={()=>setNd({...nd,documented:!nd.documented})}>
            <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${nd.documented?C.teal:C.light}`,background:nd.documented?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {nd.documented&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
            </div>
            <span style={{fontSize:13,color:C.slate}}>After-action report completed</span>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Drill Report (optional)</label>
            <input type="file" onChange={e=>setEpFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {epFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {epFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Log Drill</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── HIPAA COMPLIANCE ─────────────────────────────────────────────
const HIPAAScreen = ({hipaaTraining,setHipaaTraining,baas,setBaas,staff,agency,saveToDocumentLibrary,checklistItems,setChecklistItems}) => {
  const [showBAAModal,setShowBAAModal]=useState(false);
  const [nb,setNb]=useState({vendor:"",signed_date:today(),expiry:"",status:"active"});
  const [baaFile,setBaaFile]=useState(null);

  const markTrained = async (staffId) => {
    const existing = hipaaTraining.find(h=>h.staff_id===staffId);
    const update = {completed:true,training_date:today(),expiry:daysFromNow(365)};
    if(existing){
      await supabase.from('hipaa_training').update(update).eq('staff_id',staffId);
    } else {
      await supabase.from('hipaa_training').insert({...update,staff_id:staffId,agency_id:agency?.id||"agency-1"});
    }
    setHipaaTraining(prev=>prev.map(h=>h.staff_id===staffId?{...h,...update}:h));
  };

  const addBAA = async () => {
    if(!nb.vendor) return;
    if(!baaFile){ alert("Please attach the signed BAA document."); return; }
    const docName = `BAA — ${nb.vendor}`;
    await saveToDocumentLibrary(baaFile,"HIPAA",docName,agency?.id||"agency-1",nb.expiry,nb.signed_date);
    const newB = {...nb,agency_id:agency?.id||"agency-1"};
    const {data,error} = await supabase.from('business_associate_agreements').insert(newB).select().single();
    if(!error&&data) setBaas(prev=>[...prev,data]);
    else setBaas(prev=>[...prev,{id:uid(),...newB}]);
    setNb({vendor:"",signed_date:today(),expiry:"",status:"active"});setBaaFile(null);setShowBAAModal(false);
  };

  const HIPAA_DEFAULTS = [
    {id:"hc1",item:"HIPAA Privacy Officer designated",status:"met",ref:"45 CFR §164.530",notes:"",file_url:null},
    {id:"hc2",item:"Notice of Privacy Practices (NPP) current and posted",status:"met",ref:"45 CFR §164.520",notes:"",file_url:null},
    {id:"hc3",item:"Annual HIPAA risk assessment completed",status:"needs_review",ref:"45 CFR §164.308",notes:"",file_url:null},
    {id:"hc4",item:"Mobile device / BYOD policy documented",status:"met",ref:"45 CFR §164.310",notes:"",file_url:null},
    {id:"hc5",item:"Breach notification procedure documented",status:"met",ref:"45 CFR §164.400",notes:"",file_url:null},
    {id:"hc6",item:"PHI access log / disclosure accounting maintained",status:"needs_review",ref:"45 CFR §164.528",notes:"",file_url:null},
    {id:"hc7",item:"Data retention policy (6-year minimum)",status:"met",ref:"45 CFR §164.530",notes:"",file_url:null},
    {id:"hc8",item:"Sanctions policy documented",status:"met",ref:"45 CFR §164.530",notes:"",file_url:null},
    {id:"hc9",item:"Staff HIPAA training completed annually",status:"needs_review",ref:"45 CFR §164.530",notes:"",file_url:null},
    {id:"hc10",item:"Business Associate Agreements on file for all vendors",status:"needs_review",ref:"45 CFR §164.308",notes:"",file_url:null},
    {id:"hc11",item:"Secure messaging system for field staff PHI",status:"met",ref:"45 CFR §164.312",notes:"",file_url:null},
    {id:"hc12",item:"Encryption on all devices containing PHI",status:"needs_review",ref:"45 CFR §164.312",notes:"",file_url:null},
  ];
  const [hipaaChecklist,setHipaaChecklist]=useState(()=>HIPAA_DEFAULTS.map(d=>{
    const saved=checklistItems.find(c=>c.checklist==="hipaa"&&c.item_id===d.id);
    return saved?{...d,status:saved.status,notes:saved.notes||"",file_url:saved.file_url||null}:d;
  }));
  const updateHipaaCheck = async (id,field,value) => {
    setHipaaChecklist(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const existing = checklistItems.find(c=>c.checklist==="hipaa"&&c.item_id===id);
    const current = hipaaChecklist.find(r=>r.id===id)||{};
    const payload = {agency_id:agency?.id,checklist:"hipaa",item_id:id,status:field==="status"?value:current.status,notes:field==="notes"?value:current.notes,file_url:field==="file_url"?value:current.file_url};
    if(existing){
      await supabase.from("checklist_items").update(payload).eq("id",existing.id);
      setChecklistItems(prev=>prev.map(c=>c.id===existing.id?{...c,...payload}:c));
    } else {
      const {data} = await supabase.from("checklist_items").insert(payload).select().single();
      if(data) setChecklistItems(prev=>[...prev,data]);
    }
  };
  const [uploadingHipaa,setUploadingHipaa]=useState(null);
  const uploadHipaaEvidence = async (id,file,item) => {
    if(!file) return;
    setUploadingHipaa(id);
    const file_url = await saveToDocumentLibrary(file,"HIPAA",`HIPAA — ${item}`,agency?.id,null,null);
    if(file_url) updateHipaaCheck(id,"file_url",file_url);
    setUploadingHipaa(null);
  };

  return (
    <div className="fade-in">
      <PageHeader title="HIPAA Compliance" subtitle="Privacy · Security · Breach Notification Rules"
        action={<Btn onClick={()=>setShowBAAModal(true)}>+ Add BAA</Btn>}/>

      <TipBox>
        <strong>HIPAA:</strong> All home health agencies are covered entities under HIPAA. OCR enforcement focuses heavily on patient rights violations and failure to respond to access requests. HIPAA training must be completed at hire and annually. BAAs required for all vendors handling PHI.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Staff Trained" value={hipaaTraining.filter(h=>h.completed).length} color={C.teal} sub={`of ${hipaaTraining.length}`}/>
        <StatCard label="Training Gaps" value={hipaaTraining.filter(h=>!h.completed).length} color={hipaaTraining.filter(h=>!h.completed).length>0?C.red:C.teal}/>
        <StatCard label="Active BAAs" value={baas.filter(b=>b.status==="active").length} color={C.blue}/>
        <StatCard label="Expired BAAs" value={baas.filter(b=>b.status==="expired").length} color={baas.filter(b=>b.status==="expired").length>0?C.red:C.teal}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 HIPAA Compliance Checklist</h3>
          {hipaaChecklist.map(r=>(
            <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1,marginRight:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{r.item}</div>
                  <div style={{fontSize:11,color:C.gray,marginBottom:4}}>{r.ref}</div>
                  <input value={r.notes||""} onChange={e=>updateHipaaCheck(r.id,"notes",e.target.value)}
                    placeholder="Add evidence notes..."
                    style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",marginBottom:6}}/>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                      📎 {uploadingHipaa===r.id?"Uploading...":"Attach Evidence"}
                      <input type="file" style={{display:"none"}} onChange={e=>uploadHipaaEvidence(r.id,e.target.files[0],r.item)} disabled={uploadingHipaa===r.id}/>
                    </label>
                    {r.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(r.file_url,"_blank")}>View Evidence</Btn>}
                    {r.file_url&&<Badge label="Evidence ✓" color={C.teal} bg="#CCFBF1"/>}
                  </div>
                </div>
                <select value={r.status} onChange={e=>updateHipaaCheck(r.id,"status",e.target.value)}
                  style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",minWidth:130}}>
                  <option value="met">✓ Met</option>
                  <option value="needs_review">⚠ Needs Review</option>
                  <option value="not_met">✗ Not Met</option>
                </select>
              </div>
            </div>
          ))}
        </Card>

        <div>
          <Card style={{marginBottom:20}}>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>👤 Staff HIPAA Training</h3>
            {staff.filter(s=>s.status!=="archived").map(s=>{
              const training=hipaaTraining.find(h=>h.staff_id===s.id);
              const trained=training?.completed;
              const expiring=trained&&daysUntil(training.expiry)<60;
              return (
                <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${C.light}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <Avatar name={s.name} size={30}/>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{s.name}</div>
                      <div style={{fontSize:11,color:C.gray}}>{training?.training_date?`Trained ${fmt(training.training_date)}`:"Not trained"}</div>
                    </div>
                  </div>
                  {trained?(
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {expiring&&<Badge label="Renewal Due" color={C.amber} bg="#FEF3C7"/>}
                      <Badge label="Trained ✓" color={C.teal} bg="#CCFBF1"/>
                    </div>
                  ):(
                    <Btn variant="primary" size="sm" onClick={()=>markTrained(s.id)}>Mark Trained</Btn>
                  )}
                </div>
              );
            })}
          </Card>

          <Card>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📝 Business Associate Agreements</h3>
            {baas.map(b=>(
              <div key={b.id} style={{padding:"9px 0",borderBottom:`1px solid ${C.light}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                  <span style={{fontSize:13,fontWeight:600,color:C.slate}}>{b.vendor}</span>
                  <StatusBadge status={b.status}/>
                </div>
                <div style={{fontSize:11,color:C.gray}}>Signed {fmt(b.signed_date)} · Expires {fmt(b.expiry)}</div>
              </div>
            ))}
          </Card>
        </div>
      </div>

      {showBAAModal&&(
        <Modal title="Add Business Associate Agreement" onClose={()=>setShowBAAModal(false)}>
          <Field label="Vendor Name" value={nb.vendor} onChange={v=>setNb({...nb,vendor:v})} placeholder="Billing software, EHR vendor, email provider..." required/>
          <Field label="Date Signed" type="date" value={nb.signed_date} onChange={v=>setNb({...nb,signed_date:v})}/>
          <Field label="Expiry Date" type="date" value={nb.expiry} required onChange={v=>setNb({...nb,expiry:v})}/>
          <Dropdown label="Status" value={nb.status} onChange={v=>setNb({...nb,status:v})} options={[{value:"active",label:"Active"},{value:"expired",label:"Expired"},{value:"pending",label:"Pending"}]}/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Signed BAA <span style={{color:C.red}}>*</span></label>
            <input type="file" onChange={e=>setBaaFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {baaFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {baaFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowBAAModal(false)}>Cancel</Btn>
            <Btn onClick={addBAA}>Add BAA</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── GOVERNING BODY ───────────────────────────────────────────────
const GoverningScreen = ({agency,setAgency,checklistItems,setChecklistItems,saveToDocumentLibrary}) => {
  const [editing,setEditing]=useState(false);
  const [form,setForm]=useState({...agency});
  const [saved,setSaved]=useState(false);

  const save = () => {
    setAgency(form);setSaved(true);setEditing(false);
    setTimeout(()=>setSaved(false),2500);
  };

  const GB_DEFAULTS = [
    {id:"gb1",item:"Governing body defined and active",ref:"§484.105",status:"met",notes:"",file_url:null},
    {id:"gb2",item:"Administrator qualifications documented",ref:"§484.105(c)",status:"met",notes:"",file_url:null},
    {id:"gb3",item:"Alternate administrator designated",ref:"§484.105(c)",status:"met",notes:"",file_url:null},
    {id:"gb4",item:"Clinical manager designated with qualifications",ref:"§484.105(d)",status:"met",notes:"",file_url:null},
    {id:"gb5",item:"Governing body meeting minutes on file (annual)",ref:"§484.105",status:"met",notes:"",file_url:null},
    {id:"gb6",item:"Acceptance-to-service policy adopted (Nov 2024)",ref:"§484.105(i) — NEW 2024",status:"met",notes:"",file_url:null},
    {id:"gb7",item:"Organizational chart on file",ref:"§484.105",status:"met",notes:"",file_url:null},
    {id:"gb8",item:"On-call coverage policy documented",ref:"§484.105(e)",status:"met",notes:"",file_url:null},
    {id:"gb9",item:"Administrator continuity plan (designee for absence)",ref:"§484.105(c)",status:"met",notes:"",file_url:null},
    {id:"gb10",item:"Medicare provider number current",ref:"CMS enrollment",status:"met",notes:"",file_url:null},
    {id:"gb11",item:"NPI number registered and current",ref:"NPPES",status:"met",notes:"",file_url:null},
    {id:"gb12",item:"State agency license current",ref:"State law",status:"met",notes:"",file_url:null},
    {id:"gb13",item:"Federal Medicare certification current",ref:"42 CFR §484",status:"met",notes:"",file_url:null},
  ];
  const [gbReqs,setGbReqs]=useState(()=>GB_DEFAULTS.map(d=>{
    const saved=checklistItems.find(c=>c.checklist==="gb"&&c.item_id===d.id);
    return saved?{...d,status:saved.status,notes:saved.notes||"",file_url:saved.file_url||null}:d;
  }));
  const updateGBReq = async (id,field,value) => {
    setGbReqs(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const existing = checklistItems.find(c=>c.checklist==="gb"&&c.item_id===id);
    const current = gbReqs.find(r=>r.id===id)||{};
    const payload = {agency_id:agency?.id,checklist:"gb",item_id:id,status:field==="status"?value:current.status,notes:field==="notes"?value:current.notes,file_url:field==="file_url"?value:current.file_url};
    if(existing){
      await supabase.from("checklist_items").update(payload).eq("id",existing.id);
      setChecklistItems(prev=>prev.map(c=>c.id===existing.id?{...c,...payload}:c));
    } else {
      const {data} = await supabase.from("checklist_items").insert(payload).select().single();
      if(data) setChecklistItems(prev=>[...prev,data]);
    }
  };
  const [uploadingGB,setUploadingGB]=useState(null);
  const uploadGBEvidence = async (id,file,item) => {
    if(!file) return;
    setUploadingGB(id);
    const file_url = await saveToDocumentLibrary(file,"Governing Body",`GB — ${item}`,agency?.id,null,null);
    if(file_url) updateGBReq(id,"file_url",file_url);
    setUploadingGB(null);
  };

  return (
    <div className="fade-in">
      <PageHeader title="Governing Body & Administration" subtitle="§484.105 — Organizational structure and leadership requirements"
        action={<Btn onClick={()=>setEditing(!editing)}>{editing?"Cancel":"Edit Info"}</Btn>}/>

      <TipBox>
        <strong>NEW 2024:</strong> CMS added §484.105(i) — every HHA must now develop, implement, and maintain an <strong>Acceptance-to-Service Policy</strong> applied consistently to all patient referrals. This went into effect November 2024 and will be surveyed.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>🏛 Agency Leadership</h3>
          {editing?(
            <>
              <Field label="Administrator" value={form.administrator} required onChange={v=>setForm({...form,administrator:v})}/>
              <Field label="Alternate Administrator" value={form.alternate_admin} onChange={v=>setForm({...form,alternate_admin:v})}/>
              <Field label="Clinical Manager" value={form.clinical_manager} required onChange={v=>setForm({...form,clinical_manager:v})}/>
              <Field label="NPI Number" value={form.npi} required onChange={v=>setForm({...form,npi:v})}/>
              <Field label="Medicare Provider Number" value={form.medicare_number} onChange={v=>setForm({...form,medicare_number:v})}/>
              <Field label="State License Number" value={form.state_license} required onChange={v=>setForm({...form,state_license:v})}/>
              <Field label="State License Expiry" type="date" value={form.state_license_expiry} required onChange={v=>setForm({...form,state_license_expiry:v})}/>
              <Field label="Federal Cert Expiry" type="date" value={form.federal_cert_expiry} required onChange={v=>setForm({...form,federal_cert_expiry:v})}/>
              <Field label="Accreditation Body" value={form.accreditation} onChange={v=>setForm({...form,accreditation:v})}/>
              <Field label="Accreditation Expiry" type="date" value={form.accreditation_expiry} onChange={v=>setForm({...form,accreditation_expiry:v})}/>
              <Btn onClick={save} variant={saved?"teal":"primary"}>{saved?"✓ Saved":"Save Changes"}</Btn>
            </>
          ):(
            <div>
              {[
                {label:"Administrator",value:agency.administrator},
                {label:"Alternate Administrator",value:agency.alternate_admin},
                {label:"Clinical Manager",value:agency.clinical_manager},
                {label:"NPI Number",value:agency.npi},
                {label:"Medicare Provider #",value:agency.medicare_number},
                {label:"State License",value:`${agency.state_license} — expires ${fmt(agency.state_license_expiry)}`},
                {label:"Federal Certification",value:`Expires ${fmt(agency.federal_cert_expiry)}`},
                {label:"Accreditation",value:`${agency.accreditation} — expires ${fmt(agency.accreditation_expiry)}`},
              ].map(f=>(
                <div key={f.label} style={{padding:"8px 0",borderBottom:`1px solid ${C.light}`}}>
                  <div style={{fontSize:12,color:C.gray,marginBottom:2}}>{f.label}</div>
                  <div style={{fontSize:14,fontWeight:600,color:C.slate}}>{f.value||"—"}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 Governing Body Requirements</h3>
          {gbReqs.map(r=>(
            <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1,marginRight:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{r.item}</div>
                  <div style={{fontSize:11,color:r.ref.includes("NEW")?"#7C3AED":C.gray,fontWeight:r.ref.includes("NEW")?700:400,marginBottom:4}}>{r.ref}</div>
                  <input value={r.notes||""} onChange={e=>updateGBReq(r.id,"notes",e.target.value)}
                    placeholder="Add evidence notes..."
                    style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",marginBottom:6}}/>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                      📎 {uploadingGB===r.id?"Uploading...":"Attach Evidence"}
                      <input type="file" style={{display:"none"}} onChange={e=>uploadGBEvidence(r.id,e.target.files[0],r.item)} disabled={uploadingGB===r.id}/>
                    </label>
                    {r.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(r.file_url,"_blank")}>View Evidence</Btn>}
                    {r.file_url&&<Badge label="Evidence ✓" color={C.teal} bg="#CCFBF1"/>}
                  </div>
                </div>
                <select value={r.status} onChange={e=>updateGBReq(r.id,"status",e.target.value)}
                  style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",minWidth:130}}>
                  <option value="met">✓ Met</option>
                  <option value="needs_review">⚠ Needs Review</option>
                  <option value="not_met">✗ Not Met</option>
                </select>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ─── INSURANCE & LEGAL ────────────────────────────────────────────
const InsuranceScreen = ({insurance,setInsurance,agency,saveToDocumentLibrary}) => {
  const [showModal,setShowModal]=useState(false);
  const [ni,setNi]=useState({type:"",carrier:"",policy_number:"",limit:"",expiry:"",status:"active"});
  const [insFile,setInsFile]=useState(null);

  const add = async () => {
    if(!ni.type||!ni.carrier) return;
    if(!insFile){ alert("Please attach the insurance policy document."); return; }
    const docName = `${ni.type} — ${ni.carrier}`;
    const file_url = await saveToDocumentLibrary(insFile,"Insurance",docName,agency?.id,ni.expiry||null,null);
    const tempId = uid();
    const insData = {type:ni.type,carrier:ni.carrier,policy_number:ni.policy_number||null,limit_amount:ni.limit||null,expiry:ni.expiry||null,status:"active",file_url,agency_id:agency?.id};
    setInsurance(prev=>[...prev,{id:tempId,...insData}]);
    setNi({type:"",carrier:"",policy_number:"",limit:"",expiry:"",status:"active"});setInsFile(null);setShowModal(false);
    const {data,error} = await supabase.from('insurance_policies').insert(insData).select().single();
    if(error) console.error('Insurance insert:',error);
    if(data) setInsurance(prev=>prev.map(i=>i.id===tempId?data:i));
  };

  const INSURANCE_TYPES = ["General Liability","Professional Liability","Workers Compensation","Cyber Liability","Commercial Auto","Umbrella Policy","Directors & Officers"];

  return (
    <div className="fade-in">
      <PageHeader title="Insurance & Legal" subtitle="Track all required insurance policies and legal compliance"
        action={<Btn onClick={()=>setShowModal(true)}>+ Add Policy</Btn>}/>

      <TipBox>
        <strong>Required Coverage:</strong> General Liability (min $1M), Professional Liability, Workers Compensation (required by law in most states), Cyber Liability (HIPAA breach risk), Commercial Auto (if staff transport clients). Surveyors and state licensing boards may request proof of insurance.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Active Policies" value={insurance.filter(i=>i.status==="active").length} color={C.teal}/>
        <StatCard label="Expiring (30d)" value={insurance.filter(i=>daysUntil(i.expiry)<30&&daysUntil(i.expiry)>=0).length} color={C.amber}/>
        <StatCard label="Expired" value={insurance.filter(i=>daysUntil(i.expiry)<0).length} color={C.red}/>
        <StatCard label="Total Policies" value={insurance.length} color={C.navy}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:16}}>
        {insurance.map(pol=>{
          const days=daysUntil(pol.expiry);
          const color=days<0?C.red:days<30?C.red:days<60?C.amber:C.teal;
          const bg=days<0?"#FEE2E2":days<30?"#FEE2E2":days<60?"#FEF3C7":"#CCFBF1";
          return (
            <Card key={pol.id} style={{borderTop:`4px solid ${color}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy}}>{pol.type}</div>
                <Badge label={days<0?"EXPIRED":days<30?`${days}d left`:days<60?`${days}d left`:"Current"} color={color} bg={bg}/>
              </div>
              <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Carrier:</strong> {pol.carrier}</div>
              <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Policy #:</strong> {pol.policy_number}</div>
              <div style={{fontSize:13,color:C.slate,marginBottom:4}}><strong>Limit:</strong> {pol.limit}</div>
              <div style={{fontSize:13,color:C.slate,marginBottom:12}}><strong>Expires:</strong> {fmt(pol.expiry)}</div>
              <div style={{display:"flex",gap:8}}>
                <Btn variant="ghost" size="sm" onClick={()=>pol.file_url?window.open(pol.file_url,'_blank'):alert("No document attached")}>View Doc</Btn>
                <Btn variant="danger" size="sm" onClick={async()=>{setInsurance(prev=>prev.filter(i=>i.id!==pol.id));await supabase.from('insurance_policies').delete().eq('id',pol.id);}}>Remove</Btn>
              </div>
            </Card>
          );
        })}
      </div>

      {showModal&&(
        <Modal title="Add Insurance Policy" onClose={()=>setShowModal(false)}>
          <Dropdown label="Policy Type" value={ni.type} onChange={v=>setNi({...ni,type:v})} options={[{value:"",label:"Select type..."},...INSURANCE_TYPES.map(t=>({value:t,label:t}))]}/>
          <Field label="Insurance Carrier" value={ni.carrier} onChange={v=>setNi({...ni,carrier:v})} required/>
          <Field label="Policy Number" value={ni.policy_number} required onChange={v=>setNi({...ni,policy_number:v})}/>
          <Field label="Coverage Limit" value={ni.limit} required onChange={v=>setNi({...ni,limit:v})} placeholder="$1M / $3M"/>
          <Field label="Expiry Date" type="date" value={ni.expiry} onChange={v=>setNi({...ni,expiry:v})} required/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Policy Document <span style={{color:C.red}}>*</span></label>
            <input type="file" onChange={e=>setInsFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {insFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {insFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Add Policy</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── OIG & BILLING COMPLIANCE ─────────────────────────────────────
const OIGScreen = ({oigChecks,setOigChecks,staff,agency,saveToDocumentLibrary,checklistItems,setChecklistItems}) => {
  const [showModal,setShowModal]=useState(false);
  const [no,setNo]=useState({staff_id:"",check_date:today(),result:"Clear",checked_by:"Admin",next_due:daysFromNow(30)});

  const add = async () => {
    if(!no.staff_id) return;
    const existing=oigChecks.find(o=>o.staff_id===no.staff_id);
    const oData = {staff_id:no.staff_id,check_date:no.check_date,result:no.result,checked_by:no.checked_by||null,next_due:no.next_due||null,agency_id:agency?.id};
    if(existing){
      setOigChecks(prev=>prev.map(o=>o.staff_id===no.staff_id?{...o,...oData}:o));
      const {error} = await supabase.from('oig_checks').update(oData).eq('staff_id',no.staff_id);
      if(error) console.error('OIG update:',error);
    } else {
      const tempId = uid();
      setOigChecks(prev=>[...prev,{id:tempId,...oData}]);
      const {data,error} = await supabase.from('oig_checks').insert(oData).select().single();
      if(error) console.error('OIG insert:',error);
      if(data) setOigChecks(prev=>prev.map(o=>o.id===tempId?data:o));
    }
    setNo({staff_id:"",check_date:today(),result:"Clear",checked_by:"Admin",next_due:daysFromNow(30)});setShowModal(false);
  };

  const overdue=oigChecks.filter(o=>!o.check_date||(o.next_due&&daysUntil(o.next_due)<0));
  const neverChecked=staff.filter(s=>!oigChecks.find(o=>o.staff_id===s.id&&o.check_date));

  const OIG_DEFAULTS = [
    {id:"br1",item:"Monthly OIG exclusion list check — all staff & contractors",status:overdue.length>0?"not_met":"met",ref:"OIG mandate",notes:"",file_url:null},
    {id:"br2",item:"State Medicaid exclusion list check monthly",status:"needs_review",ref:"State requirement",notes:"",file_url:null},
    {id:"br3",item:"Anti-Kickback Statute policy on file",status:"met",ref:"42 U.S.C. §1320a-7b",notes:"",file_url:null},
    {id:"br4",item:"Internal billing audit conducted (quarterly)",status:"needs_review",ref:"OIG guidance",notes:"",file_url:null},
    {id:"br5",item:"Service authorization tracking per patient",status:"met",ref:"Payer requirement",notes:"",file_url:null},
    {id:"br6",item:"HCPCS/CPT code accuracy review",status:"met",ref:"CMS billing rules",notes:"",file_url:null},
    {id:"br7",item:"EVV data supports all billed visits",status:"met",ref:"21st Century Cures Act",notes:"",file_url:null},
    {id:"br8",item:"No phantom visits in billing records",status:"met",ref:"OIG audit target",notes:"",file_url:null},
    {id:"br9",item:"Claims denial tracking and appeals process",status:"needs_review",ref:"OIG guidance",notes:"",file_url:null},
    {id:"br10",item:"Overpayment self-disclosure procedure documented",status:"met",ref:"Affordable Care Act §6402",notes:"",file_url:null},
  ];
  const [billingRisks,setBillingRisks]=useState(()=>OIG_DEFAULTS.map(d=>{
    const saved=checklistItems.find(c=>c.checklist==="oig"&&c.item_id===d.id);
    return saved?{...d,status:saved.status,notes:saved.notes||"",file_url:saved.file_url||null}:d;
  }));
  const [uploadingBR,setUploadingBR]=useState(null);

  const updateBR = async (id,field,value) => {
    setBillingRisks(prev=>prev.map(r=>r.id===id?{...r,[field]:value}:r));
    const existing = checklistItems.find(c=>c.checklist==="oig"&&c.item_id===id);
    const current = billingRisks.find(r=>r.id===id)||{};
    const payload = {agency_id:agency?.id,checklist:"oig",item_id:id,status:field==="status"?value:current.status,notes:field==="notes"?value:current.notes,file_url:field==="file_url"?value:current.file_url};
    if(existing){
      await supabase.from("checklist_items").update(payload).eq("id",existing.id);
      setChecklistItems(prev=>prev.map(c=>c.id===existing.id?{...c,...payload}:c));
    } else {
      const {data} = await supabase.from("checklist_items").insert(payload).select().single();
      if(data) setChecklistItems(prev=>[...prev,data]);
    }
  };

  const uploadBREvidence = async (id,file,item) => {
    if(!file) return;
    setUploadingBR(id);
    const file_url = await saveToDocumentLibrary(file,"Audit Evidence",`Billing Compliance — ${item}`,agency?.id||"agency-1",null,null);
    if(file_url) updateBR(id,"file_url",file_url);
    setUploadingBR(null);
  };

  return (
    <div className="fade-in">
      <PageHeader title="OIG & Billing Compliance" subtitle="Monthly exclusion checks · Anti-fraud · Billing integrity"
        action={<Btn onClick={()=>setShowModal(true)}>+ Log OIG Check</Btn>}/>

      <TipBox>
        <strong>OIG Requirement:</strong> Every employee and contractor must be checked monthly against the OIG List of Excluded Individuals/Entities (LEIE) and state Medicaid exclusion lists. Hiring an excluded individual results in exclusion from Medicare/Medicaid programs and civil monetary penalties up to $10,000 per item billed. Check at <strong>exclusions.oig.hhs.gov</strong>
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="OIG Overdue" value={overdue.length} color={overdue.length>0?C.red:C.teal}/>
        <StatCard label="Never Checked" value={neverChecked.length} color={neverChecked.length>0?C.red:C.teal}/>
        <StatCard label="Clear This Month" value={oigChecks.filter(o=>o.check_date===today()&&o.result==="Clear").length} color={C.teal}/>
        <StatCard label="Total Staff" value={staff.length} color={C.navy}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:24}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>👤 OIG Check Status — All Staff</h3>
          {staff.filter(s=>s.status!=="archived").map(s=>{
            const check=oigChecks.find(o=>o.staff_id===s.id);
            const overdue=check?daysUntil(check.next_due)<0:true;
            const neverChecked=!check?.check_date;
            return (
              <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${C.light}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <Avatar name={s.name} size={30}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{s.name} — {s.role}</div>
                    <div style={{fontSize:11,color:C.gray}}>{check?.check_date?`Last checked ${fmt(check.check_date)}`:"Never checked"}</div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {check?.result&&<Badge label={check.result} color={check.result==="Clear"?C.teal:C.red} bg={check.result==="Clear"?"#CCFBF1":"#FEE2E2"}/>}
                  <Badge label={neverChecked?"Not Checked":overdue?"OVERDUE":`${daysUntil(check.next_due)}d`} color={neverChecked||overdue?C.red:C.teal} bg={neverChecked||overdue?"#FEE2E2":"#CCFBF1"}/>
                </div>
              </div>
            );
          })}
        </Card>

        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:14}}>📋 Billing Compliance Checklist</h3>
          {billingRisks.map((r)=>(
            <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{flex:1,marginRight:12}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{r.item}</div>
                  <div style={{fontSize:11,color:C.gray,marginBottom:6}}>{r.ref}</div>
                  <input value={r.notes||""} onChange={e=>updateBR(r.id,"notes",e.target.value)}
                    placeholder="Add evidence notes..."
                    style={{width:"100%",padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none"}}/>
                </div>
                <select value={r.status} onChange={e=>updateBR(r.id,"status",e.target.value)}
                  style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white,outline:"none",minWidth:130}}>
                  <option value="met">✓ Met</option>
                  <option value="needs_review">⚠ Needs Review</option>
                  <option value="not_met">✗ Not Met</option>
                </select>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 10px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,color:C.navy}}>
                  📎 {uploadingBR===r.id?"Uploading...":"Attach Evidence"}
                  <input type="file" style={{display:"none"}} onChange={e=>uploadBREvidence(r.id,e.target.files[0],r.item)} disabled={uploadingBR===r.id}/>
                </label>
                {r.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(r.file_url,'_blank')}>View Evidence</Btn>}
                {r.file_url&&<Badge label="Evidence ✓" color={C.teal} bg="#CCFBF1"/>}
              </div>
            </div>
          ))}
        </Card>
      </div>

      {showModal&&(
        <Modal title="Log OIG Exclusion Check" onClose={()=>setShowModal(false)}>
          <TipBox>Check exclusions.oig.hhs.gov and your state Medicaid exclusion list. Document result and date. Monthly checks required for all staff and contractors.</TipBox>
          <Dropdown label="Staff Member" value={no.staff_id} onChange={v=>setNo({...no,staff_id:v})} options={[{value:"",label:"Select staff..."},...staff.map(s=>({value:s.id,label:`${s.name} (${s.role})`}))]}/>
          <Field label="Check Date" type="date" value={no.check_date} onChange={v=>setNo({...no,check_date:v})} required/>
          <Dropdown label="Result" value={no.result} onChange={v=>setNo({...no,result:v})} options={[{value:"Clear",label:"✓ Clear — Not on exclusion list"},{value:"Listed",label:"⚠ Listed — Do NOT employ"}]}/>
          <Field label="Checked By" value={no.checked_by} required onChange={v=>setNo({...no,checked_by:v})} placeholder="Administrator"/>
          <Field label="Next Check Due" type="date" value={no.next_due} required onChange={v=>setNo({...no,next_due:v})}/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Log Check</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── COMPLAINTS & INCIDENTS ───────────────────────────────────────
const ComplaintsScreen = ({complaints,setComplaints,incidents,setIncidents,agency}) => {
  const [tab,setTab]=useState("complaints");
  const [showComplaintModal,setShowComplaintModal]=useState(false);
  const [showIncidentModal,setShowIncidentModal]=useState(false);
  const [complaintFile,setComplaintFile]=useState(null);
  const [incidentFile,setIncidentFile]=useState(null);
  const [nc,setNc]=useState({date:today(),patient:"",nature:"",received_by:"",status:"open",resolution:"",resolution_date:null});
  const [ni,setNi]=useState({date:today(),type:"",patient:"",staff_involved:"",description:"",reported_to_state:false,report_date:null,outcome:""});

  const addComplaint = async () => {
    if(!nc.nature) return;
    const tempId = uid();
    let file_url = null;
    if(complaintFile) file_url = await saveToDocumentLibrary(complaintFile,"Audit Evidence",`Complaint — ${nc.nature.slice(0,40)}`,agency?.id,null,null);
    const cData = {date:nc.date,patient:nc.patient||null,nature:nc.nature,received_by:nc.received_by||null,status:"open",resolution:null,resolution_date:null,file_url,agency_id:agency?.id};
    setComplaints(prev=>[...prev,{id:tempId,...cData}]);
    setNc({date:today(),patient:"",nature:"",received_by:"",status:"open",resolution:"",resolution_date:null});setComplaintFile(null);setShowComplaintModal(false);
    const {data,error} = await supabase.from('complaints').insert(cData).select().single();
    if(error) console.error('Complaint:',error);
    if(data) setComplaints(prev=>prev.map(c=>c.id===tempId?data:c));
  };
  const addIncident = async () => {
    if(!ni.type) return;
    const tempId = uid();
    let file_url = null;
    if(incidentFile) file_url = await saveToDocumentLibrary(incidentFile,"Audit Evidence",`Incident — ${ni.type} ${ni.date}`,agency?.id,null,null);
    const iData = {date:ni.date,type:ni.type,patient:ni.patient||null,staff_id:ni.staff_involved||null,description:ni.description||null,reported_to_state:ni.reported_to_state,report_date:ni.report_date||null,outcome:ni.outcome||null,file_url,agency_id:agency?.id};
    setIncidents(prev=>[...prev,{id:tempId,...iData}]);
    setNi({date:today(),type:"",patient:"",staff_involved:"",description:"",reported_to_state:false,report_date:null,outcome:""});setIncidentFile(null);setShowIncidentModal(false);
    const {data,error} = await supabase.from('incidents').insert(iData).select().single();
    if(error) console.error('Incident:',error);
    if(data) setIncidents(prev=>prev.map(i=>i.id===tempId?data:i));
  };
  const resolveComplaint = async id => {
    setComplaints(prev=>prev.map(c=>c.id===id?{...c,status:"resolved",resolution_date:today()}:c));
    await supabase.from("complaints").update({status:"resolved",resolution_date:today()}).eq("id",id);
  };

  const INCIDENT_TYPES = ["Patient Fall","Medication Error","Medication Omission","Patient Injury","Abuse Allegation","Neglect Allegation","Theft","Vehicle Accident","Staff Injury","Unexpected Death","Hospitalization","Emergency 911 Call","HIPAA Breach","Other"];

  return (
    <div className="fade-in">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:28}}>
        <div>
          <h1 style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:800,color:C.navy}}>Complaints & Incidents</h1>
          <div style={{color:C.gray,fontSize:14}}>Patient grievances · Incident reports · State reporting</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <Btn variant="ghost" onClick={()=>setShowIncidentModal(true)}>+ Incident</Btn>
          <Btn onClick={()=>setShowComplaintModal(true)}>+ Complaint</Btn>
        </div>
      </div>

      <TipBox>
        <strong>Required:</strong> All agencies must maintain a patient complaint/grievance log. Surveyors always request this during surveys. Complaints must be investigated and resolved within agency-defined timeframes. Certain incidents (abuse, neglect, serious injury, unexpected death) must be reported to the state agency.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Open Complaints" value={complaints.filter(c=>c.status==="open").length} color={complaints.filter(c=>c.status==="open").length>0?C.amber:C.teal}/>
        <StatCard label="Resolved" value={complaints.filter(c=>c.status==="resolved").length} color={C.teal}/>
        <StatCard label="Total Incidents" value={incidents.length} color={C.navy}/>
        <StatCard label="Unreported to State" value={incidents.filter(i=>!i.reported_to_state).length} color={incidents.filter(i=>!i.reported_to_state).length>0?C.amber:C.teal}/>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {["complaints","incidents"].map(t=>(
          <Btn key={t} variant={tab===t?"navy":"light"} onClick={()=>setTab(t)}>
            {t==="complaints"?`Complaint Log (${complaints.length})`:`Incident Log (${incidents.length})`}
          </Btn>
        ))}
      </div>

      {tab==="complaints"&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {complaints.length===0?<Card style={{textAlign:"center",padding:40}}><div style={{color:C.gray}}>No complaints on record.</div></Card>:
            complaints.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(c=>(
              <Card key={c.id} style={{borderLeft:`4px solid ${c.status==="open"?C.amber:C.teal}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:700,color:C.slate,marginBottom:4}}>{c.nature}</div>
                    <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Patient: {c.patient} · Received by: {c.received_by} · {fmt(c.date)}</div>
                    {c.resolution&&<div style={{fontSize:13,color:C.slate}}>Resolution: {c.resolution}</div>}
                    {c.resolution_date&&<div style={{fontSize:12,color:C.gray}}>Resolved: {fmt(c.resolution_date)}</div>}
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <StatusBadge status={c.status}/>
                    {c.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(c.file_url,'_blank')}>View Doc</Btn>}
                    {c.status==="open"&&<Btn variant="teal" size="sm" onClick={()=>resolveComplaint(c.id)}>Mark Resolved</Btn>}
                    <Btn variant="danger" size="sm" onClick={async()=>{if(!window.confirm("Delete this complaint?"))return;setComplaints(prev=>prev.filter(x=>x.id!==c.id));await supabase.from("complaints").delete().eq("id",c.id);}}>Delete</Btn>
                  </div>
                </div>
              </Card>
            ))
          }
        </div>
      )}

      {tab==="incidents"&&(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {incidents.length===0?<Card style={{textAlign:"center",padding:40}}><div style={{color:C.gray}}>No incidents on record.</div></Card>:
            incidents.sort((a,b)=>new Date(b.date)-new Date(a.date)).map(inc=>(
              <Card key={inc.id} style={{borderLeft:`4px solid ${inc.reported_to_state?C.teal:C.amber}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:14,fontWeight:700,color:C.slate}}>{inc.type}</span>
                      <Badge label={inc.type} color={C.navy} bg="#E8EEF5"/>
                    </div>
                    <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Patient: {inc.patient} · {fmt(inc.date)}</div>
                    <div style={{fontSize:13,color:C.slate,marginBottom:4}}>{inc.description}</div>
                    {inc.outcome&&<div style={{fontSize:12,color:C.slate,fontStyle:"italic"}}>Outcome: {inc.outcome}</div>}
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                    <Badge label={inc.reported_to_state?"State Notified":"Not Reported"} color={inc.reported_to_state?C.teal:C.amber} bg={inc.reported_to_state?"#CCFBF1":"#FEF3C7"}/>
                    {inc.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(inc.file_url,'_blank')}>View Doc</Btn>}
                    <Btn variant="danger" size="sm" onClick={async()=>{if(!window.confirm("Delete this incident?"))return;setIncidents(prev=>prev.filter(x=>x.id!==inc.id));await supabase.from("incidents").delete().eq("id",inc.id);}}>Delete</Btn>
                  </div>
                </div>
              </Card>
            ))
          }
        </div>
      )}

      {showComplaintModal&&(
        <Modal title="Log Patient Complaint / Grievance" onClose={()=>setShowComplaintModal(false)}>
          <Field label="Date Received" type="date" value={nc.date} required onChange={v=>setNc({...nc,date:v})}/>
          <Field label="Patient Name" value={nc.patient} onChange={v=>setNc({...nc,patient:v})} required/>
          <Textarea label="Nature of Complaint" value={nc.nature} onChange={v=>setNc({...nc,nature:v})} placeholder="Describe the complaint in detail..." required/>
          <Field label="Received By" value={nc.received_by} onChange={v=>setNc({...nc,received_by:v})} placeholder="Administrator, Clinical Manager..."/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Evidence (optional)</label>
            <input type="file" onChange={e=>setComplaintFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {complaintFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {complaintFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowComplaintModal(false)}>Cancel</Btn>
            <Btn onClick={addComplaint}>Log Complaint</Btn>
          </div>
        </Modal>
      )}

      {showIncidentModal&&(
        <Modal title="Log Incident Report" onClose={()=>setShowIncidentModal(false)}>
          <Field label="Date" type="date" value={ni.date} onChange={v=>setNi({...ni,date:v})}/>
          <Dropdown label="Incident Type" value={ni.type} onChange={v=>setNi({...ni,type:v})} options={[{value:"",label:"Select type..."},...INCIDENT_TYPES.map(t=>({value:t,label:t}))]}/>
          <Field label="Patient Name" value={ni.patient} required onChange={v=>setNi({...ni,patient:v})}/>
          <Textarea label="Description" value={ni.description} onChange={v=>setNi({...ni,description:v})} required/>
          <Textarea label="Outcome / Actions Taken" value={ni.outcome} onChange={v=>setNi({...ni,outcome:v})}/>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,cursor:"pointer"}} onClick={()=>setNi({...ni,reported_to_state:!ni.reported_to_state})}>
            <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${ni.reported_to_state?C.teal:C.light}`,background:ni.reported_to_state?C.teal:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {ni.reported_to_state&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
            </div>
            <span style={{fontSize:13,color:C.slate}}>Reported to state agency</span>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach Evidence (optional)</label>
            <input type="file" onChange={e=>setIncidentFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {incidentFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {incidentFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowIncidentModal(false)}>Cancel</Btn>
            <Btn onClick={addIncident}>Log Incident</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── PLAN OF CORRECTION ───────────────────────────────────────────
const POCScreen = ({poc,setPoc,cops,agency}) => {
  const [showModal,setShowModal]=useState(false);
  const [np,setNp]=useState({survey_date:"",deficiency:"",tag:"",cop:"",submitted_date:"",completion_date:"",status:"open",corrective_action:""});
  const [pocFile,setPocFile]=useState(null);

  const add = async () => {
    if(!np.deficiency) return;
    const tempId = uid();
    let file_url = null;
    if(pocFile) file_url = await saveToDocumentLibrary(pocFile,"Audit Evidence",`PoC — ${np.deficiency.slice(0,40)}`,agency?.id,null,null);
    const pData = {survey_date:np.survey_date||null,deficiency:np.deficiency,tag:np.tag||null,cop:np.cop||null,submitted_date:np.submitted_date||null,completion_date:np.completion_date||null,status:np.status,corrective_action:np.corrective_action||null,file_url,agency_id:agency?.id};
    setPoc(prev=>[...prev,{id:tempId,...pData}]);
    setNp({survey_date:"",deficiency:"",tag:"",cop:"",submitted_date:"",completion_date:"",status:"open",corrective_action:""});setPocFile(null);setShowModal(false);
    const {data,error} = await supabase.from('plan_of_correction').insert(pData).select().single();
    if(error) console.error('POC insert:',error);
    if(data) setPoc(prev=>prev.map(p=>p.id===tempId?data:p));
  };

  return (
    <div className="fade-in">
      <PageHeader title="Plan of Correction" subtitle="Survey deficiency history · CMS Form 2567 tracking"
        action={<Btn onClick={()=>setShowModal(true)}>+ Add Deficiency</Btn>}/>

      <TipBox>
        <strong>CMS Process:</strong> When a survey finds deficiencies, the agency receives Form CMS-2567 (Statement of Deficiencies). The agency must submit a Plan of Correction (PoC) within 10 days. Surveyors review previous PoC history during every subsequent survey. Unresolved deficiencies can trigger immediate jeopardy findings.
      </TipBox>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Deficiencies" value={poc.length} color={C.navy}/>
        <StatCard label="Resolved" value={poc.filter(p=>p.status==="resolved").length} color={C.teal}/>
        <StatCard label="Open" value={poc.filter(p=>p.status==="open").length} color={poc.filter(p=>p.status==="open").length>0?C.amber:C.teal}/>
        <StatCard label="Last Survey" value={poc.length>0?fmt(poc.sort((a,b)=>new Date(b.survey_date)-new Date(a.survey_date))[0]?.survey_date):"No history"} color={C.blue}/>
      </div>

      {poc.length===0?(
        <Card style={{textAlign:"center",padding:60}}>
          <div style={{fontSize:40,marginBottom:12}}>✓</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:C.navy,marginBottom:8}}>No deficiency history</div>
          <div style={{color:C.gray,fontSize:14}}>Add survey deficiencies here to track Plans of Correction and demonstrate improvement to surveyors.</div>
        </Card>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {poc.sort((a,b)=>new Date(b.survey_date)-new Date(a.survey_date)).map(p=>(
            <Card key={p.id} style={{borderLeft:`4px solid ${p.status==="resolved"?C.teal:C.amber}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                    {p.tag&&<Badge label={p.tag} color={C.red} bg="#FEE2E2"/>}
                    {p.cop&&<Badge label={`§${p.cop}`} color={C.blue} bg="#EEF5FF"/>}
                  </div>
                  <div style={{fontSize:14,fontWeight:700,color:C.slate,marginBottom:4}}>{p.deficiency}</div>
                  <div style={{fontSize:12,color:C.gray}}>Survey: {fmt(p.survey_date)} · PoC Submitted: {fmt(p.submitted_date)} · Completed: {fmt(p.completion_date)}</div>
                </div>
                <StatusBadge status={p.status}/>
              </div>
              {p.corrective_action&&(
                <div style={{background:C.mint,borderRadius:8,padding:"10px 14px",fontSize:13,color:C.slate}}>
                  <strong>Corrective Action:</strong> {p.corrective_action}
                </div>
              )}
              <div style={{marginTop:10,display:"flex",gap:8}}>
                {p.file_url&&<Btn variant="ghost" size="sm" onClick={()=>window.open(p.file_url,'_blank')}>View Doc</Btn>}
                {p.status!=="resolved"&&<Btn variant="teal" size="sm" onClick={async()=>{
      setPoc(prev=>prev.map(x=>x.id===p.id?{...x,status:"resolved",completion_date:today()}:x));
      await supabase.from("plan_of_correction").update({status:"resolved",completion_date:today()}).eq("id",p.id);
    }}>Mark Resolved</Btn>}
                <Btn variant="danger" size="sm" onClick={async()=>{setPoc(prev=>prev.filter(x=>x.id!==p.id));await supabase.from('plan_of_correction').delete().eq('id',p.id);}}>Remove</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showModal&&(
        <Modal title="Add Survey Deficiency / PoC" onClose={()=>setShowModal(false)} width={600}>
          <Field label="Survey Date" type="date" value={np.survey_date} onChange={v=>setNp({...np,survey_date:v})}/>
          <Field label="G-Tag Number (e.g. G0157)" value={np.tag} onChange={v=>setNp({...np,tag:v})} placeholder="G0157"/>
          <Field label="CoP Reference (e.g. 484.75)" value={np.cop} onChange={v=>setNp({...np,cop:v})} placeholder="484.75"/>
          <Textarea label="Deficiency Description" value={np.deficiency} onChange={v=>setNp({...np,deficiency:v})} placeholder="Describe the deficiency as cited on CMS-2567..." required required/>
          <Field label="PoC Submission Date" type="date" value={np.submitted_date} onChange={v=>setNp({...np,submitted_date:v})}/>
          <Field label="Completion Date" type="date" value={np.completion_date} onChange={v=>setNp({...np,completion_date:v})}/>
          <Textarea label="Corrective Action Taken" value={np.corrective_action} onChange={v=>setNp({...np,corrective_action:v})} placeholder="Describe specific corrective actions, staff training, policy changes..."/>
          <Dropdown label="Status" value={np.status} onChange={v=>setNp({...np,status:v})} options={[{value:"open",label:"Open"},{value:"in_progress",label:"In Progress"},{value:"resolved",label:"Resolved"}]}/>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach CMS-2567 or PoC Document (optional)</label>
            <input type="file" onChange={e=>setPocFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {pocFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {pocFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Add Deficiency</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── EVV COMPLIANCE ───────────────────────────────────────────────
const EVVScreen = ({evv,setEvv,staff,agency}) => {
  const [showModal,setShowModal]=useState(false);
  const [activeVisit,setActiveVisit]=useState(null);
  const [locError,setLocError]=useState("");
  const [checking,setChecking]=useState(false);
  const [nv,setNv]=useState({staff_id:"",client:"",visit_date:today(),check_in:"",check_out:"",gps:false,status:"pending",service_type:"HHA Services"});

  const getLocation = () => new Promise((resolve,reject)=>{
    if(!navigator.geolocation){reject(new Error("Geolocation not supported."));return;}
    navigator.geolocation.getCurrentPosition(
      pos=>resolve({lat:pos.coords.latitude,lng:pos.coords.longitude,accuracy:pos.coords.accuracy}),
      err=>reject(new Error(err.code===1?"Location denied. Enable location in device settings.":"Unable to get location.")),
      {enableHighAccuracy:true,timeout:10000,maximumAge:0}
    );
  });

  const startVisit = async () => {
    if(!nv.staff_id||!nv.client){alert("Select a clinician and enter client name.");return;}
    setChecking(true);setLocError("");
    try {
      const loc = await getLocation();
      const now = new Date();
      const timeStr = now.toTimeString().slice(0,5);
      const visit = {id:uid(),...nv,visit_date:today(),check_in:timeStr,check_out:"",
        gps:true,lat_in:loc.lat,lng_in:loc.lng,accuracy_in:Math.round(loc.accuracy),
        checkin_timestamp:now.toISOString(),status:"active",agency_id:agency?.id||"agency-1"};
      setActiveVisit(visit);
      setEvv(prev=>[...prev,visit]);
      const {data} = await supabase.from('evv_records').insert({
        staff_id:visit.staff_id,client:visit.client,visit_date:visit.visit_date,
        check_in:visit.check_in,gps:true,status:"active",
        service_type:visit.service_type,agency_id:visit.agency_id
      }).select().single();
      if(data) {
        setActiveVisit(v=>({...v,db_id:data.id}));
        setEvv(prev=>prev.map(v=>v.id===visit.id?{...data}:v));
      }
      setShowModal(false);
    } catch(e){setLocError(e.message);}
    setChecking(false);
  };

  const endVisit = async () => {
    if(!activeVisit) return;
    setChecking(true);setLocError("");
    try {
      const loc = await getLocation();
      const now = new Date();
      const timeStr = now.toTimeString().slice(0,5);
      const updated = {...activeVisit,check_out:timeStr,lat_out:loc.lat,lng_out:loc.lng,
        checkout_timestamp:now.toISOString(),status:"verified"};
      setEvv(prev=>prev.map(v=>v.id===activeVisit.id?updated:v));
      if(activeVisit.db_id){
        await supabase.from('evv_records').update({check_out:timeStr,status:"verified"}).eq('id',activeVisit.db_id);
      }
      setActiveVisit(null);
    } catch(e){setLocError(e.message);}
    setChecking(false);
  };

  const add = async () => {
    if(!nv.client||!nv.staff_id) return;
    const tempId = uid();
    const evvData = {staff_id:nv.staff_id,client:nv.client,visit_date:nv.visit_date||null,check_in:nv.check_in||null,check_out:nv.check_out||null,gps:nv.gps,status:nv.gps?"verified":"pending",service_type:nv.service_type,agency_id:agency?.id};
    setEvv(prev=>[...prev,{id:tempId,...evvData}]);
    setNv({staff_id:"",client:"",visit_date:today(),check_in:"",check_out:"",gps:false,status:"pending",service_type:"HHA Services"});setShowModal(false);
    const {data,error} = await supabase.from('evv_records').insert(evvData).select().single();
    if(error) console.error('EVV insert:',error);
    if(data) setEvv(prev=>prev.map(v=>v.id===tempId?data:v));
  };
  const updateStatus = async (id,status) => {
    setEvv(prev=>prev.map(v=>v.id===id?{...v,status}:v));
    await supabase.from('evv_records').update({status}).eq('id',id);
  };
  const [uploadingEVV,setUploadingEVV]=useState(null);
  const uploadEVVDoc = async (id,file,client) => {
    if(!file) return;
    setUploadingEVV(id);
    const file_url = await saveToDocumentLibrary(file,"Audit Evidence",`EVV — ${client}`,agency?.id,null,null);
    if(file_url){
      setEvv(prev=>prev.map(v=>v.id===id?{...v,file_url}:v));
      await supabase.from('evv_records').update({file_url}).eq('id',id);
    }
    setUploadingEVV(null);
  };
  const verified=evv.filter(e=>e.status==="verified").length;
  const compliance=evv.length>0?Math.round((verified/evv.length)*100):100;

  return (
    <div className="fade-in">
      <PageHeader title="EVV Compliance" subtitle="21st Century Cures Act — Electronic Visit Verification required for all Medicaid home health visits"
        action={<Btn onClick={()=>setShowModal(true)}>+ Log Past Visit</Btn>}/>

      <TipBox>
        <strong>Federal Mandate:</strong> EVV is required for all Medicaid-funded home health visits. Required data: type of service, date, location, time in/out, individual receiving services, caregiver identity. Non-compliance leads to FMAP reductions and OIG fraud investigations.
      </TipBox>

      {/* Mobile GPS Check-in Panel */}
      <Card style={{marginBottom:20,border:`2px solid ${activeVisit?C.teal:C.blue}`,background:activeVisit?"#F0FDF4":"#EFF6FF"}}>
        <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy,marginBottom:4}}>
          {activeVisit?"🟢 Visit In Progress":"📍 GPS Visit Check-In"}
        </h3>
        <div style={{fontSize:13,color:C.gray,marginBottom:16}}>
          {activeVisit
            ?`${activeVisit.client} · ${staff.find(s=>s.id===activeVisit.staff_id)?.name} · Started ${activeVisit.check_in} · GPS ✓`
            :"Start a visit with automatic GPS timestamp. Location must be enabled on your device."}
        </div>
        {locError&&<div style={{background:"#FEE2E2",color:C.red,padding:"10px 14px",borderRadius:8,fontSize:13,fontWeight:600,marginBottom:12}}>⚠ {locError}</div>}
        {!activeVisit?(
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <Btn onClick={()=>setShowModal(true)} variant="primary">Set Up Visit</Btn>
            <div style={{fontSize:12,color:C.gray}}>
              📱 Works on iPad and mobile · GPS auto-captured · Time-stamped · Location-stamped
            </div>
          </div>
        ):(
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{background:"#CCFBF1",borderRadius:10,padding:"10px 16px"}}>
              <div style={{fontSize:12,color:C.teal,fontWeight:700}}>CHECK-IN</div>
              <div style={{fontSize:18,fontWeight:900,color:C.navy}}>{activeVisit.check_in}</div>
              <div style={{fontSize:11,color:C.gray}}>GPS: {activeVisit.lat_in?.toFixed(4)}, {activeVisit.lng_in?.toFixed(4)}</div>
              <div style={{fontSize:11,color:C.gray}}>Accuracy: ±{activeVisit.accuracy_in}m</div>
            </div>
            <Btn onClick={endVisit} variant="teal" disabled={checking}>
              {checking?"Getting location...":"✓ Check Out — End Visit"}
            </Btn>
          </div>
        )}
      </Card>

      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Visits" value={evv.length} color={C.navy}/>
        <StatCard label="EVV Rate" value={`${compliance}%`} color={compliance>=95?C.teal:compliance>=80?C.amber:C.red}/>
        <StatCard label="Verified" value={verified} color={C.teal}/>
        <StatCard label="Pending" value={evv.filter(e=>e.status==="pending").length} color={C.amber}/>
        <StatCard label="Flagged" value={evv.filter(e=>e.status==="flagged").length} color={evv.filter(e=>e.status==="flagged").length>0?C.red:C.teal}/>
      </div>

      <Card>
        <div style={{display:"grid",gridTemplateColumns:"2fr 2fr 1.5fr 1.5fr 1fr 1.5fr 1.2fr 1.5fr",gap:10,padding:"8px 0",borderBottom:`2px solid ${C.light}`,marginBottom:6}}>
          {["Client","Clinician","Service","Date","GPS","Status","EVV","Action"].map(h=>(
            <div key={h} style={{fontSize:11,fontWeight:800,color:C.gray,textTransform:"uppercase",letterSpacing:0.5}}>{h}</div>
          ))}
        </div>
        {evv.map(v=>{
          const s=staff.find(st=>st.id===v.staff_id);
          return (
            <div key={v.id} style={{display:"grid",gridTemplateColumns:"2fr 2fr 1.5fr 1.5fr 1fr 1.5fr 1.2fr 1.5fr",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.light}`,alignItems:"center"}}>
              <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{v.client}</div>
              <div style={{fontSize:12,color:C.gray}}>{s?.name} <span style={{fontSize:11}}>({s?.role})</span></div>
              <div style={{fontSize:12,color:C.gray}}>{v.service_type}</div>
              <div style={{fontSize:12,color:C.gray}}>{fmt(v.visit_date)}</div>
              <div>{v.gps?<Badge label="✓" color={C.teal} bg="#CCFBF1"/>:<Badge label="✗" color={C.amber} bg="#FEF3C7"/>}</div>
              <StatusBadge status={v.status}/>
              <div style={{fontSize:11,color:C.gray}}>{v.check_in}–{v.check_out}</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                <select value={v.status} onChange={e=>updateStatus(v.id,e.target.value)}
                  style={{padding:"4px 6px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:11,color:C.slate,background:C.white,outline:"none"}}>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="flagged">Flagged</option>
                </select>
                <label style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 8px",background:C.mint,border:`1px solid ${C.light}`,borderRadius:5,cursor:"pointer",fontSize:10,fontWeight:600,color:C.navy}}>
                  {uploadingEVV===v.id?"⏳":"📎"} {v.file_url?"Replace":"Attach"}
                  <input type="file" style={{display:"none"}} onChange={e=>uploadEVVDoc(v.id,e.target.files[0],v.client)} disabled={uploadingEVV===v.id}/>
                </label>
                {v.file_url&&<span onClick={()=>window.open(v.file_url,"_blank")} style={{fontSize:10,color:C.blue,cursor:"pointer",fontWeight:600}}>View Doc</span>}
              </div>
            </div>
          );
        })}
      </Card>

      {showModal&&(
        <Modal title="Set Up Visit — GPS Check-In" onClose={()=>setShowModal(false)}>
          <TipBox>
            📍 <strong>GPS Required:</strong> After filling in visit details, click "Start Visit + Check In" — your device will capture your exact location, date, and time automatically. Make sure location is enabled in your device settings.
          </TipBox>
          <Dropdown label="Clinician" value={nv.staff_id} onChange={v=>setNv({...nv,staff_id:v})} options={[{value:"",label:"Select clinician..."},...staff.map(s=>({value:s.id,label:`${s.name} (${s.role})`}))]}/>
          <Field label="Client Name" value={nv.client} onChange={v=>setNv({...nv,client:v})} required/>
          <Dropdown label="Service Type" value={nv.service_type} onChange={v=>setNv({...nv,service_type:v})} options={["HHA Services","Skilled Nursing","PT","OT","SLP","MSW"].map(s=>({value:s,label:s}))}/>
          {locError&&<div style={{background:"#FEE2E2",color:C.red,padding:"10px 14px",borderRadius:8,fontSize:13,fontWeight:600,marginBottom:12}}>⚠ {locError}</div>}
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={startVisit} variant="teal" disabled={checking}>
              {checking?"Getting GPS location...":"📍 Start Visit + Check In"}
            </Btn>
          </div>
          <div style={{borderTop:`1px solid ${C.light}`,marginTop:20,paddingTop:16}}>
            <div style={{fontSize:12,color:C.gray,marginBottom:12}}>Or log a past visit manually:</div>
            <Field label="Visit Date" type="date" value={nv.visit_date} onChange={v=>setNv({...nv,visit_date:v})}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Field label="Check In" type="time" value={nv.check_in} onChange={v=>setNv({...nv,check_in:v})}/>
              <Field label="Check Out" type="time" value={nv.check_out} onChange={v=>setNv({...nv,check_out:v})}/>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <Btn onClick={add} variant="light">Log Past Visit (No GPS)</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── DOCUMENTS ────────────────────────────────────────────────────
const DocumentsScreen = ({documents,setDocuments,agency}) => {
  const [showModal,setShowModal]=useState(false);
  const [nd,setNd]=useState({name:"",category:"Policy",uploaded_by:"Admin",valid_from:"",expiry_date:""});
  const [uploadFile,setUploadFile]=useState(null);
  const DOC_CATS = ["Policy","License","Certification","Background Check","Training Record","Audit Evidence","Insurance","Governing Body","HIPAA","Emergency Preparedness","QAPI","Other"];
  const DOC_CAT_LABELS = [
    {value:"Policy",label:"Policy — Agency policies and procedures"},
    {value:"License",label:"License — State and federal licenses"},
    {value:"Certification",label:"Certification — Staff credentials and certs"},
    {value:"Background Check",label:"Background Check — Criminal background checks"},
    {value:"Training Record",label:"Training Record — Staff training and CEUs"},
    {value:"Audit Evidence",label:"Audit Evidence — Survey and audit documentation"},
    {value:"Insurance",label:"Insurance — Insurance policies and certificates"},
    {value:"Governing Body",label:"Governing Body — Board and admin documents"},
    {value:"HIPAA",label:"HIPAA — BAAs, privacy notices, risk assessments"},
    {value:"Emergency Preparedness",label:"Emergency Preparedness — EP plans and drill records"},
    {value:"QAPI",label:"QAPI — Quality improvement meeting records"},
    {value:"Other",label:"Other — Miscellaneous compliance documents"},
  ];

  const add = async () => {
    if(!nd.name) return;
    if(!uploadFile) { alert("Please attach a file before saving."); return; }
    const path = `${agency?.id}/${Date.now()}_${uploadFile.name}`;
    const {error:upErr} = await supabase.storage.from('documents').upload(path, uploadFile);
    const file_url = upErr ? null : supabase.storage.from('documents').getPublicUrl(path).data.publicUrl;
    const tempId = uid();
    const docData = {name:nd.name,category:nd.category,uploaded_by:nd.uploaded_by||"Admin",file_url,valid_from:nd.valid_from||null,expiry_date:nd.expiry_date||null,agency_id:agency?.id,created_at:new Date().toISOString()};
    setDocuments(prev=>[...prev,{id:tempId,...docData}]);
    setNd({name:"",category:"Policy",uploaded_by:"Admin",valid_from:"",expiry_date:""});setUploadFile(null);setShowModal(false);
    const {data,error} = await supabase.from('documents').insert(docData).select().single();
    if(error) console.error('Doc insert:',error);
    if(data) setDocuments(prev=>prev.map(d=>d.id===tempId?data:d));
  };

  const grouped=DOC_CATS.reduce((a,cat)=>({...a,[cat]:documents.filter(d=>d.category===cat)}),{});

  return (
    <div className="fade-in">
      <PageHeader title="Document Library" subtitle={`${documents.length} documents · Organize all compliance evidence`}
        action={<Btn onClick={()=>setShowModal(true)}>+ Upload Document</Btn>}/>
    
      {documents.length===0?(
        <Card style={{textAlign:"center",padding:60}}>
          <div style={{fontSize:40,marginBottom:12}}>📁</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:800,color:C.navy,marginBottom:8}}>No documents yet</div>
          <Btn onClick={()=>setShowModal(true)}>Upload First Document</Btn>
        </Card>
      ):(
        DOC_CATS.filter(cat=>documents.filter(d=>d.category===cat).length>0).map(cat=>(
          <div key={cat} style={{marginBottom:24}}>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:800,color:C.navy,marginBottom:10,textTransform:"uppercase",letterSpacing:0.5}}>{cat} ({documents.filter(d=>d.category===cat).length})</h3>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
              {documents.filter(d=>d.category===cat).map(doc=>(
                <Card key={doc.id} style={{padding:16,borderTop:`3px solid ${doc.expiry_date&&daysUntil(doc.expiry_date)<0?C.red:doc.expiry_date&&daysUntil(doc.expiry_date)<30?C.red:doc.expiry_date&&daysUntil(doc.expiry_date)<60?C.amber:C.teal}`}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.slate,marginBottom:4}}>{doc.name}</div>
                  <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Added {fmt(doc.created_at?.split("T")[0])} by {doc.uploaded_by}</div>
                  {doc.valid_from&&<div style={{fontSize:12,color:C.gray,marginBottom:2}}>Valid: {fmt(doc.valid_from)} → {fmt(doc.expiry_date)}</div>}
                  {doc.expiry_date&&(
                    <div style={{marginBottom:10}}>
                      {daysUntil(doc.expiry_date)<0
                        ?<Badge label="EXPIRED" color={C.red} bg="#FEE2E2"/>
                        :daysUntil(doc.expiry_date)<30
                        ?<Badge label={`Expires in ${daysUntil(doc.expiry_date)}d`} color={C.red} bg="#FEE2E2"/>
                        :daysUntil(doc.expiry_date)<60
                        ?<Badge label={`Expires in ${daysUntil(doc.expiry_date)}d`} color={C.amber} bg="#FEF3C7"/>
                        :<Badge label="Current" color={C.teal} bg="#CCFBF1"/>
                      }
                    </div>
                  )}
                  <div style={{display:"flex",gap:8}}>
                    <Btn variant="ghost" size="sm" onClick={()=>doc.file_url?window.open(doc.file_url,'_blank'):alert("No file attached")}>View</Btn>
                    <Btn variant="danger" size="sm" onClick={async()=>{setDocuments(prev=>prev.filter(d=>d.id!==doc.id));await supabase.from('documents').delete().eq('id',doc.id);}}>Delete</Btn>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
      {showModal&&(
        <Modal title="Add Document" onClose={()=>setShowModal(false)}>
          <Field label="Document Name" value={nd.name} onChange={v=>setNd({...nd,name:v})} required/>
          <Dropdown label="Category" value={nd.category} onChange={v=>setNd({...nd,category:v})} options={DOC_CAT_LABELS}/>
          <Field label="Uploaded By" value={nd.uploaded_by} onChange={v=>setNd({...nd,uploaded_by:v})}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="Valid From" type="date" value={nd.valid_from} onChange={v=>setNd({...nd,valid_from:v})}/>
            <Field label="Expiry Date" type="date" value={nd.expiry_date} onChange={v=>setNd({...nd,expiry_date:v})}/>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:13,fontWeight:600,color:C.slate,marginBottom:6}}>Attach File (optional)</label>
            <input type="file" onChange={e=>setUploadFile(e.target.files[0])}
              style={{width:"100%",padding:"8px",borderRadius:8,border:`1.5px solid ${C.light}`,fontSize:13,background:C.white}}/>
            {uploadFile&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>✓ {uploadFile.name}</div>}
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn onClick={add}>Save Document</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── CHECKOUT FORM ────────────────────────────────────────────────
const CheckoutForm = ({agency, user, onSuccess, priceId="price_1TLEsSB0WpRalfPpZm2GapS4"}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");

  const handleSubmit = async () => {
    if(!stripe||!elements) return;
    if(!name) { setError("Please enter the name on your card."); return; }
    setLoading(true);
    setError("");

    try {
      const cardElement = elements.getElement(CardElement);
      const {paymentMethod, error:pmError} = await stripe.createPaymentMethod({
        type:"card",
        card:cardElement,
        billing_details:{name, email:user?.email}
      });

      if(pmError){ setError(pmError.message); setLoading(false); return; }

      const res = await fetch("/api/create-subscription",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          email:user?.email,
          paymentMethodId:paymentMethod.id,
          agencyId:agency?.id,
          priceId
        })
      });
      const data = await res.json();
      if(data.error){ setError(data.error); setLoading(false); return; }

      if(data.clientSecret){
        const {error:confirmError} = await stripe.confirmCardPayment(data.clientSecret);
        if(confirmError){ setError(confirmError.message); setLoading(false); return; }
      }

      onSuccess(data.subscriptionId);
    } catch(e){ setError(e.message); }
    setLoading(false);
  };

  return (
    <div>
      <div style={{marginBottom:16}}>
        <label style={{display:"block",fontSize:13,fontWeight:600,color:"#1F2937",marginBottom:6}}>Name on Card</label>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="John Smith"
          style={{width:"100%",padding:"10px 14px",borderRadius:8,border:"1.5px solid #E5E7EB",fontSize:14,color:"#1F2937",outline:"none",background:"#F8FAFC"}}/>
      </div>
      <div style={{marginBottom:20}}>
        <label style={{display:"block",fontSize:13,fontWeight:600,color:"#1F2937",marginBottom:6}}>Card Details</label>
        <div style={{padding:"12px 14px",borderRadius:8,border:"1.5px solid #E5E7EB",background:"#F8FAFC"}}>
          <CardElement options={{style:{base:{fontSize:"14px",color:"#1F2937",fontFamily:"DM Sans, sans-serif","::placeholder":{color:"#9CA3AF"}}}}}/>
        </div>
      </div>
      {error&&<div style={{color:"#EF4444",fontSize:13,marginBottom:12,padding:"8px 12px",background:"#FEE2E2",borderRadius:8}}>{error}</div>}
      <button onClick={handleSubmit} disabled={!stripe||loading}
        style={{width:"100%",padding:"14px 20px",background:loading?"#9CA3AF":"#2F80ED",color:"#fff",border:"none",borderRadius:10,cursor:loading?"not-allowed":"pointer",fontWeight:700,fontSize:15}}>
        {loading?"Processing payment...":`Subscribe — $${priceId==="price_1TLnLcB0WpRalfPpQMk66f8w"?"99":"249"}/mo`}
      </button>
      <div style={{textAlign:"center",marginTop:12,fontSize:12,color:"#6B7280"}}>
        🔒 Secured by Stripe · 256-bit SSL encryption
      </div>
    </div>
  );
};

// ─── BILLING SCREEN ────────────────────────────────────────────────
const PLANS = {
  starter: {
    id:"starter", name:"Starter", price:99,
    priceId:"price_1TLnLcB0WpRalfPpQMk66f8w",
    desc:"For small agencies getting started",
    limit:"Up to 10 clinicians",
    features:[
      "Staff & credential tracking",
      "Basic HR file management",
      "CMS CoPs checklist",
      "Patient records",
      "Document library",
      "Email support",
    ],
    locked:[
      "GPS EVV compliance",
      "Daily expiry email alerts",
      "HIPAA compliance module",
      "OIG exclusion check log",
      "Emergency preparedness",
      "QAPI program",
      "Complaints & incidents",
      "Plan of correction",
      "Support tickets",
    ]
  },
  professional: {
    id:"professional", name:"Professional", price:249,
    priceId:"price_1TLEsSB0WpRalfPpZm2GapS4",
    desc:"For growing agencies that need full compliance",
    limit:"Unlimited clinicians",
    features:[
      "Everything in Starter",
      "Unlimited clinicians",
      "GPS EVV compliance — built in",
      "Daily credential expiry alerts",
      "HIPAA compliance module",
      "OIG exclusion check log",
      "Emergency preparedness tracking",
      "QAPI quarterly meeting management",
      "Complaints & incidents log",
      "Plan of correction history",
      "Priority support tickets",
    ],
    locked:[]
  }
};

const STARTER_LOCKED = ["supervision","qapi","infection","ep","hipaa","governing","insurance","oig","complaints","poc","evv"];

const BillingScreen = ({agency,user,setAgency}) => {
  const [subscribed, setSubscribed] = useState(agency?.subscribed||false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [currentTier, setCurrentTier] = useState(agency?.plan_tier||"trial");
  const [selectedPlan, setSelectedPlan] = useState(null);

  const features = [
    "All 15 CMS Conditions of Participation tracking",
    "Complete HR file per clinician — credentials & docs",
    "HHA 60-day aide supervision tracking",
    "QAPI quarterly meeting management",
    "Infection control program & incident log",
    "Emergency preparedness drills & checklist",
    "HIPAA compliance module with BAA tracking",
    "OIG exclusion check log — monthly",
    "Patient records, OASIS & Plan of Care tracking",
    "Plan of Correction history",
    "GPS EVV compliance monitoring",
    "Complaint & grievance log",
    "Document library — all evidence in one place",
    "Credential expiry alerts",
    "Unlimited staff & patients",
  ];

  const onSuccess = async (subId) => {
    setSubscriptionId(subId);
    setSubscribed(true);
    setCurrentTier(selectedPlan);
    await supabase.from("agencies").update({plan:"active",subscribed:true,plan_tier:selectedPlan}).eq("id",agency?.id);
    if(setAgency) setAgency(prev=>({...prev,plan:"active",subscribed:true,plan_tier:selectedPlan}));
  };

  return (
    <div className="fade-in">
      <PageHeader title="Billing" subtitle="Simple pricing. Everything included."/>
      {subscribed?(
        <Card style={{maxWidth:560,margin:"0 auto",textAlign:"center",padding:48}}>
          <div style={{fontSize:48,marginBottom:16}}>✓</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:C.navy,marginBottom:8}}>
            {currentTier==="starter"?"Starter Plan Active":"Professional Plan Active"}
          </div>
          <div style={{fontSize:14,color:C.gray,marginBottom:16}}>{agency?.name}</div>
          <Badge label={currentTier==="starter"?"Starter — $99/mo":"Professional — $249/mo"} color={C.blue} bg="#EEF5FF"/>
          {currentTier==="starter"&&(
            <div style={{marginTop:24,padding:16,background:C.mint,borderRadius:10,fontSize:13,color:C.slate}}>
              <strong>Upgrade to Professional</strong> — unlock GPS EVV, HIPAA, OIG, QAPI and more.
              <div style={{marginTop:12}}>
                <Btn onClick={()=>{setSubscribed(false);setSelectedPlan("professional");}} variant="primary">Upgrade to $249/mo</Btn>
              </div>
            </div>
          )}
        </Card>
      ):selectedPlan?(
        <div style={{maxWidth:520,margin:"0 auto"}}>
          <Btn variant="ghost" size="sm" onClick={()=>setSelectedPlan(null)} style={{marginBottom:20}}>← Back to plans</Btn>
          <div style={{background:"#fff",borderRadius:20,padding:36,boxShadow:`0 0 0 2px ${C.blue},0 8px 30px rgba(47,128,237,0.1)`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:800,color:C.navy}}>{PLANS[selectedPlan].name}</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:24,fontWeight:900,color:C.navy}}>${PLANS[selectedPlan].price}<span style={{fontSize:13,fontWeight:400,color:C.gray}}>/mo</span></div>
            </div>
            <div style={{fontSize:13,color:C.gray,marginBottom:24}}>Cancel anytime · No setup fees · No contracts</div>
            <Elements stripe={stripePromise}>
              <CheckoutForm agency={agency} user={user} onSuccess={onSuccess} priceId={PLANS[selectedPlan].priceId}/>
            </Elements>
          </div>
        </div>
      ):(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,maxWidth:900,margin:"0 auto"}}>
          {Object.values(PLANS).map(plan=>(
            <div key={plan.id}
              style={{background:"#fff",borderRadius:20,padding:32,position:"relative",
                boxShadow:`0 1px 6px rgba(18,48,74,0.07)`,transition:"all 0.2s"}}>
              {plan.id==="professional"&&<div style={{position:"absolute",top:-13,left:"50%",transform:"translateX(-50%)",background:C.blue,color:"#fff",fontSize:11,fontWeight:800,padding:"4px 20px",borderRadius:100,whiteSpace:"nowrap"}}>MOST POPULAR</div>}
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:800,color:C.navy,marginBottom:4}}>{plan.name}</div>
              <div style={{fontSize:13,color:C.gray,marginBottom:16}}>{plan.desc}</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:48,fontWeight:900,color:C.navy,lineHeight:1,marginBottom:4}}>
                ${plan.price}<span style={{fontSize:15,fontWeight:400,color:C.gray}}>/mo</span>
              </div>
              <div style={{fontSize:12,color:C.gray,marginBottom:20}}>{plan.limit}</div>
              <button onClick={()=>setSelectedPlan(plan.id)}
                style={{width:"100%",padding:"13px 20px",background:plan.id==="professional"?C.blue:C.navy,color:"#fff",border:"none",borderRadius:10,cursor:"pointer",fontWeight:700,fontSize:15,marginBottom:20}}>
                Subscribe — ${plan.price}/mo
              </button>
              <div style={{borderTop:`1px solid ${C.light}`,paddingTop:16}}>
                {plan.features.map(f=>(
                  <div key={f} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,fontSize:13,color:C.slate}}>
                    <span style={{color:C.teal,fontWeight:900,flexShrink:0}}>✓</span>{f}
                  </div>
                ))}
                {plan.locked.map(f=>(
                  <div key={f} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,fontSize:13,color:C.gray,opacity:0.5}}>
                    <span style={{flexShrink:0}}>✗</span>{f}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"none",gridTemplateColumns:"1fr 1fr",gap:24,maxWidth:960,margin:"0 auto",alignItems:"start"}}>
        <div style={{background:"#fff",borderRadius:20,padding:36,boxShadow:"0 1px 6px rgba(18,48,74,0.07)"}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:900,color:C.navy,marginBottom:4}}>AuditNest</div>
          <div style={{fontSize:14,color:C.gray,marginBottom:16}}>Complete home health compliance platform</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:52,fontWeight:900,color:C.navy,lineHeight:1,marginBottom:4}}>
            $249<span style={{fontSize:16,fontWeight:400,color:C.gray}}>/mo</span>
          </div>
          <div style={{fontSize:13,color:C.gray,marginBottom:24}}>Cancel anytime. No setup fees. No contracts.</div>
          <div style={{borderTop:`1px solid ${C.light}`,paddingTop:20}}>
            {features.map(f=>(
              <div key={f} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,fontSize:13,color:C.slate}}>
                <span style={{color:C.teal,fontWeight:900,flexShrink:0}}>✓</span>{f}
              </div>
            ))}
          </div>
        </div>

        <div style={{background:"#fff",borderRadius:20,padding:36,boxShadow:`0 0 0 2.5px ${C.blue},0 8px 30px rgba(47,128,237,0.12)`}}>
          {subscribed?(
            <div style={{textAlign:"center",padding:"40px 0"}}>
              <div style={{fontSize:48,marginBottom:16}}>✓</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:C.navy,marginBottom:8}}>You're subscribed!</div>
              <div style={{fontSize:14,color:C.gray,marginBottom:8}}>AuditNest is active for {agency?.name}</div>
              {subscriptionId&&<div style={{fontSize:11,color:C.gray}}>Subscription ID: {subscriptionId}</div>}
            </div>
          ):(
            <>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:800,color:C.navy,marginBottom:4}}>Subscribe Now</div>
              <div style={{fontSize:13,color:C.gray,marginBottom:24}}>Enter your card details below</div>
              <Elements stripe={stripePromise}>
                <CheckoutForm agency={agency} user={user} onSuccess={onSuccess}/>
              </Elements>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── SETTINGS ─────────────────────────────────────────────────────
const SettingsScreen = ({agency,setAgency,onLogout}) => {
  const [form,setForm]=useState({...agency});
  const [saved,setSaved]=useState(false);
  const save = () => {setAgency(form);setSaved(true);setTimeout(()=>setSaved(false),2500);};
  return (
    <div className="fade-in">
      <PageHeader title="Settings"/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:22}}>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy,marginBottom:20}}>Agency Information</h3>
          <Field label="Agency Name" value={form.name} onChange={v=>setForm({...form,name:v})}/>
          <Field label="Address" value={form.address} onChange={v=>setForm({...form,address:v})}/>
          <Field label="Phone" value={form.phone} onChange={v=>setForm({...form,phone:v})}/>
          <Field label="Email" type="email" value={form.email} onChange={v=>setForm({...form,email:v})}/>
          <Btn onClick={save} variant={saved?"teal":"primary"}>{saved?"✓ Saved":"Save Changes"}</Btn>
        </Card>
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy,marginBottom:20}}>Alert Schedule</h3>
          <div style={{fontSize:13,color:C.gray,marginBottom:16}}>Credential expiration alerts sent to agency admin email via SendGrid.</div>
          {[{days:90,label:"90-day warning"},{days:60,label:"60-day warning"},{days:30,label:"30-day warning"},{days:0,label:"Expiration day alert"}].map(a=>(
            <div key={a.days} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",borderBottom:`1px solid ${C.light}`}}>
              <div>
                <div style={{fontSize:14,fontWeight:600,color:C.slate}}>{a.label}</div>
                <div style={{fontSize:12,color:C.gray}}>{a.days} days before expiry</div>
              </div>
              <Badge label="Active" color={C.teal} bg="#CCFBF1"/>
            </div>
          ))}
          <div style={{marginTop:14,fontSize:13,color:C.gray}}>
            Wire alerts: Create <code>/api/send-alert.js</code> using Twilio (SMS) or SendGrid (email). Trigger via Supabase Edge Function on daily cron job.
          </div>
        </Card>
        
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy,marginBottom:20}}>Account</h3>
          <div style={{fontSize:14,color:C.gray,marginBottom:20,lineHeight:1.6}}>Manage your subscription and account settings.</div>
          <Btn variant="danger" style={{width:"100%",justifyContent:"center"}} onClick={()=>{if(window.confirm("Sign out?"))onLogout();}}>Sign Out</Btn>
        </Card>
      </div>
    </div>
  );
};

// ─── SUPPORT SCREEN ───────────────────────────────────────────────
const SupportScreen = ({agency, user, selectedTicket, setSelectedTicket}) => {
  const [tickets,setTickets]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showNewModal,setShowNewModal]=useState(false);
  const [subject,setSubject]=useState("");
  const [message,setMessage]=useState("");
  const [priority,setPriority]=useState("normal");
  const [reply,setReply]=useState("");
  const [replies,setReplies]=useState([]);
  const [submitting,setSubmitting]=useState(false);

  useEffect(()=>{
    loadTickets();
  },[]);

  const loadTickets = async () => {
    const {data} = await supabase.from("support_tickets")
      .select("*").eq("agency_id",agency?.id).order("created_at",{ascending:false});
    if(data) setTickets(data);
    setLoading(false);
  };

  const loadReplies = async (ticketId) => {
    const {data} = await supabase.from("ticket_replies")
      .select("*").eq("ticket_id",ticketId).order("created_at",{ascending:true});
    if(data) setReplies(data);
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await loadReplies(ticket.id);
  };

  const submitTicket = async () => {
    if(!subject||!message) return;
    setSubmitting(true);
    const {data,error} = await supabase.from("support_tickets").insert({
      agency_id:agency?.id, subject, message, status:"open", priority
    }).select().single();
    if(data){
      setTickets(prev=>[data,...prev]);
      // Add initial message as first reply
      await supabase.from("ticket_replies").insert({
        ticket_id:data.id, message, sender:"agency", sender_name:agency?.name||"Agency"
      });
      setSubject("");setMessage("");setPriority("normal");setShowNewModal(false);
      // Notify Joshua of new ticket
      await fetch("/api/send-alert",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          type:"ticket_reply",
          to:"joshuaporo@gmail.com",
          subject:`New AuditNest Support Ticket: ${subject}`,
          ticketSubject:subject,
          message:`Agency: ${agency?.name||"Unknown"}
Priority: ${priority}

${message}`
        })
      });
    }
    setSubmitting(false);
  };

  const sendReply = async () => {
    if(!reply||!selectedTicket) return;
    const {data} = await supabase.from("ticket_replies").insert({
      ticket_id:selectedTicket.id, message:reply, sender:"agency", sender_name:agency?.name||"Agency"
    }).select().single();
    if(data){
      setReplies(prev=>[...prev,data]);
      setReply("");
      await supabase.from("support_tickets").update({updated_at:new Date().toISOString(),status:"open"}).eq("id",selectedTicket.id);
    }
  };

  const statusColor = s => s==="open"?C.amber:s==="in_progress"?C.blue:s==="resolved"?C.teal:C.gray;
  const priorityColor = p => p==="urgent"?C.red:p==="high"?C.amber:C.gray;

  return (
    <div className="fade-in">
      <PageHeader title="Support" subtitle="Get help from the AuditNest team"
        action={<Btn onClick={()=>setShowNewModal(true)}>+ New Ticket</Btn>}/>

      {selectedTicket?(
        <div>
          <Btn variant="ghost" size="sm" onClick={()=>setSelectedTicket(null)} style={{marginBottom:16}}>← Back to tickets</Btn>
          <Card style={{marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:800,color:C.navy,marginBottom:4}}>{selectedTicket.subject}</h2>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <Badge label={selectedTicket.status.replace("_"," ")} color={statusColor(selectedTicket.status)} bg={selectedTicket.status==="resolved"?"#CCFBF1":selectedTicket.status==="in_progress"?"#EEF5FF":"#FEF3C7"}/>
                  <Badge label={selectedTicket.priority} color={priorityColor(selectedTicket.priority)} bg="#FEF3C7"/>
                  <span style={{fontSize:12,color:C.gray}}>Opened {fmt(selectedTicket.created_at?.split("T")[0])}</span>
                </div>
              </div>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
              {replies.map(r=>(
                <div key={r.id} style={{
                  display:"flex",flexDirection:"column",
                  alignItems:r.sender==="admin"?"flex-start":"flex-end"
                }}>
                  <div style={{
                    maxWidth:"75%",padding:"12px 16px",borderRadius:12,
                    background:r.sender==="admin"?C.navy:"#EEF5FF",
                    color:r.sender==="admin"?"#fff":C.slate
                  }}>
                    <div style={{fontSize:11,fontWeight:700,marginBottom:6,opacity:0.7}}>
                      {r.sender_name} · {fmt(r.created_at?.split("T")[0])}
                    </div>
                    <div style={{fontSize:14,lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                      {r.message.startsWith("[Screenshot:")
                        ? (() => { const match = r.message.match(/\((.+)\)$/); return match ? <img src={match[1]} alt="screenshot" style={{maxWidth:"100%",borderRadius:8,marginTop:4}}/> : r.message; })()
                        : r.message}
                    </div>
                  </div>
                </div>
              ))}
              {replies.length===0&&<div style={{color:C.gray,fontSize:13,textAlign:"center",padding:20}}>No replies yet. Our team will respond within 24 hours.</div>}
            </div>

            {selectedTicket.status!=="resolved"&&(
              <div>
                <Textarea label="Add Reply" value={reply} onChange={setReply} placeholder="Type your message... (Enter to send, Shift+Enter for new line)" rows={3}
                  onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendReply();}}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13,color:C.blue,fontWeight:600}}>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={async e=>{
                      const file = e.target.files[0];
                      if(!file) return;
                      const path = `ticket-screenshots/${selectedTicket.id}/${Date.now()}-${file.name}`;
                      const {error} = await supabase.storage.from('documents').upload(path, file);
                      if(!error){
                        const url = supabase.storage.from('documents').getPublicUrl(path).data.publicUrl;
                        const imgMsg = `[Screenshot: ${file.name}](${url})`;
                        await supabase.from("ticket_replies").insert({
                          ticket_id:selectedTicket.id, message:imgMsg, sender:"agency", sender_name:agency?.name||"Agency"
                        });
                        const {data:newReplies} = await supabase.from("ticket_replies").select("*").eq("ticket_id",selectedTicket.id).order("created_at",{ascending:true});
                        if(newReplies) setReplies(newReplies);
                      }
                      e.target.value="";
                    }}/>
                    📎 Attach Screenshot
                  </label>
                  <Btn onClick={sendReply} disabled={!reply}>Send Reply</Btn>
                </div>
              </div>
            )}
          </Card>
        </div>
      ):(
        <div>
          <TipBox>Our support team typically responds within 24 hours on business days. For urgent compliance issues, mark your ticket as urgent.</TipBox>
          {loading?(
            <div style={{textAlign:"center",padding:40,color:C.gray}}>Loading tickets...</div>
          ):tickets.length===0?(
            <Card style={{textAlign:"center",padding:60}}>
              <div style={{fontSize:40,marginBottom:12}}>🎫</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:800,color:C.navy,marginBottom:8}}>No support tickets</div>
              <div style={{color:C.gray,fontSize:14,marginBottom:20}}>Have a question or issue? Create a ticket and our team will help.</div>
              <Btn onClick={()=>setShowNewModal(true)}>+ Create First Ticket</Btn>
            </Card>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {tickets.map(t=>(
                <div key={t.id} onClick={()=>openTicket(t)}
                  style={{background:"#fff",borderRadius:14,padding:24,boxShadow:"0 1px 6px rgba(18,48,74,0.07)",cursor:"pointer",borderLeft:`4px solid ${statusColor(t.status)}`,marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:15,fontWeight:700,color:C.slate,marginBottom:4}}>{t.subject}</div>
                      <div style={{fontSize:12,color:C.gray}}>{fmt(t.created_at?.split("T")[0])} · Last updated {fmt(t.updated_at?.split("T")[0])}</div>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {t.priority==="urgent"&&<Badge label="URGENT" color={C.red} bg="#FEE2E2"/>}
                      {t.priority==="high"&&<Badge label="HIGH" color={C.amber} bg="#FEF3C7"/>}
                      <Badge label={t.status.replace("_"," ")} color={statusColor(t.status)} bg={t.status==="resolved"?"#CCFBF1":t.status==="in_progress"?"#EEF5FF":"#FEF3C7"}/>
                      <span style={{fontSize:12,color:C.blue,fontWeight:600}}>View →</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showNewModal&&(
        <Modal title="New Support Ticket" onClose={()=>setShowNewModal(false)}>
          <Field label="Subject" value={subject} onChange={setSubject} placeholder="Brief description of your issue" required/>
          <Dropdown label="Priority" value={priority} onChange={setPriority} options={[
            {value:"normal",label:"Normal — General question or minor issue"},
            {value:"high",label:"High — Affecting daily operations"},
            {value:"urgent",label:"Urgent — Critical compliance issue"},
          ]}/>
          <Textarea label="Describe your issue" value={message} onChange={setMessage} placeholder="Please provide as much detail as possible..." rows={5} required/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn variant="ghost" onClick={()=>setShowNewModal(false)}>Cancel</Btn>
            <Btn onClick={submitTicket} disabled={submitting}>{submitting?"Submitting...":"Submit Ticket"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── ADMIN DASHBOARD ──────────────────────────────────────────────
const AdminScreen = ({currentUser}) => {
  const [agencies,setAgencies]=useState([]);
  const [fullAgencies,setFullAgencies]=useState([]);
  const [loading,setLoading]=useState(true);
  const [selectedAgency,setSelectedAgency]=useState(null);
  const [changingPlan,setChangingPlan]=useState(null);
  const [showMessageModal,setShowMessageModal]=useState(null);
  const [msgSubject,setMsgSubject]=useState("");
  const [msgBody,setMsgBody]=useState("");
  const [sending,setSending]=useState(false);
  const [msgSent,setMsgSent]=useState(false);
  const [complianceData,setComplianceData]=useState({});
  const [activeTab,setActiveTab]=useState("agencies");
  const [allTickets,setAllTickets]=useState([]);
  const [selectedTicket,setSelectedTicket]=useState(null);
  const [adminReply,setAdminReply]=useState("");
  const [ticketReplies,setTicketReplies]=useState([]);
  const [errorLogs,setErrorLogs]=useState([]);
  const [errorsLoading,setErrorsLoading]=useState(false);

  const loadErrors = async () => {
    setErrorsLoading(true);
    const {data} = await supabase.from("error_logs").select("*").order("created_at",{ascending:false}).limit(200);
    if(data) setErrorLogs(data);
    setErrorsLoading(false);
  };

  useEffect(()=>{
    const load = async () => {
      const {data:viewData} = await supabase.from("admin_agencies").select("*").order("created_at",{ascending:false});
      const {data:fullData} = await supabase.from("agencies").select("*").order("created_at",{ascending:false});
      if(viewData) setAgencies(viewData);
      if(fullData) setFullAgencies(fullData);

      // Load compliance data for each agency
      if(viewData) {
        const compData = {};
        for(const agency of viewData) {
          const [{data:cops},{data:creds},{data:oig},{data:sup}] = await Promise.all([
            supabase.from("cms_checklist").select("status").eq("agency_id",agency.id),
            supabase.from("credentials").select("expiry_date").eq("agency_id",agency.id),
            supabase.from("oig_checks").select("next_due").eq("agency_id",agency.id),
            supabase.from("aide_supervision").select("next_due").eq("agency_id",agency.id),
          ]);
          const copsMet = cops?.filter(c=>c.status==="met").length||0;
          const copsTotal = cops?.length||15;
          const expiredCreds = creds?.filter(c=>Math.ceil((new Date(c.expiry_date)-new Date())/86400000)<0).length||0;
          const oigOverdue = oig?.filter(o=>!o.next_due||Math.ceil((new Date(o.next_due)-new Date())/86400000)<0).length||0;
          const supOverdue = sup?.filter(s=>s.next_due&&Math.ceil((new Date(s.next_due)-new Date())/86400000)<0).length||0;
          const score = Math.max(0,Math.round((copsMet/Math.max(copsTotal,1))*60 + (expiredCreds===0?20:0) + (oigOverdue===0?10:0) + (supOverdue===0?10:0)));
          compData[agency.id] = {score, copsMet, copsTotal, expiredCreds, oigOverdue, supOverdue};
        }
        setComplianceData(compData);
      }
      // Load all support tickets
      const {data:ticketsData} = await supabase.from("support_tickets").select("*, agencies(name,email)").order("created_at",{ascending:false});
      if(ticketsData) setAllTickets(ticketsData);

      setLoading(false);
    };
    load();
  },[]);

  const getFullAgency = id => fullAgencies.find(a=>a.id===id)||{};

  const sendMessage = async (agency) => {
    if(!msgSubject||!msgBody) return;
    setSending(true);
    try {
      await fetch("/api/send-alert",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          to: agency.email,
          subject: msgSubject,
          html: `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
            <div style="background:#12304A;padding:24px 32px;border-radius:12px 12px 0 0">
              <h1 style="color:#fff;font-size:20px;margin:0">AuditNest</h1>
            </div>
            <div style="background:#F8FAFC;padding:32px;border-radius:0 0 12px 12px;border:1px solid #E5E7EB">
              <p style="color:#1F2937;font-size:15px;line-height:1.7;white-space:pre-wrap">${msgBody}</p>
              <div style="margin-top:24px;text-align:center">
                <a href="https://app.audit-nest.com" style="background:#12B8A6;color:#fff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:700;font-size:14px">Open AuditNest →</a>
              </div>
            </div>
          </div>`
        })
      });
      await supabase.from("admin_messages").insert({agency_id:agency.id,subject:msgSubject,message:msgBody});
      setMsgSent(true);
      setTimeout(()=>{setShowMessageModal(null);setMsgSubject("");setMsgBody("");setMsgSent(false);},2000);
    } catch(e){}
    setSending(false);
  };

  const suspendAgency = async (id, currentStatus) => {
    const action = currentStatus?"Unsuspend":"Suspend";
    if(!window.confirm(`${action} this agency?`)) return;
    await supabase.from("agencies").update({suspended:!currentStatus}).eq("id",id);
    setFullAgencies(prev=>prev.map(a=>a.id===id?{...a,suspended:!currentStatus}:a));
  };

  const changePlan = async (id, plan) => {
    await supabase.from("agencies").update({plan}).eq("id",id);
    setFullAgencies(prev=>prev.map(a=>a.id===id?{...a,plan}:a));
    setChangingPlan(null);
  };

  const totalStaff = agencies.reduce((sum,a)=>sum+(a.staff_count||0),0);
  const totalPatients = agencies.reduce((sum,a)=>sum+(a.patient_count||0),0);
  const subscribedCount = fullAgencies.filter(a=>a.subscribed).length;
  const trialCount = fullAgencies.filter(a=>!a.subscribed).length;
  const starterCount = fullAgencies.filter(a=>a.subscribed&&a.plan_tier==="starter").length;
  const proCount = fullAgencies.filter(a=>a.subscribed&&a.plan_tier==="professional").length;
  const mrr = (starterCount*99)+(proCount*249);
  const arr = mrr*12;

  return (
    <div className="fade-in">
      <PageHeader title="🔑 Admin Dashboard" subtitle="AuditNest platform overview — visible to you only"/>

      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:14,marginBottom:24}}>
        <StatCard label="Total Agencies" value={agencies.length} color={C.navy}/>
        <StatCard label="MRR" value={`$${mrr.toLocaleString()}`} color={C.teal} sub="subscribed"/>
        <StatCard label="ARR" value={`$${arr.toLocaleString()}`} color={C.teal} sub="annualized"/>
        <StatCard label="Subscribed" value={subscribedCount} color={C.green} sub="$249/mo"/>
        <StatCard label="On Trial" value={trialCount} color={C.amber} sub="free"/>
        <StatCard label="Total Clinicians" value={totalStaff} color={C.blue}/>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {["agencies","compliance","revenue","tickets","errors"].map(t=>(
          <Btn key={t} variant={activeTab===t?"navy":"light"} size="sm" onClick={()=>setActiveTab(t)}>
            {t==="agencies"?"🏥 Agencies":t==="compliance"?"📊 Compliance Scores":t==="revenue"?"💰 Revenue":t==="tickets"?"🎫 Support Tickets":"🚨 Errors"}
          </Btn>
        ))}
      </div>

      {activeTab==="agencies"&&(
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>All Agencies</h3>
          {loading?(
            <div style={{textAlign:"center",padding:40,color:C.gray}}>Loading...</div>
          ):(
            agencies.map(a=>{
              const full = getFullAgency(a.id);
              const suspended = full.suspended||false;
              const comp = complianceData[a.id];
              const trialDays = full.trial_ends_at ? Math.ceil((new Date(full.trial_ends_at)-new Date())/86400000) : null;
              return (
                <div key={a.id} style={{padding:"14px 0",borderBottom:`1px solid ${C.light}`}}>
                  <div style={{display:"grid",gridTemplateColumns:"2fr 1.5fr 1fr 0.7fr 0.7fr 1.2fr 1.5fr",gap:12,alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:suspended?C.gray:C.slate}}>{a.name}{suspended&&" (Suspended)"}</div>
                      <div style={{fontSize:11,color:C.gray}}>{a.email}</div>
                      {trialDays!==null&&!full.subscribed&&(
                        <div style={{fontSize:10,color:trialDays<=0?C.red:trialDays<=3?C.amber:C.gray,fontWeight:600,marginTop:2}}>
                          {trialDays<=0?"Trial expired":`Trial: ${trialDays}d left`}
                        </div>
                      )}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
        {full.subscribed?<Badge label={full.plan_tier==="starter"?"Starter ✓":"Pro ✓"} color={C.teal} bg="#CCFBF1"/>:<Badge label="Trial" color={C.amber} bg="#FEF3C7"/>}
                      {changingPlan===a.id?(
                        <select defaultValue={full.plan||"trial"} onChange={e=>changePlan(a.id,e.target.value)}
                          style={{padding:"3px 6px",borderRadius:5,border:`1px solid ${C.light}`,fontSize:11,color:C.slate,background:C.white}}>
                          <option value="active">Active</option>
                          <option value="trial">Trial</option>
                        </select>
                      ):(
                        <span onClick={()=>setChangingPlan(a.id)} style={{fontSize:10,color:C.blue,cursor:"pointer",fontWeight:600}}>change</span>
                      )}
                    </div>
                    <div>
                      {comp&&(
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{width:36,height:36,borderRadius:18,background:comp.score>=80?C.teal:comp.score>=60?C.amber:C.red,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:800,fontSize:12}}>{comp.score}%</div>
                        </div>
                      )}
                    </div>
                    <div style={{fontSize:13,color:C.slate,fontWeight:600,textAlign:"center"}}>{a.staff_count||0}</div>
                    <div style={{fontSize:13,color:C.slate,fontWeight:600,textAlign:"center"}}>{a.patient_count||0}</div>
                    <div style={{fontSize:11,color:C.gray}}>
                      <div>Joined: {a.created_at?fmt(a.created_at.split("T")[0]):"—"}</div>
                      <div>Login: {full.last_login?fmt(full.last_login.split("T")[0]):"Never"}</div>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      <Btn variant={suspended?"teal":"danger"} size="sm" onClick={()=>suspendAgency(a.id,suspended)}>
                        {suspended?"Unsuspend":"Suspend"}
                      </Btn>
                      <Btn variant="ghost" size="sm" onClick={()=>{setShowMessageModal(a);setMsgSubject("");setMsgBody("");setMsgSent(false);}}>
                        ✉ Message
                      </Btn>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {!loading&&agencies.length===0&&<div style={{textAlign:"center",padding:40,color:C.gray}}>No agencies yet.</div>}
        </Card>
      )}

      {activeTab==="compliance"&&(
        <Card>
          <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>Compliance Scores by Agency</h3>
          {agencies.map(a=>{
            const comp = complianceData[a.id];
            if(!comp) return null;
            const color = comp.score>=80?C.teal:comp.score>=60?C.amber:C.red;
            return (
              <div key={a.id} style={{padding:"14px 0",borderBottom:`1px solid ${C.light}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:C.slate}}>{a.name}</div>
                    <div style={{fontSize:12,color:C.gray}}>{a.email}</div>
                  </div>
                  <div style={{fontFamily:"'Outfit',sans-serif",fontSize:28,fontWeight:900,color}}>{comp.score}%</div>
                </div>
                <div style={{width:"100%",height:6,background:C.light,borderRadius:3,marginBottom:8}}>
                  <div style={{width:`${comp.score}%`,height:6,background:color,borderRadius:3,transition:"width 1s ease"}}/>
                </div>
                <div style={{display:"flex",gap:16,fontSize:12,color:C.gray}}>
                  <span>CoPs: {comp.copsMet}/{comp.copsTotal} met</span>
                  {comp.expiredCreds>0&&<span style={{color:C.red}}>⚠ {comp.expiredCreds} expired creds</span>}
                  {comp.oigOverdue>0&&<span style={{color:C.red}}>⚠ {comp.oigOverdue} OIG overdue</span>}
                  {comp.supOverdue>0&&<span style={{color:C.amber}}>⚠ {comp.supOverdue} supervision overdue</span>}
                  {comp.expiredCreds===0&&comp.oigOverdue===0&&comp.supOverdue===0&&<span style={{color:C.teal}}>✓ No critical issues</span>}
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {activeTab==="revenue"&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
          <Card>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>💰 Revenue Overview</h3>
            <div style={{padding:"16px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Monthly Recurring Revenue</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:36,fontWeight:900,color:C.teal}}>${mrr.toLocaleString()}</div>
            </div>
            <div style={{padding:"16px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Annual Recurring Revenue</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:36,fontWeight:900,color:C.navy}}>${arr.toLocaleString()}</div>
            </div>
            <div style={{padding:"16px 0",borderBottom:`1px solid ${C.light}`}}>
              <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Paying Customers</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:36,fontWeight:900,color:C.blue}}>{subscribedCount}</div>
              <div style={{display:"flex",gap:12,marginTop:8}}>
                <span style={{fontSize:12,color:C.gray}}>Starter: <strong style={{color:C.slate}}>{starterCount}</strong></span>
                <span style={{fontSize:12,color:C.gray}}>Professional: <strong style={{color:C.slate}}>{proCount}</strong></span>
              </div>
            </div>
            <div style={{padding:"16px 0"}}>
              <div style={{fontSize:12,color:C.gray,marginBottom:4}}>Trial Conversion Pipeline</div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:36,fontWeight:900,color:C.amber}}>{trialCount} <span style={{fontSize:14,fontWeight:400,color:C.gray}}>trials</span></div>
              <div style={{fontSize:12,color:C.gray,marginTop:4}}>Potential MRR if all convert: ${(trialCount*249).toLocaleString()}</div>
            </div>
          </Card>
          <Card>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>📈 Growth Milestones</h3>
            {[{n:10,label:"Ramen profitable"},{n:25,label:"Part-time hire possible"},{n:50,label:"Full-time hire"},{n:100,label:"$300K ARR"},{n:250,label:"$750K ARR"},{n:500,label:"$1.5M ARR"}].map(m=>{
              const pct = Math.min(100,Math.round((subscribedCount/m.n)*100));
              return (
                <div key={m.n} style={{padding:"10px 0",borderBottom:`1px solid ${C.light}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:C.slate}}>{m.n} customers — {m.label}</div>
                      <div style={{fontSize:11,color:C.gray}}>${(m.n*249).toLocaleString()}/mo · ${(m.n*249*12).toLocaleString()}/yr</div>
                    </div>
                    <div style={{fontSize:13,fontWeight:700,color:pct>=100?C.teal:C.gray}}>{pct}%</div>
                  </div>
                  <div style={{width:"100%",height:4,background:C.light,borderRadius:2}}>
                    <div style={{width:`${pct}%`,height:4,background:pct>=100?C.teal:C.blue,borderRadius:2}}/>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      )}

      {activeTab==="tickets"&&(
        <div>
          {selectedTicket?(
            <Card>
              <Btn variant="ghost" size="sm" onClick={()=>{setSelectedTicket(null);setTicketReplies([]);}} style={{marginBottom:16}}>← Back to tickets</Btn>
              <div style={{marginBottom:16}}>
                <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:800,color:C.navy,marginBottom:4}}>{selectedTicket.subject}</h3>
                <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:13,color:C.gray}}>{selectedTicket.agencies?.name} · {fmt(selectedTicket.created_at?.split("T")[0])}</span>
                  <select value={selectedTicket.status} onChange={async e=>{
                    const status=e.target.value;
                    setSelectedTicket(prev=>({...prev,status}));
                    setAllTickets(prev=>prev.map(t=>t.id===selectedTicket.id?{...t,status}:t));
                    await supabase.from("support_tickets").update({status}).eq("id",selectedTicket.id);
                  }} style={{padding:"3px 8px",borderRadius:6,border:`1px solid ${C.light}`,fontSize:12,color:C.slate,background:C.white}}>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
                {ticketReplies.map(r=>(
                  <div key={r.id} style={{display:"flex",flexDirection:"column",alignItems:r.sender==="admin"?"flex-end":"flex-start"}}>
                    <div style={{maxWidth:"75%",padding:"12px 16px",borderRadius:12,background:r.sender==="admin"?C.teal:C.mint,color:r.sender==="admin"?"#fff":C.slate}}>
                      <div style={{fontSize:11,fontWeight:700,marginBottom:6,opacity:0.7}}>{r.sender_name} · {fmt(r.created_at?.split("T")[0])}</div>
                      <div style={{fontSize:14,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{r.message}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Textarea label="Reply as AuditNest Support" value={adminReply} onChange={setAdminReply} placeholder="Type your reply..." rows={4}/>
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <Btn onClick={async()=>{
                  if(!adminReply) return;
                  const {data} = await supabase.from("ticket_replies").insert({
                    ticket_id:selectedTicket.id,message:adminReply,sender:"admin",sender_name:"AuditNest Support"
                  }).select().single();
                  if(data){setTicketReplies(prev=>[...prev,data]);setAdminReply("");}
                  await supabase.from("support_tickets").update({status:"in_progress",updated_at:new Date().toISOString()}).eq("id",selectedTicket.id);
                  const agencyEmail = selectedTicket.agencies?.email || selectedTicket.agency_email;
                  if(agencyEmail){
                    await fetch("/api/send-alert",{
                      method:"POST",
                      headers:{"Content-Type":"application/json"},
                      body:JSON.stringify({
                        type:"ticket_reply",
                        to:agencyEmail,
                        subject:"AuditNest Support: New reply to your ticket",
                        ticketSubject:selectedTicket.subject,
                        message:adminReply
                      })
                    });
                  }
                }}>Send Reply</Btn>
              </div>
            </Card>
          ):(
            <Card>
              <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,marginBottom:16}}>All Support Tickets ({allTickets.length})</h3>
              {allTickets.length===0?<div style={{color:C.gray,textAlign:"center",padding:40}}>No tickets yet.</div>:
                allTickets.map(t=>(
                  <div key={t.id} style={{padding:"12px 0",borderBottom:`1px solid ${C.light}`,cursor:"pointer"}} onClick={async()=>{
                    setSelectedTicket(t);
                    const {data} = await supabase.from("ticket_replies").select("*").eq("ticket_id",t.id).order("created_at",{ascending:true});
                    if(data) setTicketReplies(data);
                  }}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:700,color:C.slate,marginBottom:2}}>{t.subject}</div>
                        <div style={{fontSize:12,color:C.gray}}>{t.agencies?.name} · {fmt(t.created_at?.split("T")[0])}</div>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {t.priority==="urgent"&&<Badge label="URGENT" color={C.red} bg="#FEE2E2"/>}
                        <Badge label={t.status.replace("_"," ")} color={t.status==="resolved"?C.teal:t.status==="in_progress"?C.blue:C.amber} bg={t.status==="resolved"?"#CCFBF1":t.status==="in_progress"?"#EEF5FF":"#FEF3C7"}/>
                        <span style={{fontSize:12,color:C.blue,fontWeight:600}}>View →</span>
                      </div>
                    </div>
                  </div>
                ))
              }
            </Card>
          )}
        </div>
      )}

      {activeTab==="errors"&&(
        <Card>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:800,color:C.navy,margin:0}}>Error Logs</h3>
            <Btn variant="navy" size="sm" onClick={loadErrors}>{errorsLoading?"Loading...":"Refresh"}</Btn>
          </div>
          {errorLogs.length===0?(
            <div style={{textAlign:"center",padding:40,color:C.gray}}>{errorsLoading?"Loading...":"No errors logged. Click Refresh to load."}</div>
          ):(
            errorLogs.map(log=>(
              <div key={log.id} style={{padding:"12px 16px",marginBottom:8,borderRadius:8,background:log.resolved?"#F8FAFC":"#FEF2F2",border:`1px solid ${log.resolved?"#E5E7EB":"#FECACA"}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:4}}>
                      <Badge label={log.resolved?"RESOLVED":"UNRESOLVED"} color={log.resolved?C.teal:C.red} bg={log.resolved?"#CCFBF1":"#FEE2E2"}/>
                      <span style={{fontSize:12,fontWeight:700,color:C.slate}}>{log.action}</span>
                    </div>
                    <div style={{fontSize:13,color:C.slate,marginBottom:4}}>{log.error_message}</div>
                    {log.error_code&&<div style={{fontSize:11,color:C.gray}}>Code: {log.error_code}</div>}
                    <div style={{fontSize:11,color:C.gray,marginTop:4}}>{fmt(log.created_at?.split("T")[0])} · Agency: {log.agency_id||"—"}</div>
                  </div>
                  {!log.resolved&&(
                    <Btn variant="ghost" size="sm" onClick={async()=>{
                      await supabase.from("error_logs").update({resolved:true}).eq("id",log.id);
                      setErrorLogs(p=>p.map(x=>x.id===log.id?{...x,resolved:true}:x));
                    }}>Mark Resolved</Btn>
                  )}
                </div>
              </div>
            ))
          )}
        </Card>
      )}

      {showMessageModal&&(
        <Modal title={`Message — ${showMessageModal.name}`} onClose={()=>setShowMessageModal(null)}>
          {msgSent?(
            <div style={{textAlign:"center",padding:"32px 0"}}>
              <div style={{fontSize:40,marginBottom:12}}>✓</div>
              <div style={{fontWeight:700,color:C.teal}}>Message sent successfully</div>
            </div>
          ):(
            <>
              <Field label="Subject" value={msgSubject} onChange={setMsgSubject} placeholder="e.g. Important update about your account"/>
              <Textarea label="Message" value={msgBody} onChange={setMsgBody} placeholder="Write your message here..." rows={6}/>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <Btn variant="ghost" onClick={()=>setShowMessageModal(null)}>Cancel</Btn>
                <Btn onClick={()=>sendMessage(showMessageModal)} disabled={sending}>
                  {sending?"Sending...":"Send Message"}
                </Btn>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

// ─── MAIN APP ─────────────────────────────────────────────────────
export default function App() {
  const [user,setUser]     = useState(()=>{try{const u=localStorage.getItem("auditnest_user");return u?JSON.parse(u):null;}catch{return null;}});
  const [sessionReady,setSessionReady] = useState(false);
  const [loading,setLoading] = useState(true);
  const [screen,setScreen] = useState(()=>localStorage.getItem("auditnest_screen")||"dashboard");
  const [agency,setAgency] = useState(()=>{
    try {
      const u = localStorage.getItem("auditnest_user");
      if(u) {
        const parsed = JSON.parse(u);
        if(parsed?.agency?.id) return parsed.agency;
      }
    } catch(e){}
    return SEED_AGENCY;
  });
  const [staff,setStaff]   = useState([]);
  const [credentials,setCredentials] = useState([]);
  const [hrDocs,setHrDocs] = useState([]);
  const [aideSupervision,setAideSupervision] = useState([]);
  const [cops,setCops]     = useState([]);
  const [patients,setPatients] = useState([]);
  const [qapiMeetings,setQapiMeetings] = useState([]);
  const [qapiMetrics,setQapiMetrics]   = useState([]);
  const [infectionIncidents,setInfectionIncidents] = useState([]);
  const [epDrills,setEpDrills] = useState([]);
  const [hipaaTraining,setHipaaTraining] = useState([]);
  const [baas,setBaas]     = useState([]);
  const [oigChecks,setOigChecks] = useState([]);
  const [insurance,setInsurance] = useState([]);
  const [complaints,setComplaints] = useState([]);
  const [incidents,setIncidents]   = useState([]);
  const [poc,setPoc]       = useState([]);
  const [evv,setEvv]       = useState([]);
  const [documents,setDocuments] = useState([]);
  const [patientDocs,setPatientDocs] = useState([]);
  const [checklistItems,setChecklistItems] = useState([]);
  const [supportTicket,setSupportTicket] = useState(null);

  // Load all data from Supabase on login
  useEffect(() => {
    if(!user || !agency?.id) return;
    const agencyId = agency.id;
    const load = async () => {
      try {
        const [
          {data:staffData},{data:credData},{data:hrData},
          {data:supData},{data:copData},{data:patData},
          {data:qapiMData},{data:qapiMtData},{data:infData},
          {data:epData},{data:hipData},{data:baaData},
          {data:oigData},{data:insData},{data:compData},
          {data:incData},{data:pocData},{data:evvData},{data:docData},
          {data:patDocsData},{data:clData}
        ] = await Promise.all([
          supabase.from('staff_members').select('*').eq('agency_id',agencyId),
          supabase.from('credentials').select('*').eq('agency_id',agencyId),
          supabase.from('hr_documents').select('*').eq('agency_id',agencyId),
          supabase.from('aide_supervision').select('*').eq('agency_id',agencyId),
          supabase.from('cms_checklist').select('*').eq('agency_id',agencyId),
          supabase.from('patient_records').select('*').eq('agency_id',agencyId),
          supabase.from('qapi_meetings').select('*').eq('agency_id',agencyId),
          supabase.from('qapi_metrics').select('*').eq('agency_id',agencyId),
          supabase.from('infection_incidents').select('*').eq('agency_id',agencyId),
          supabase.from('ep_drills').select('*').eq('agency_id',agencyId),
          supabase.from('hipaa_training').select('*').eq('agency_id',agencyId),
          supabase.from('business_associate_agreements').select('*').eq('agency_id',agencyId),
          supabase.from('oig_checks').select('*').eq('agency_id',agencyId),
          supabase.from('insurance_policies').select('*').eq('agency_id',agencyId),
          supabase.from('complaints').select('*').eq('agency_id',agencyId),
          supabase.from('incidents').select('*').eq('agency_id',agencyId),
          supabase.from('plan_of_correction').select('*').eq('agency_id',agencyId),
          supabase.from('evv_records').select('*').eq('agency_id',agencyId),
          supabase.from('documents').select('*').eq('agency_id',agencyId),
          supabase.from('patient_documents').select('*').eq('agency_id',agencyId),
          supabase.from('checklist_items').select('*').eq('agency_id',agencyId),
        ]);
        if(staffData?.length)  setStaff(staffData);
        if(credData?.length)   setCredentials(credData);
        if(hrData?.length)     setHrDocs(hrData);
        if(supData?.length)    setAideSupervision(supData);
        if(copData?.length)    setCops(copData); else setCops(SEED_COPS);
        if(patData?.length)    setPatients(patData);
        if(qapiMData?.length)  setQapiMeetings(qapiMData);
        if(qapiMtData?.length) setQapiMetrics(qapiMtData);
        if(infData?.length)    setInfectionIncidents(infData);
        if(epData?.length)     setEpDrills(epData);
        if(hipData?.length)    setHipaaTraining(hipData);
        if(baaData?.length)    setBaas(baaData);
        if(oigData?.length)    setOigChecks(oigData);
        if(insData?.length)    setInsurance(insData);
        if(compData?.length)   setComplaints(compData);
        if(incData?.length)    setIncidents(incData);
        if(pocData?.length)    setPoc(pocData);
        if(evvData?.length)    setEvv(evvData);
        if(docData?.length)    setDocuments(docData);
        if(patDocsData?.length) setPatientDocs(patDocsData);
        if(clData?.length) setChecklistItems(clData);
      } catch(e){ console.error('Data load error:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [user, agency?.id, sessionReady]); // eslint-disable-line

  const onLogout = () => {
    localStorage.removeItem("auditnest_user");
    localStorage.removeItem("auditnest_screen");
    window.location.href = "/";
  };

  // Restore Supabase session on app load
  useEffect(() => {
    const stored = localStorage.getItem("auditnest_user");
    if(stored) {
      try {
        const parsed = JSON.parse(stored);
        if(parsed?.session?.access_token) {
          supabase.auth.setSession({
            access_token: parsed.session.access_token,
            refresh_token: parsed.session.refresh_token,
          }).then(({data,error}) => {
            if(error) console.error('Session restore error:', error);
          });
        }
      } catch(e) {}
    }
  }, []);

  const saveToDocumentLibrary = async (file, category, name, agencyId, expiry_date=null, valid_from=null) => {
    if(!file) return null;
    const path = `${agencyId}/${Date.now()}_${file.name}`;
    const {data:upData,error:upErr} = await supabase.storage.from('documents').upload(path, file);
    if(upErr) { console.error('Storage error:', upErr); return null; }
    const file_url = supabase.storage.from('documents').getPublicUrl(path).data.publicUrl;
    const newDoc = {
      name, category, file_url, agency_id:agencyId,
      uploaded_by:"Agency Admin",
      valid_from, expiry_date,
      created_at:new Date().toISOString()
    };
    const {data, error} = await supabase.from('documents').insert(newDoc).select().single();
    if(data) setDocuments(prev=>[...prev,data]);
    return file_url;
  };

  if(!user) return <AuthScreen onAuth={u=>{
    localStorage.setItem("auditnest_user",JSON.stringify(u));
    window.location.href = "/";
  }}/>;

  const props = {staff,credentials,hrDocs,aideSupervision,cops,patients,qapiMeetings,qapiMetrics,infectionIncidents,epDrills,hipaaTraining,baas,oigChecks,insurance,complaints,incidents,poc,evv,documents,agency,setScreen};

  const screens = {
    dashboard:  <DashboardScreen staff={staff} credentials={credentials} hrDocs={hrDocs} aideSupervision={aideSupervision} cops={cops} patients={patients} qapiMeetings={qapiMeetings} infectionIncidents={infectionIncidents} hipaaTraining={hipaaTraining} oigChecks={oigChecks} insurance={insurance} complaints={complaints} incidents={incidents} evv={evv} documents={documents} agency={agency} setScreen={setScreen}/>,
    staff:      <StaffScreen staff={staff} setStaff={setStaff} credentials={credentials} setCredentials={setCredentials} hrDocs={hrDocs} setHrDocs={setHrDocs} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary}/>,
    supervision:<SupervisionScreen staff={staff} aideSupervision={aideSupervision} setAideSupervision={setAideSupervision} agency={agency}/>,
    cops:       <CopsScreen cops={cops} setCops={setCops} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary}/>,
    patients:   <PatientScreen patients={patients} setPatients={setPatients} staff={staff} agency={agency} patientDocs={patientDocs} setPatientDocs={setPatientDocs} saveToDocumentLibrary={saveToDocumentLibrary}/>,
    qapi:       <QAPIScreen qapiMeetings={qapiMeetings} setQapiMeetings={setQapiMeetings} qapiMetrics={qapiMetrics} setQapiMetrics={setQapiMetrics} agency={agency}/>,
    infection:  <InfectionScreen infectionIncidents={infectionIncidents} setInfectionIncidents={setInfectionIncidents} staff={staff} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary} checklistItems={checklistItems} setChecklistItems={setChecklistItems}/>,
    ep:         <EPScreen epDrills={epDrills} setEpDrills={setEpDrills} staff={staff} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary} checklistItems={checklistItems} setChecklistItems={setChecklistItems}/>,
    hipaa:      <HIPAAScreen hipaaTraining={hipaaTraining} setHipaaTraining={setHipaaTraining} baas={baas} setBaas={setBaas} staff={staff} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary} checklistItems={checklistItems} setChecklistItems={setChecklistItems}/>,
    governing:  <GoverningScreen agency={agency} setAgency={setAgency} checklistItems={checklistItems} setChecklistItems={setChecklistItems} saveToDocumentLibrary={saveToDocumentLibrary}/>,
    insurance:  <InsuranceScreen insurance={insurance} setInsurance={setInsurance} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary}/>,
    oig:        <OIGScreen oigChecks={oigChecks} setOigChecks={setOigChecks} staff={staff} agency={agency} saveToDocumentLibrary={saveToDocumentLibrary} checklistItems={checklistItems} setChecklistItems={setChecklistItems}/>,
    complaints: <ComplaintsScreen complaints={complaints} setComplaints={setComplaints} incidents={incidents} setIncidents={setIncidents} agency={agency}/>,
    poc:        <POCScreen poc={poc} setPoc={setPoc} cops={cops} agency={agency}/>,
    evv:        <EVVScreen evv={evv} setEvv={setEvv} staff={staff} agency={agency}/>,
    documents:  <DocumentsScreen documents={documents} setDocuments={setDocuments} agency={agency}/>,
    billing:    <BillingScreen agency={agency} user={user} setAgency={setAgency}/>,
    support:    <SupportScreen agency={agency} user={user} selectedTicket={supportTicket} setSelectedTicket={setSupportTicket}/>,
    settings:   <SettingsScreen agency={agency} setAgency={setAgency} onLogout={onLogout}/>,
    admin:      user?.email==="joshuaporo@gmail.com"?<AdminScreen currentUser={user}/>:<div style={{padding:40,color:C.gray}}>Access denied.</div>,
  };

  useEffect(()=>{ localStorage.setItem("auditnest_screen", screen); },[screen]);

  const complianceStatus = {
    staff:      credentials.filter(c=>daysUntil(c.expiry_date)<0).length>0?"red":credentials.filter(c=>{const d=daysUntil(c.expiry_date);return d>=0&&d<=60;}).length>0?"amber":"green",
    supervision:aideSupervision.filter(s=>daysUntil(s.next_due)<0).length>0?"red":"green",
    cops:       cops.filter(c=>c.status==="not_met").length>0?"red":cops.filter(c=>c.status==="needs_review").length>0?"amber":"green",
    patients:   patients.filter(p=>p.poc_due&&daysUntil(p.poc_due)<0).length>0?"red":patients.filter(p=>!p.oasis_submitted).length>0?"amber":"green",
    qapi:       qapiMeetings.length===0?"red":Math.abs(daysUntil(qapiMeetings.sort((a,b)=>new Date(b.meeting_date)-new Date(a.meeting_date))[0]?.meeting_date))>90?"red":"green",
    infection:  infectionIncidents.filter(i=>!i.investigated).length>0?"amber":"green",
    ep:         epDrills.length===0?"red":daysUntil(epDrills.sort((a,b)=>new Date(b.date)-new Date(a.date))[0]?.next_due)<0?"red":"green",
    hipaa:      hipaaTraining.filter(h=>!h.completed).length>0?"red":baas.filter(b=>b.status==="expired").length>0?"amber":"green",
    governing:  daysUntil(agency?.state_license_expiry)<60?"amber":"green",
    insurance:  insurance.filter(i=>daysUntil(i.expiry)<0).length>0?"red":insurance.filter(i=>daysUntil(i.expiry)<30).length>0?"amber":"green",
    oig:        oigChecks.filter(o=>!o.check_date).length>0?"red":oigChecks.filter(o=>o.next_due&&daysUntil(o.next_due)<0).length>0?"red":"green",
    complaints: complaints.filter(c=>c.status==="open").length>0?"amber":"green",
    poc:        poc.filter(p=>p.status==="open").length>0?"amber":"green",
    evv:        evv.length>0&&Math.round(evv.filter(e=>e.status==="verified").length/evv.length*100)<95?"amber":"green",
  };

  // Check if this is a password reset redirect
  const [isReset,setIsReset]=useState(()=>{
    const resetTs = localStorage.getItem("auditNestReset");
    return !!(resetTs && (Date.now() - parseInt(resetTs)) < 600000);
  });

  // Listen for Supabase PASSWORD_RECOVERY event as fallback
  useEffect(()=>{
    const {data:{subscription}} = supabase.auth.onAuthStateChange((event,session)=>{
      if(event==="PASSWORD_RECOVERY") setIsReset(true);
    });
    return ()=>subscription.unsubscribe();
  },[]);

  if(isReset) return <ResetPasswordScreen onDone={()=>{localStorage.removeItem("auditNestReset");setIsReset(false);window.location.href="/";}} />;

  if(loading) return (
    <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#E8F8F5"}}>
      <div style={{width:48,height:48,borderRadius:12,background:"#12304A",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:20}}>
        <span style={{color:"#12B8A6",fontWeight:900,fontSize:24}}>A</span>
      </div>
      <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:800,color:"#12304A",marginBottom:8}}>AuditNest</div>
      <div style={{fontSize:14,color:"#6B7280",marginBottom:24}}>Loading your compliance data...</div>
      <div style={{width:200,height:4,background:"#E5E7EB",borderRadius:2,overflow:"hidden"}}>
        <div style={{width:"60%",height:"100%",background:"#12B8A6",borderRadius:2,animation:"slideLoad 1.2s ease-in-out infinite"}}/>
      </div>
      <style>{`@keyframes slideLoad{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>
    </div>
  );

  // Trial enforcement — read only after expiry
  const trialExpired = agency?.subscribed ? false : agency?.trial_ends_at ? Math.ceil((new Date(agency.trial_ends_at) - new Date()) / 86400000) <= 0 : false;

  const readOnlyScreens = ["dashboard","billing","settings","admin","support"];
  const isStarterLocked = agency?.subscribed && agency?.plan_tier==="starter" && STARTER_LOCKED.includes(screen);
  const currentScreenAllowed = !trialExpired || readOnlyScreens.includes(screen);

  return (
    <div style={{display:"flex",minHeight:"100vh",background:C.mint,flexDirection:"column"}}>
      <TrialBanner agency={agency} setScreen={setScreen}/>
      <div style={{display:"flex",flex:1}}>
        <Sidebar screen={screen} setScreen={setScreen} agency={agency} onLogout={onLogout} complianceStatus={complianceStatus}/>
        <div style={{flex:1,padding:"32px 36px"}}>
          <div style={{maxWidth:1120,margin:"0 auto"}}>
            {isStarterLocked ? (
              <div style={{textAlign:"center",padding:"80px 40px"}}>
                <div style={{fontSize:48,marginBottom:16}}>⭐</div>
                <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:24,fontWeight:800,color:C.navy,marginBottom:12}}>Professional Feature</h2>
                <p style={{color:C.gray,fontSize:15,marginBottom:28,maxWidth:400,margin:"0 auto 28px"}}>This feature is available on the Professional plan. Upgrade to unlock GPS EVV, HIPAA, OIG, QAPI and more.</p>
                <button onClick={()=>setScreen("billing")}
                  style={{background:C.blue,color:"#fff",border:"none",borderRadius:10,padding:"14px 32px",fontWeight:700,fontSize:15,cursor:"pointer"}}>
                  Upgrade to Professional — $249/mo
                </button>
              </div>
            ) : currentScreenAllowed ? screens[screen] : (
              <div style={{textAlign:"center",padding:"80px 40px"}}>
                <div style={{fontSize:48,marginBottom:16}}>🔒</div>
                <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:24,fontWeight:800,color:C.navy,marginBottom:12}}>Trial Expired</h2>
                <p style={{color:C.gray,fontSize:15,marginBottom:28,maxWidth:400,margin:"0 auto 28px"}}>Your 14-day free trial has ended. Subscribe to continue accessing all compliance features.</p>
                <button onClick={()=>setScreen("billing")}
                  style={{background:C.blue,color:"#fff",border:"none",borderRadius:10,padding:"14px 32px",fontWeight:700,fontSize:15,cursor:"pointer"}}>
                  Subscribe Now — $249/mo
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
